import { BASE_URL, HttpError, LONG_RUNNING_TIMEOUT_MS, apiFetch, apiFetchBlob } from './core'
import type { components, paths } from './types.gen'

// PERF-6 — the fetch/auth transport core lives in ./core now; it is
// re-exported here so the ~155 existing `@/api/client` imports (api, types,
// HttpError, registerAuthBridge, bootstrapAuth…) keep working unchanged.
// This module keeps the domain `api` object + DTO type aliases and vite
// emits it as its own 'api' chunk (manualChunks), out of the boot entry.
export * from './core'

// ── Typed helpers ────────────────────────────────────────────────────────────

type Schemas = components['schemas']

// Loosely-required helpers — backend always populates these fields, but
// openapi-typescript treats them as optional. Required<> wrappers used.
export type VATDeclaration = Required<Schemas['VATDeclaration']>
export type VATProof = Required<Schemas['VATProof']>
export type BankAccount = Required<Schemas['BankAccount']>
export type BankStatement = Required<Schemas['BankStatement']>
export type BankStatementLine = Required<Schemas['BankStatementLine']>
export type AutoMatchResult = Required<Schemas['AutoMatchResult']>
export type AutoMatchCandidate = Required<Schemas['AutoMatchCandidate']>
export type ExpenseCategory = Required<Schemas['ExpenseCategory']>
export type ExpenseClaim = Required<Schemas['ExpenseClaim']>
export type ExpenseClaimLine = Required<Schemas['ExpenseClaimLine']>
export type ExpenseApproval = Required<Schemas['ExpenseApproval']>
export type ExpenseApprovalRule = Required<Schemas['ExpenseApprovalRule']>
// CRM (M08)
export type CRMTier = Schemas['Tier']
export type CRMChurnRisk = Schemas['ChurnRisk']
export type CRMInteractionType = Schemas['InteractionType']
export type CRMInteractionOutcome = Schemas['InteractionOutcome']
export type Customer360 = Required<Schemas['Customer360']>
// CrmEntity is intentionally NOT Required<> — only one of lead/opportunity/
// customer is ever populated, discriminated by `type`.
export type CrmEntity = Schemas['CrmEntity']
export type CustomerContact = Required<Schemas['CustomerContact']>
export type CustomerInteraction = Required<Schemas['CustomerInteraction']>
export type CustomerScoring = Required<Schemas['CustomerScoring']>
export type CRMDashboard = Required<Schemas['CRMDashboard']>
export type CRMSegmentSummary = Required<Schemas['SegmentSummary']>
export type CRMTimelineItem = Required<Schemas['TimelineItem']>
export type CRMSettings = Required<Schemas['CRMSettings']>
export type OpportunityStage = Schemas['OpportunityStage']
export type OpportunityPriority = Schemas['OpportunityPriority']
export type Opportunity = Required<Schemas['Opportunity']>
export type OpportunityStageChange = Required<Schemas['OpportunityStageChange']>
export type LeadSource = Schemas['LeadSource']
export type LeadStatus = Schemas['LeadStatus']
export type Lead = Required<Schemas['Lead']>
export type LeadImportResult = Schemas['LeadImportResult']
export type ConvertLeadResponse = Required<Schemas['ConvertLeadResponse']>
export type ActivityType = Schemas['ActivityType']
export type ActivityStatus = Schemas['ActivityStatus']
export type Activity = Required<Schemas['Activity']>
export type AdvancedSegments = Required<Schemas['AdvancedSegments']>
export type SalesTarget = Required<Schemas['SalesTarget']>
export type RepForecast = Required<Schemas['RepForecast']>
export type Forecast = Required<Schemas['Forecast']>

export type PayrollAnalyticsOverview = Required<Schemas['PayrollAnalyticsOverview']>
export type DepartmentPayroll = Required<Schemas['DepartmentPayroll']>
export type MonthlyEvolution = Required<Schemas['MonthlyEvolution']>
export type CostBreakdown = Required<Schemas['CostBreakdown']>
export type TopEarner = Required<Schemas['TopEarner']>

// ─────────────────────────────────────────────────────────────
// Theme (manually typed until openapi-typescript regenerates types.gen.ts)
// Mirrors backend/api/openapi.yaml ThemeDTO / PresetDTO / UpdateThemeRequest
// ─────────────────────────────────────────────────────────────
export type ThemeTokens = {
  light: Record<string, string>
  dark: Record<string, string>
}

export type ThemeBranding = {
  logo_light_url?: string | null
  logo_dark_url?: string | null
  favicon_url?: string | null
  company_name?: string | null
  legal_notice?: string | null
  pdf_logo_url?: string | null
}

export type ThemePreferences = {
  default_locale: string
  date_format: string
  primary_currency: string
  secondary_currencies?: string[]
  dark_mode_enabled: boolean
  force_dark_mode: boolean
}

export type ThemeDTO = {
  tokens: ThemeTokens
  branding: ThemeBranding
  preferences: ThemePreferences
  dark_mode_enabled: boolean
  force_dark_mode: boolean
}

export type ThemePresetDTO = {
  code: string
  name: string
  description?: string | null
  tokens: ThemeTokens
  is_premium?: boolean
  sort_order?: number
}

export type UpdateThemeRequest = {
  preset_code?: string | null
  custom_tokens?: ThemeTokens | null
  company_name?: string | null
  logo_light_url?: string | null
  logo_dark_url?: string | null
  favicon_url?: string | null
  legal_notice?: string | null
  default_locale?: string | null
  date_format?: string | null
  primary_currency?: string | null
  secondary_currencies?: string[] | null
  dark_mode_enabled?: boolean | null
  force_dark_mode?: boolean | null
}

type Envelope<T> = { data: T }
type Paginated<T> = { data: T[]; meta: Schemas['PaginationMeta'] }

// =====================================================================
// Fleet (M07) — hand-typed because OpenAPI spec doesn't yet include
// /v1/fleet/* endpoints. Once added, swap to `Schemas['FleetVehicle']`
// etc. and delete these.
// =====================================================================

export type FleetVehicleType =
  | 'truck'
  | 'van'
  | 'pickup'
  | 'trailer'
  | 'semi_trailer'
  | 'motorcycle'
export type FleetVehicleOwnership = 'owned' | 'leased'
export type FleetVehicleStatus =
  | 'available'
  | 'on_mission'
  | 'maintenance'
  | 'out_of_service'
  | 'sold'
export type FleetFuelType = 'diesel' | 'gasoline' | 'electric' | 'hybrid' | 'lpg'

export type FleetVehicle = {
  id: string
  org_id: string
  ref: string
  plate: string
  ownership: FleetVehicleOwnership
  make: string
  model: string
  year?: number | null
  vin?: string | null
  color?: string | null
  vehicle_type: FleetVehicleType
  capacity_kg?: number | null
  capacity_cbm?: number | null
  capacity_pallets?: number | null
  fuel_type?: FleetFuelType | null
  fuel_tank_liters?: number | null
  registration_doc_url?: string | null
  registration_expiry?: string | null
  insurance_company?: string | null
  insurance_policy_number?: string | null
  insurance_expiry?: string | null
  insurance_doc_url?: string | null
  inspection_expiry?: string | null
  inspection_doc_url?: string | null
  status: FleetVehicleStatus
  current_location?: { lat: number; lng: number; recorded_at: string } | null
  odometer_km: number
  has_gps_tracker: boolean
  has_cold_chain: boolean
  notes?: string | null
  created_at: string
  updated_at: string
}

export type FleetVehicleBody = {
  plate: string
  ownership?: FleetVehicleOwnership
  make: string
  model: string
  year?: number | null
  vin?: string | null
  color?: string | null
  vehicle_type: FleetVehicleType
  capacity_kg?: number | null
  capacity_cbm?: number | null
  capacity_pallets?: number | null
  fuel_type?: FleetFuelType | null
  fuel_tank_liters?: number | null
  registration_doc_url?: string | null
  registration_expiry?: string | null
  insurance_company?: string | null
  insurance_policy_number?: string | null
  insurance_expiry?: string | null
  insurance_doc_url?: string | null
  inspection_expiry?: string | null
  inspection_doc_url?: string | null
  odometer_km?: number
  has_gps_tracker?: boolean
  has_cold_chain?: boolean
  notes?: string | null
}

export type FleetVehicleListParams = {
  status?: FleetVehicleStatus
  ownership?: FleetVehicleOwnership
  type?: FleetVehicleType
  q?: string
  limit?: number
}

export type FleetDriverRelationship = 'employee' | 'contractor' | 'temporary'
export type FleetDriverStatus = 'active' | 'on_leave' | 'suspended' | 'terminated'

export type FleetDriver = {
  id: string
  org_id: string
  ref: string
  user_id?: string | null
  employee_id?: string | null
  first_name: string
  last_name: string
  phone?: string | null
  email?: string | null
  photo_url?: string | null
  license_number: string
  license_types: string[]
  license_expiry?: string | null
  license_country?: string | null
  relationship: FleetDriverRelationship
  rating?: number | null
  on_time_rate?: number | null
  safety_score?: number | null
  fuel_efficiency_score?: number | null
  customer_satisfaction?: number | null
  status: FleetDriverStatus
  preferred_vehicle_id?: string | null
  license_categories_authorized?: string[] | null
  license_doc_url?: string | null
  notes?: string | null
  created_at: string
  updated_at: string
}

export type FleetDriverBody = {
  user_id?: string | null
  employee_id?: string | null
  first_name: string
  last_name: string
  phone?: string | null
  email?: string | null
  photo_url?: string | null
  license_number: string
  license_types?: string[]
  license_expiry?: string | null
  license_country?: string | null
  relationship?: FleetDriverRelationship
  preferred_vehicle_id?: string | null
  license_categories_authorized?: string[] | null
  license_doc_url?: string | null
  notes?: string | null
}

export type FleetDriverListParams = {
  status?: FleetDriverStatus
  relationship?: FleetDriverRelationship
  q?: string
  limit?: number
}

export type FleetDriverHours = {
  id: string
  driver_id: string
  date: string
  driving_hours: number
  rest_hours?: number | null
  total_work_hours?: number | null
  missions_count: number
  breach_max_driving: boolean
  breach_min_rest: boolean
  breach_details?: string | null
  last_calculated_at: string
}

export type FleetMissionStatus = 'planned' | 'in_progress' | 'completed' | 'cancelled'

export type FleetMission = {
  id: string
  org_id: string
  ref: string
  shipment_id: string
  vehicle_id?: string | null
  driver_id?: string | null
  status: FleetMissionStatus
  scheduled_start: string
  scheduled_end?: string | null
  actual_start?: string | null
  actual_end?: string | null
  pickup_address?: Record<string, unknown> | null
  delivery_address?: Record<string, unknown> | null
  pickup_contact?: { name?: string; phone?: string; email?: string } | null
  delivery_contact?: { name?: string; phone?: string; email?: string } | null
  distance_planned_km?: number | null
  distance_actual_km?: number | null
  fuel_liters?: number | null
  fuel_cost?: number | null
  cost_breakdown?: Record<string, number> | null
  cost_total?: number | null
  notes?: string | null
  cancellation_reason?: string | null
  assigned_at?: string | null
  created_at: string
  updated_at: string
}

export type FleetMissionBody = {
  shipment_id: string
  scheduled_start: string
  scheduled_end?: string | null
  pickup_address?: Record<string, unknown> | null
  delivery_address?: Record<string, unknown> | null
  pickup_contact?: { name?: string; phone?: string; email?: string } | null
  delivery_contact?: { name?: string; phone?: string; email?: string } | null
  route_plan?: Record<string, unknown> | null
  distance_planned_km?: number | null
  notes?: string | null
}

export type FleetMissionListParams = {
  status?: FleetMissionStatus
  vehicle_id?: string
  driver_id?: string
  shipment_id?: string
  from?: string
  to?: string
  unassigned?: boolean
  limit?: number
}

export type FleetLocation = {
  id: string
  vehicle_id: string
  mission_id?: string | null
  driver_id?: string | null
  lat: number
  lng: number
  speed_kmh?: number | null
  heading?: number | null
  accuracy_m?: number | null
  altitude_m?: number | null
  source: 'driver_app' | 'gps_tracker' | 'manual'
  battery_level?: number | null
  recorded_at: string
}

export type FleetFuelRefill = {
  id: string
  vehicle_id: string
  mission_id?: string | null
  driver_id?: string | null
  quantity_liters: number
  unit_price: number
  total_cost: number
  currency: string
  station_name?: string | null
  station_address?: string | null
  odometer_km: number
  receipt_url?: string | null
  consumption_l_per_100km?: number | null
  consumption_avg_3m?: number | null
  anomaly_detected: boolean
  anomaly_reason?: string | null
  refueled_at: string
  created_at: string
}

export type FleetFuelListParams = {
  vehicle_id?: string
  mission_id?: string
  only_anomalies?: boolean
  from?: string
  to?: string
  limit?: number
}

export type FleetMaintenanceSchedule = {
  id: string
  vehicle_id: string
  maintenance_type: string
  description?: string | null
  interval_km?: number | null
  interval_months?: number | null
  last_done_at?: string | null
  last_done_km?: number | null
  next_due_at?: string | null
  next_due_km?: number | null
  status: 'scheduled' | 'due' | 'overdue' | 'completed' | 'paused'
  notes?: string | null
  created_at: string
  updated_at: string
}

export type FleetMaintenanceScheduleBody = {
  vehicle_id: string
  maintenance_type: string
  description?: string | null
  interval_km?: number | null
  interval_months?: number | null
  last_done_at?: string | null
  last_done_km?: number | null
  next_due_at?: string | null
  next_due_km?: number | null
  notes?: string | null
}

export type FleetMaintenanceSchedulePatchBody = {
  description?: string | null
  interval_km?: number | null
  interval_months?: number | null
  next_due_at?: string | null
  next_due_km?: number | null
  status?: 'scheduled' | 'due' | 'overdue' | 'completed' | 'paused'
  notes?: string | null
}

export type FleetMaintenanceEvent = {
  id: string
  vehicle_id: string
  schedule_id?: string | null
  maintenance_type: string
  performed_at: string
  odometer_km: number
  duration_hours?: number | null
  provider_name?: string | null
  parts_cost: number
  labor_cost: number
  currency: string
  parts_replaced?: string[] | null
  work_done?: string | null
  notes?: string | null
  invoice_url?: string | null
  created_at: string
}

export type FleetMaintenanceEventBody = {
  vehicle_id: string
  schedule_id?: string | null
  maintenance_type: string
  performed_at: string
  odometer_km: number
  duration_hours?: number | null
  provider_name?: string | null
  parts_cost?: number
  labor_cost?: number
  currency?: string
  parts_replaced?: string[]
  work_done?: string | null
  notes?: string | null
  invoice_url?: string | null
}

export type FleetIncidentType =
  | 'breakdown'
  | 'accident'
  | 'damage'
  | 'theft'
  | 'fuel_theft'
  | 'fine'
  | 'other'
export type FleetIncidentSeverity = 'minor' | 'major' | 'critical'
export type FleetIncidentStatus = 'reported' | 'investigating' | 'resolved' | 'closed'

export type FleetIncident = {
  id: string
  ref: string
  vehicle_id?: string | null
  driver_id?: string | null
  mission_id?: string | null
  incident_type: FleetIncidentType
  severity: FleetIncidentSeverity
  title: string
  description?: string | null
  status: FleetIncidentStatus
  repair_cost: number
  insurance_amount_recovered: number
  created_at: string
  updated_at: string
}

export type FleetIncidentListParams = {
  status?: FleetIncidentStatus
  severity?: FleetIncidentSeverity
  type?: FleetIncidentType
  limit?: number
}

function qs(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) return ''
  const usp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.append(k, String(v))
  }
  const s = usp.toString()
  return s ? `?${s}` : ''
}

export type ListParams = { cursor?: string; limit?: number }
export type QuoteListParams = ListParams & {
  status?: Schemas['Quote']['status']
  customer_id?: string
  mode?: Schemas['Quote']['mode']
  q?: string
}

// ── Customers (M01) ──────────────────────────────────────────────────────────
// Types mirror backend/internal/customers DTOs (not yet in openapi.yaml).

export type CustomerTier = 'bronze' | 'silver' | 'gold'
export type CustomerStatus = 'active' | 'suspended' | 'archived'

export interface CustomerAddress {
  street?: string
  city?: string
  region?: string
  postal_code?: string
  country?: string
}

export interface Customer {
  id: string
  org_id: string
  ref: string
  legal_name: string
  ninea?: string | null
  rc?: string | null
  email?: string | null
  phone?: string | null
  address?: CustomerAddress | null
  payment_terms_days: number
  credit_limit?: number | null
  tier: CustomerTier
  status: CustomerStatus
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface CustomerSummary {
  id: string
  ref: string
  legal_name: string
  tier: CustomerTier
  status: CustomerStatus
}

export interface CustomerCreateBody {
  legal_name: string
  ninea?: string | null
  rc?: string | null
  email?: string | null
  phone?: string | null
  address?: CustomerAddress | null
  payment_terms_days?: number | null
  credit_limit?: number | null
  tier?: CustomerTier | null
  notes?: string | null
}

export type CustomerPatchBody = Partial<CustomerCreateBody> & {
  status?: CustomerStatus | null
}

export type CustomerListParams = ListParams & {
  q?: string
  tier?: CustomerTier
  status?: CustomerStatus
}

// ── Tariffs (M01) ────────────────────────────────────────────────────────────
export type TransportMode = 'air' | 'sea' | 'road'
export type SurchargeCalcType = 'fixed' | 'percent' | 'per_kg'

export interface TariffGrid {
  id: string
  org_id: string
  name: string
  mode: TransportMode
  origin: string
  destination: string
  base_price_per_kg: number
  base_price_per_cbm: number
  min_charge: number
  currency: string
  valid_from: string
  valid_until?: string | null
  is_active: boolean
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface TariffGridCreateBody {
  name: string
  mode: TransportMode
  origin?: string
  destination?: string
  base_price_per_kg: number
  base_price_per_cbm: number
  min_charge: number
  currency?: string
  valid_from?: string | null
  valid_until?: string | null
  is_active?: boolean
  notes?: string | null
}

export type TariffGridPatchBody = Partial<TariffGridCreateBody>

export interface Surcharge {
  id: string
  org_id: string
  code: string
  name: string
  description?: string | null
  calc_type: SurchargeCalcType
  value: number
  applies_to: TransportMode[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface SurchargeCreateBody {
  code: string
  name: string
  description?: string | null
  calc_type: SurchargeCalcType
  value: number
  applies_to?: TransportMode[]
  is_active?: boolean
}

export type SurchargePatchBody = Partial<SurchargeCreateBody>

export type TariffGridListParams = ListParams & {
  mode?: TransportMode
  is_active?: boolean
}

// ── Bookings (M01) ───────────────────────────────────────────────────────────
// Types mirror backend/internal/bookings DTOs (schema per migration 017).
// Backend domain lands in Session 6; frontend code targets the stable shape.

export type BookingStatus = 'confirmed' | 'cancelled' | 'completed'

export interface Booking {
  id: string
  org_id: string
  ref: string
  quote_id?: string | null
  customer_id: string
  status: BookingStatus
  mode: TransportMode
  origin: string
  destination: string
  incoterm?: string | null
  pickup_date?: string | null
  delivery_date_estimated?: string | null
  total: number
  currency: string
  proforma_url?: string | null
  instructions_url?: string | null
  cancelled_at?: string | null
  cancellation_reason?: string | null
  created_at: string
  updated_at: string
}

export interface BookingSummary {
  id: string
  ref: string
  customer_id: string
  status: BookingStatus
  mode: TransportMode
  origin: string
  destination: string
  total: number
  currency: string
  created_at: string
}

export type BookingListParams = ListParams & {
  status?: BookingStatus
  customer_id?: string
  mode?: TransportMode
  q?: string
}

export interface BookingCancelBody {
  reason: string
}

export type BookingDocumentCategory =
  | 'proforma'
  | 'invoice'
  | 'contract'
  | 'bl'
  | 'awb'
  | 'packing_list'
  | 'customs'
  | 'photo'
  | 'other'

export interface BookingDocument {
  id: string
  booking_id: string
  category: BookingDocumentCategory
  file_url: string
  file_name: string
  mime_type?: string | null
  size_bytes?: number | null
  notes?: string | null
  uploaded_by?: string | null
  created_at: string
}

export interface UploadBookingDocumentBody {
  file: File
  category?: BookingDocumentCategory
  notes?: string
}

// ── Users / Org / Sessions (M00 extensions) ──────────────────────────────────
// `User`, `Organization`, `Role`, `Session`, `UpdateUserRequest`,
// `UpdateOrgRequest`, `InviteUserRequest`, `SetUserRolesRequest` are already
// in types.gen.ts. Local DTOs below layer fields the generator hasn't picked
// up (phone, per-user roles payload) and endpoints that don't yet exist on
// the server (change-password, sessions).

export interface UserWithPhone {
  id: string
  org_id: string
  email: string
  email_verified: boolean
  first_name?: string | null
  last_name?: string | null
  phone?: string | null
  locale: 'fr' | 'en'
  status: 'active' | 'invited' | 'suspended'
  last_login_at?: string | null
  created_at: string
}

export interface UserListItem extends UserWithPhone {
  roles?: Array<{ id: string; name: string }>
}

export type UserListParams = ListParams & {
  status?: 'active' | 'invited' | 'suspended'
  q?: string
}

export interface UpdateMyProfileBody {
  first_name?: string | null
  last_name?: string | null
  phone?: string | null
  locale?: 'fr' | 'en'
}

export interface ChangePasswordBody {
  current_password: string
  new_password: string
}

export interface SessionInfo {
  id: string
  user_agent?: string | null
  ip?: string | null
  created_at: string
  expires_at: string
  is_current?: boolean
}

// ── Shipments (M02) ──────────────────────────────────────────────────────────
// Types mirror backend/internal/shipments DTOs. The backend domain lives under
// backend/internal/shipments and drives the 12-status state machine.

export type ShipmentStatus =
  | 'CONFIRMED'
  | 'PICKUP_SCHEDULED'
  | 'PICKED_UP'
  | 'IN_TRANSIT_ORIGIN'
  | 'AT_ORIGIN_HUB'
  | 'IN_CUSTOMS_ORIGIN'
  | 'IN_TRANSIT'
  | 'AT_DESTINATION_HUB'
  | 'IN_CUSTOMS_DESTINATION'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED'
  | 'CANCELLED'

export type ShipmentEventType =
  | 'status_changed'
  | 'location_update'
  | 'note_added'
  | 'document_added'
  | 'pod_captured'
  | 'delay_detected'
  | 'subscriber_added'

export type ShipmentActorType = 'agent' | 'driver' | 'system' | 'client'

export type ShipmentDocType =
  | 'bl'
  | 'awb'
  | 'invoice'
  | 'packing_list'
  | 'pod_photo'
  | 'pod_signature'
  | 'customs_doc'
  | 'other'

export interface ShipmentLocation {
  lat: number
  lng: number
  address?: string
  updated_at?: string
}

export interface Shipment {
  id: string
  org_id: string
  ref: string
  booking_id?: string | null
  customer_id: string
  status: ShipmentStatus
  mode: TransportMode
  origin: string
  destination: string
  current_location?: ShipmentLocation | null
  pickup_date?: string | null
  pickup_date_actual?: string | null
  eta?: string | null
  delivered_at?: string | null
  tracking_token?: string | null
  public_tracking_enabled: boolean
  notes?: string | null
  delay_detected_at?: string | null
  cancellation_reason?: string | null
  created_at: string
  updated_at: string
}

export interface ShipmentSummary {
  id: string
  ref: string
  status: ShipmentStatus
  mode: TransportMode
  origin: string
  destination: string
  customer_id: string
  eta?: string | null
  updated_at: string
}

export interface ShipmentEvent {
  id: string
  shipment_id: string
  event_type: ShipmentEventType
  from_status?: ShipmentStatus | null
  to_status?: ShipmentStatus | null
  location?: ShipmentLocation | null
  notes?: string | null
  actor_user_id?: string | null
  actor_type?: ShipmentActorType | null
  metadata?: unknown
  created_at: string
}

export interface ShipmentDocument {
  id: string
  shipment_id: string
  doc_type: ShipmentDocType
  file_url: string
  file_name: string
  mime_type?: string | null
  size_bytes?: number | null
  uploaded_by?: string | null
  created_at: string
}

export interface ProofOfDelivery {
  id: string
  shipment_id: string
  received_by_name: string
  signature_url: string
  photo_urls: string[]
  location?: ShipmentLocation | null
  notes?: string | null
  delivered_at: string
  captured_by?: string | null
  created_at: string
}

export interface ShipmentSubscriber {
  id: string
  shipment_id: string
  email: string
  channels: string[]
  created_at: string
}

export interface PublicTrackingEvent {
  date: string
  status_label: string
  notes?: string | null
  location?: string | null
}

export interface PublicTrackingView {
  ref: string
  status: ShipmentStatus
  status_label: string
  mode: TransportMode
  origin: string
  destination: string
  eta?: string | null
  delivered_at?: string | null
  progress: number
  events: PublicTrackingEvent[]
  org_name: string
}

export type ShipmentListParams = ListParams & {
  status?: ShipmentStatus
  customer_id?: string
  mode?: TransportMode
  booking_id?: string
  q?: string
}

export interface StatusTransitionInput {
  to_status: ShipmentStatus
  notes?: string
  location?: { lat: number; lng: number; address?: string }
  actor_type?: ShipmentActorType
}

export interface LocationInput {
  lat: number
  lng: number
  address?: string
}

export interface AddNoteInput {
  note: string
  actor_type?: ShipmentActorType
}

export interface CancelShipmentInput {
  reason: string
}

export interface PODInput {
  received_by_name: string
  signature_url: string
  photo_urls: string[]
  notes?: string
  location?: { lat: number; lng: number; address?: string }
  delivered_at?: string
  device_info?: Record<string, unknown>
}

export interface AddShipmentDocumentInput {
  doc_type: ShipmentDocType
  file_url: string
  file_name: string
  mime_type?: string
  size_bytes?: number
}

export interface AddSubscriberInput {
  email: string
  channels?: string[]
}

// ── Finance (M03) ─────────────────────────────────────────────────────────
// Types mirror backend/internal/finance DTOs.

export type InvoiceStatus = 'draft' | 'issued' | 'partial' | 'paid' | 'overdue' | 'voided'
export type InvoiceLineType =
  | 'freight'
  | 'customs_duties'
  | 'storage'
  | 'surcharge'
  | 'discount'
  | 'other'
export type PaymentMethod = 'bank_transfer' | 'wave' | 'orange_money' | 'check' | 'cash' | 'other'
export type VendorBillStatus = 'draft' | 'received' | 'approved' | 'paid' | 'voided' | 'rejected'

export interface InvoiceLine {
  id: string
  line_type: InvoiceLineType
  description: string
  quantity: number
  unit?: string | null
  unit_price: number
  tax_rate: number
  amount_excl_tax: number
  tax_amount: number
  amount_incl_tax: number
  position: number
}

export interface Invoice {
  id: string
  org_id: string
  ref: string
  status: InvoiceStatus
  customer_id: string
  shipment_id?: string | null
  booking_id?: string | null
  issue_date: string
  due_date: string
  payment_terms_days: number
  subtotal: number
  discount_amount: number
  tax_amount: number
  total: number
  paid_amount: number
  balance: number
  currency: string
  pdf_url?: string | null
  notes?: string | null
  auto_generated: boolean
  voided_at?: string | null
  voided_reason?: string | null
  lines?: InvoiceLine[]
  created_at: string
  updated_at: string
}

export interface InvoiceSummary {
  id: string
  ref: string
  status: InvoiceStatus
  customer_id: string
  shipment_id?: string | null
  issue_date: string
  due_date: string
  total: number
  paid_amount: number
  balance: number
  currency: string
  auto_generated: boolean
  created_at: string
  updated_at: string
}

export interface PaymentAllocationDTO {
  id: string
  invoice_id: string
  amount: number
  allocated_at: string
}

export interface Payment {
  id: string
  org_id: string
  ref: string
  customer_id: string
  amount: number
  currency: string
  method: PaymentMethod
  reference?: string | null
  received_at: string
  notes?: string | null
  allocations?: PaymentAllocationDTO[]
  created_at: string
  updated_at: string
}

export interface ARAgingBucket {
  customer_id: string
  customer_name: string
  bucket_0_30: number
  bucket_30_60: number
  bucket_60_90: number
  bucket_90_plus: number
  total: number
}

export interface ARAgingSummary {
  buckets: ARAgingBucket[]
  total_bucket_0_30: number
  total_bucket_30_60: number
  total_bucket_60_90: number
  total_bucket_90_plus: number
  grand_total: number
  as_of: string
}

export interface CustomerStatement {
  customer_id: string
  from: string
  to: string
  invoices: Invoice[]
  payments: Payment[]
  outstanding: number
}

export interface VendorBill {
  id: string
  ref: string
  supplier_id?: string | null
  supplier_ref?: string | null
  status: VendorBillStatus
  issue_date: string
  due_date: string
  subtotal: number
  tax_amount: number
  total: number
  currency: string
  pdf_url?: string | null
  notes?: string | null
  expense_account_code?: string | null
  journal_entry_id?: string | null
  payment_journal_entry_id?: string | null
  approved_at?: string | null
  paid_at?: string | null
  payment_method?: string | null
  payment_reference?: string | null
  created_at: string
  updated_at: string
}

export interface PayVendorBillBody {
  method: 'bank_transfer' | 'cash' | 'check' | 'mobile_money' | 'wave' | 'orange_money' | 'other'
  reference: string
  paid_at?: string
  bank_account_code?: string
}

export type InvoiceListParams = ListParams & {
  status?: InvoiceStatus
  customer_id?: string
  shipment_id?: string
  booking_id?: string
  from?: string
  to?: string
  q?: string
}

export type PaymentListParams = ListParams & {
  customer_id?: string
  method?: PaymentMethod
  from?: string
  to?: string
  q?: string
}

export interface CreateInvoiceLineBody {
  line_type: InvoiceLineType
  description: string
  quantity: number
  unit?: string
  unit_price: number
  tax_rate?: number
}

export interface CreateInvoiceBody {
  customer_id: string
  shipment_id?: string
  booking_id?: string
  issue_date?: string
  due_date?: string
  payment_terms_days?: number
  notes?: string
  lines: CreateInvoiceLineBody[]
}

export interface AllocationBody {
  invoice_id: string
  amount: number
}

export interface RecordPaymentBody {
  customer_id: string
  amount: number
  method: PaymentMethod
  reference?: string
  received_at: string
  notes?: string
  allocations?: AllocationBody[]
}

export interface VoidInvoiceBody {
  reason: string
}

export interface CreateVendorBillBody {
  supplier_id?: string
  supplier_ref?: string
  issue_date: string
  due_date: string
  subtotal?: number
  tax_amount?: number
  total: number
  currency?: string
  notes?: string
  expense_account_code?: string
}

// ── Suppliers (M10) ──────────────────────────────────────────────────────────
// Types mirror backend/internal/suppliers DTOs (not yet in openapi.yaml).

export type SupplierCategory =
  | 'carrier'
  | 'customs_broker'
  | 'insurer'
  | 'security'
  | 'fuel'
  | 'maintenance'
  | 'it_services'
  | 'professional'
  | 'utilities'
  | 'rent'
  | 'other'

export type SupplierQualificationStatus =
  | 'in_qualification'
  | 'qualified'
  | 'suspended'
  | 'blacklisted'

export interface SupplierAddress {
  street?: string
  city?: string
  region?: string
  postal_code?: string
  country?: string
}

export interface SupplierBankDetails {
  bank_name?: string
  iban?: string
  swift?: string
  account_number?: string
  account_name?: string
  mobile_money_provider?: string
  mobile_money_msisdn?: string
}

export type SupplierTaxRegime =
  | 'real_normal'
  | 'real_simplified'
  | 'ctc'
  | 'exempt'
  | 'foreign'
  | 'other'

export type SupplierPaymentMethodPref =
  | 'bank_transfer'
  | 'cash'
  | 'check'
  | 'mobile_money'
  | 'wave'
  | 'orange_money'
  | 'card'
  | 'other'

// A/B/I status derived server-side from qualification_status.
export type SupplierStatusLabel = 'active' | 'blocked' | 'inactive'

export interface Supplier {
  id: string
  org_id: string
  ref: string
  legal_name: string
  trade_name?: string | null
  ninea?: string | null
  rc?: string | null
  vat_number?: string | null
  tax_regime?: SupplierTaxRegime | null
  website?: string | null
  category?: SupplierCategory | null
  subcategory?: string | null
  contact_name?: string | null
  email?: string | null
  phone?: string | null
  address?: SupplierAddress | null
  bank_details?: SupplierBankDetails | null
  credit_limit?: number | null
  preferred_payment_method?: SupplierPaymentMethodPref | null
  qualification_status: SupplierQualificationStatus
  status_label: SupplierStatusLabel
  qualification_date?: string | null
  qualification_reason?: string | null
  preferred_rank?: number | null
  rating_avg?: number | null
  payment_terms_days: number
  currency: string
  notes?: string | null
  created_at: string
  updated_at: string
}

// SupplierEnriched extends Supplier with the per-row aggregates returned
// by GET /v1/suppliers?include=aggregates.
export interface SupplierEnriched extends Supplier {
  purchases_total: number
  outstanding_balance: number
  bills_count: number
  last_invoice_at?: string | null
  last_invoice_ref?: string | null
  last_activity_at?: string | null
}

// DashboardSnapshot — payload of GET /v1/suppliers/dashboard.
export interface SupplierTopEntry {
  supplier_id: string
  ref: string
  legal_name: string
  category?: SupplierCategory | null
  purchases_ltm: number
  bills_count: number
  outstanding_balance: number
}

export interface SupplierDashboardSnapshot {
  total_suppliers: number
  active_suppliers: number
  inactive_suppliers: number
  blocked_suppliers: number
  bills_pending: number
  bills_overdue: number
  bills_draft: number
  bills_validated: number
  bills_paid: number
  purchases_total_ytd: number
  purchases_total_ltm: number
  outstanding_balance: number
  payments_this_month: number
  top_suppliers: SupplierTopEntry[]
  as_of: string
}

// Saved views — persistent per-user list configurations.
export interface SupplierSavedView {
  id: string
  name: string
  filters: Record<string, unknown>
  columns: string[]
  sort_by?: string | null
  sort_dir?: 'asc' | 'desc' | null
  is_default: boolean
  created_at: string
  updated_at: string
}

export interface SupplierSavedViewBody {
  name: string
  filters?: Record<string, unknown>
  columns?: string[]
  sort_by?: string | null
  sort_dir?: 'asc' | 'desc' | null
  is_default?: boolean
}

export interface SupplierSummary {
  id: string
  ref: string
  legal_name: string
  category?: SupplierCategory | null
  qualification_status: SupplierQualificationStatus
}

export interface CreateSupplierBody {
  legal_name: string
  trade_name?: string
  ninea?: string
  rc?: string
  vat_number?: string
  tax_regime?: SupplierTaxRegime
  website?: string
  category?: SupplierCategory
  subcategory?: string
  contact_name?: string
  email?: string
  phone?: string
  address?: SupplierAddress
  bank_details?: SupplierBankDetails
  credit_limit?: number
  preferred_payment_method?: SupplierPaymentMethodPref
  payment_terms_days?: number
  currency?: string
  preferred_rank?: number
  notes?: string
}

export type UpdateSupplierBody = Partial<CreateSupplierBody>

export interface QualificationBody {
  reason?: string
}

export type SupplierListParams = ListParams & {
  q?: string
  category?: SupplierCategory
  qualification_status?: SupplierQualificationStatus
  status?: SupplierStatusLabel
  preferred_payment_method?: SupplierPaymentMethodPref
  tax_regime?: SupplierTaxRegime
  country?: string
  credit_limit_min?: number
  credit_limit_max?: number
  purchases_min?: number
  purchases_max?: number
  has_balance?: boolean
  last_activity_from?: string
  last_activity_to?: string
  sort_by?:
    | 'created_at'
    | 'legal_name'
    | 'purchases_total'
    | 'outstanding_balance'
    | 'last_activity_at'
    | 'credit_limit'
  sort_dir?: 'asc' | 'desc'
  include?: 'aggregates'
}

// ── Vendor payments (M10) ────────────────────────────────────────────────────

export type VendorPaymentMethod =
  | 'bank_transfer'
  | 'wave'
  | 'orange_money'
  | 'free_money'
  | 'mobile_money'
  | 'check'
  | 'cash'
  | 'other'

export interface VendorPaymentAllocation {
  id: string
  vendor_bill_id: string
  amount: number
  allocated_at: string
}

export interface VendorPayment {
  id: string
  org_id: string
  ref: string
  supplier_id: string
  amount: number
  currency: string
  method: VendorPaymentMethod
  reference?: string | null
  paid_at: string
  bank_account_code?: string | null
  journal_entry_id?: string | null
  notes?: string | null
  allocations: VendorPaymentAllocation[]
  created_at: string
  updated_at: string
}

export interface VendorPaymentAllocationBody {
  vendor_bill_id: string
  amount: number
}

export interface RecordVendorPaymentBody {
  supplier_id: string
  amount: number
  currency?: string
  method: VendorPaymentMethod
  reference?: string
  paid_at?: string
  bank_account_code?: string
  notes?: string
  allocations: VendorPaymentAllocationBody[]
}

export interface VendorBillReasonBody {
  reason: string
}

export type VendorPaymentListParams = ListParams & {
  supplier_id?: string
  from?: string
  to?: string
}

export type DashboardKPI = {
  quotes: {
    draft_count: number
    sent_count: number
    sent_total_amount: number
    accepted_count: number
    accepted_this_month: number
    revenue_this_month: number
    rejected_count: number
    expired_count: number
    expiring_soon_count: number
  }
}

export const api = {
  auth: {
    signup: (body: Schemas['SignupRequest']) =>
      apiFetch<Envelope<Schemas['SignupResponse']>>('/v1/auth/signup', {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      }).then((r) => r.data),

    login: (body: Schemas['LoginRequest']) =>
      apiFetch<Envelope<Schemas['TokenPair']>>('/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      }).then((r) => r.data),

    // SEC-1: refresh is cookie-driven. The body argument is kept for
    // backward-compat with any legacy caller but is no longer required.
    refresh: () =>
      apiFetch<Envelope<Schemas['TokenPair']>>('/v1/auth/refresh', {
        method: 'POST',
        skipAuth: true,
      }).then((r) => r.data),

    me: () => apiFetch<Envelope<Schemas['MeResponse']>>('/v1/auth/me').then((r) => r.data),

    // Mirror the shape used elsewhere for updates. The backend M00 does not
    // expose PATCH /v1/auth/me yet, so we PATCH /v1/users/{self} — equivalent
    // fields (`first_name`, `last_name`, `phone`, `locale`) are accepted.
    updateMe: (selfId: string, body: UpdateMyProfileBody) =>
      apiFetch<Envelope<UserWithPhone>>(`/v1/users/${selfId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    changePassword: (body: ChangePasswordBody) =>
      apiFetch<void>('/v1/auth/change-password', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    sessions: () => apiFetch<{ data: SessionInfo[] }>('/v1/auth/sessions').then((r) => r.data),

    revokeSession: (id: string) => apiFetch<void>(`/v1/auth/sessions/${id}`, { method: 'DELETE' }),

    logout: () => apiFetch<void>('/v1/auth/logout', { method: 'POST' }),

    logoutAll: () => apiFetch<void>('/v1/auth/logout-all', { method: 'POST' }),

    forgotPassword: (body: Schemas['ForgotPasswordRequest']) =>
      apiFetch<void>('/v1/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      }),

    acceptInvite: (body: {
      token: string
      password: string
      first_name?: string
      last_name?: string
    }) =>
      apiFetch<void>('/v1/auth/accept-invite', {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      }),

    // Completes an admin-triggered or self-service password reset. The
    // token is a short-lived (15min) purpose JWT delivered by email;
    // the endpoint returns 204 and revokes every existing session.
    resetPassword: (body: Schemas['ResetPasswordRequest']) =>
      apiFetch<void>('/v1/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      }),
  },

  users: {
    list: (params?: UserListParams) => apiFetch<Paginated<UserListItem>>(`/v1/users${qs(params)}`),

    get: (id: string) => apiFetch<Envelope<UserListItem>>(`/v1/users/${id}`).then((r) => r.data),

    invite: (body: Schemas['InviteUserRequest']) =>
      apiFetch<Envelope<Schemas['InviteUserResponse']>>('/v1/users', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    patch: (id: string, body: Schemas['UpdateUserRequest']) =>
      apiFetch<Envelope<UserListItem>>(`/v1/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    delete: (id: string) => apiFetch<void>(`/v1/users/${id}`, { method: 'DELETE' }),

    setRoles: (id: string, body: Schemas['SetUserRolesRequest']) =>
      apiFetch<Envelope<UserListItem>>(`/v1/users/${id}/roles`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    // Returns a fresh invite URL so the admin can copy it and ship it
    // through an out-of-band channel (WhatsApp, SMS, personal email).
    // Only valid for users whose status is still `invited`.
    inviteLink: (id: string) =>
      apiFetch<Envelope<{ url: string; token: string; expires_in: number; email: string }>>(
        `/v1/users/${id}/invite-link`,
        { method: 'POST' },
      ).then((r) => r.data),
  },

  org: {
    get: () => apiFetch<Envelope<Schemas['Organization']>>('/v1/org').then((r) => r.data),

    patch: (body: Schemas['UpdateOrgRequest']) =>
      apiFetch<Envelope<Schemas['Organization']>>('/v1/org', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    license: () => apiFetch<Envelope<Schemas['License']>>('/v1/org/license').then((r) => r.data),

    uploadLogo: (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return apiFetch<Envelope<Schemas['Organization']>>('/v1/org/logo', {
        method: 'POST',
        body: fd,
      }).then((r) => r.data)
    },

    deleteLogo: () =>
      apiFetch<Envelope<Schemas['Organization']>>('/v1/org/logo', {
        method: 'DELETE',
      }).then((r) => r.data),

    // Update the per-tenant email configuration (from_name/from_email/
    // provider/resend_key). The redacted view is returned — resend_key
    // never leaves the server.
    //
    // resend_key special cases:
    //   undefined / omitted → leave as is
    //   ""                  → leave as is (useful when only updating other fields)
    //   "-"                 → clear the stored key
    //   anything else       → replace
    updateMailerConfig: (body: {
      provider?: 'log' | 'resend'
      from_name?: string
      from_email?: string
      resend_key?: string
    }) =>
      apiFetch<Envelope<Schemas['MailerConfigView']>>('/v1/org/mailer-config', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
  },

  theme: {
    getCurrent: () => apiFetch<Envelope<ThemeDTO>>('/v1/theme').then((r) => r.data),

    listPresets: () =>
      apiFetch<Envelope<ThemePresetDTO[]>>('/v1/theme/presets').then((r) => r.data ?? []),

    update: (body: UpdateThemeRequest) =>
      apiFetch<Envelope<ThemeDTO>>('/v1/theme', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    uploadLogo: (file: File, fileType: 'logo_light' | 'logo_dark' | 'favicon' | 'pdf_logo') => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('file_type', fileType)
      return apiFetch<Envelope<{ url: string }>>('/v1/theme/upload-logo', {
        method: 'POST',
        body: fd,
      }).then((r) => r.data.url)
    },
  },

  dashboard: {
    get: () => apiFetch<Envelope<DashboardKPI>>('/v1/dashboard').then((r) => r.data),
  },

  quotes: {
    list: (params?: QuoteListParams) =>
      apiFetch<Paginated<Schemas['QuoteSummary']>>(`/v1/quotes${qs(params)}`),

    get: (id: string) =>
      apiFetch<Envelope<Schemas['Quote']>>(`/v1/quotes/${id}`).then((r) => r.data),

    create: (body: Schemas['CreateQuoteRequest']) =>
      apiFetch<Envelope<Schemas['Quote']>>('/v1/quotes', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    patch: (id: string, body: Schemas['PatchQuoteRequest']) =>
      apiFetch<Envelope<Schemas['Quote']>>(`/v1/quotes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    delete: (id: string) => apiFetch<void>(`/v1/quotes/${id}`, { method: 'DELETE' }),

    calculate: (body: Schemas['CalculateQuoteRequest']) =>
      apiFetch<Envelope<Schemas['QuoteCalculation']>>('/v1/quotes/calculate', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    send: (id: string, body?: { email_override?: string; locale?: 'fr' | 'en' }) =>
      apiFetch<Envelope<Schemas['Quote']>>(`/v1/quotes/${id}/send`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      }).then((r) => r.data),

    expire: (id: string) =>
      apiFetch<Envelope<Schemas['Quote']>>(`/v1/quotes/${id}/expire`, { method: 'POST' }).then(
        (r) => r.data,
      ),

    // PDF download URL — auth'd endpoint; callers fetch with Bearer and
    // blob() to trigger a proper download. The handler returns
    // application/pdf with a Content-Disposition filename attached.
    getPDFUrl: (id: string, locale?: 'fr' | 'en') =>
      `${BASE_URL}/v1/quotes/${id}/pdf${locale ? `?locale=${locale}` : ''}`,

    duplicate: (id: string) =>
      apiFetch<Envelope<Schemas['Quote']>>(`/v1/quotes/${id}/duplicate`, {
        method: 'POST',
      }).then((r) => r.data),

    // Manual lifecycle — internal agent drives the transition on
    // behalf of the client (phone confirmation, paper trail, etc.).
    acceptManual: (id: string, body?: Schemas['ManualAcceptRequest']) =>
      apiFetch<Envelope<Schemas['Quote']>>(`/v1/quotes/${id}/accept`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      }).then((r) => r.data),

    rejectManual: (id: string, body?: Schemas['RejectQuoteRequest']) =>
      apiFetch<Envelope<Schemas['Quote']>>(`/v1/quotes/${id}/reject`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      }).then((r) => r.data),

    // Revert a sent quote back to draft so it can be edited + re-sent.
    // Clears the acceptance token so the outstanding customer link 404s.
    revert: (id: string) =>
      apiFetch<Envelope<Schemas['Quote']>>(`/v1/quotes/${id}/revert`, {
        method: 'POST',
      }).then((r) => r.data),

    // Returns the public review URL of a sent quote. Same token the
    // email CTA uses — one source of truth. 409 when the quote is not
    // in `sent` state.
    shareLink: (id: string) =>
      apiFetch<Envelope<{ url: string; token: string; status: string; ref: string }>>(
        `/v1/quotes/${id}/share-link`,
      ).then((r) => r.data),

    // Renders the quote email + PDF and stores the .eml under a
    // short-lived token. Does not mutate the quote — the agent decides
    // later to download the .eml, open Gmail via mailto, or dispatch
    // directly (when a provider is configured) before calling `send`.
    previewEmail: (id: string, body?: { email_override?: string; locale?: 'fr' | 'en' }) =>
      apiFetch<Envelope<Schemas['EmailPreview']>>(`/v1/quotes/${id}/preview-email`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      }).then((r) => r.data),

    // Builds the .eml download URL with the token from previewEmail.
    // Clicking this link triggers a file download (Content-Disposition).
    emlDownloadUrl: (id: string, token: string) =>
      `${BASE_URL}/v1/quotes/${id}/email.eml?token=${encodeURIComponent(token)}`,
  },

  customers: {
    list: (params?: CustomerListParams) =>
      apiFetch<Paginated<Customer>>(`/v1/customers${qs(params)}`),

    search: (q: string, limit = 20) =>
      apiFetch<{ data: CustomerSummary[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/customers/search${qs({ q, limit })}`,
      ),

    get: (id: string) => apiFetch<Envelope<Customer>>(`/v1/customers/${id}`).then((r) => r.data),

    create: (body: CustomerCreateBody) =>
      apiFetch<Envelope<Customer>>('/v1/customers', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    patch: (id: string, body: CustomerPatchBody) =>
      apiFetch<Envelope<Customer>>(`/v1/customers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    delete: (id: string) => apiFetch<void>(`/v1/customers/${id}`, { method: 'DELETE' }),
  },

  fleet: {
    // Vehicles ----------------------------------------------------------
    listVehicles: (params?: FleetVehicleListParams) =>
      apiFetch<Envelope<FleetVehicle[]>>(`/v1/fleet/vehicles${qs(params)}`),

    getVehicle: (id: string) =>
      apiFetch<Envelope<FleetVehicle>>(`/v1/fleet/vehicles/${id}`).then((r) => r.data),

    createVehicle: (body: FleetVehicleBody) =>
      apiFetch<Envelope<FleetVehicle>>('/v1/fleet/vehicles', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    patchVehicle: (id: string, body: FleetVehicleBody) =>
      apiFetch<Envelope<FleetVehicle>>(`/v1/fleet/vehicles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    deleteVehicle: (id: string) => apiFetch<void>(`/v1/fleet/vehicles/${id}`, { method: 'DELETE' }),

    setVehicleStatus: (id: string, status: FleetVehicleStatus) =>
      apiFetch<Envelope<FleetVehicle>>(`/v1/fleet/vehicles/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      }).then((r) => r.data),

    getVehicleLatestLocation: (id: string) =>
      apiFetch<Envelope<FleetLocation | null>>(`/v1/fleet/vehicles/${id}/location`).then(
        (r) => r.data,
      ),

    getVehicleTrack: (id: string, from?: string, to?: string, limit = 1000) =>
      apiFetch<Envelope<FleetLocation[]>>(
        `/v1/fleet/vehicles/${id}/track${qs({ from, to, limit })}`,
      ).then((r) => r.data),

    // Drivers -----------------------------------------------------------
    listDrivers: (params?: FleetDriverListParams) =>
      apiFetch<Envelope<FleetDriver[]>>(`/v1/fleet/drivers${qs(params)}`),

    getDriver: (id: string) =>
      apiFetch<Envelope<FleetDriver>>(`/v1/fleet/drivers/${id}`).then((r) => r.data),

    createDriver: (body: FleetDriverBody) =>
      apiFetch<Envelope<FleetDriver>>('/v1/fleet/drivers', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    patchDriver: (id: string, body: FleetDriverBody) =>
      apiFetch<Envelope<FleetDriver>>(`/v1/fleet/drivers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    deleteDriver: (id: string) => apiFetch<void>(`/v1/fleet/drivers/${id}`, { method: 'DELETE' }),

    setDriverStatus: (id: string, status: FleetDriverStatus) =>
      apiFetch<Envelope<FleetDriver>>(`/v1/fleet/drivers/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      }).then((r) => r.data),

    getDriverHours: (id: string, date?: string) =>
      apiFetch<Envelope<FleetDriverHours | null>>(
        `/v1/fleet/drivers/${id}/hours${qs({ date })}`,
      ).then((r) => r.data),

    // Missions ----------------------------------------------------------
    listMissions: (params?: FleetMissionListParams) =>
      apiFetch<Envelope<FleetMission[]>>(`/v1/fleet/missions${qs(params)}`),

    getMission: (id: string) =>
      apiFetch<Envelope<FleetMission>>(`/v1/fleet/missions/${id}`).then((r) => r.data),

    createMission: (body: FleetMissionBody) =>
      apiFetch<Envelope<FleetMission>>('/v1/fleet/missions', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    assignMission: (id: string, body: { vehicle_id: string; driver_id: string }) =>
      apiFetch<Envelope<FleetMission>>(`/v1/fleet/missions/${id}/assign`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    startMission: (id: string, body: { odometer_start?: number; started_at?: string }) =>
      apiFetch<Envelope<FleetMission>>(`/v1/fleet/missions/${id}/start`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    completeMission: (
      id: string,
      body: {
        odometer_end?: number
        completed_at?: string
        distance_actual_km?: number
        notes?: string
      },
    ) =>
      apiFetch<Envelope<FleetMission>>(`/v1/fleet/missions/${id}/complete`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    cancelMission: (id: string, reason: string) =>
      apiFetch<Envelope<FleetMission>>(`/v1/fleet/missions/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }).then((r) => r.data),

    getMissionTrack: (id: string) =>
      apiFetch<Envelope<FleetLocation[]>>(`/v1/fleet/missions/${id}/track`).then((r) => r.data),

    // Fuel
    listFuelRefills: (params?: FleetFuelListParams) =>
      apiFetch<Envelope<FleetFuelRefill[]>>(`/v1/fleet/fuel-refills${qs(params)}`),

    // Maintenance
    listMaintenanceSchedules: (vehicleId?: string, limit = 100) =>
      apiFetch<Envelope<FleetMaintenanceSchedule[]>>(
        `/v1/fleet/maintenance/schedules${qs({ vehicle_id: vehicleId, limit })}`,
      ),

    createMaintenanceSchedule: (body: FleetMaintenanceScheduleBody) =>
      apiFetch<Envelope<FleetMaintenanceSchedule>>('/v1/fleet/maintenance/schedules', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    patchMaintenanceSchedule: (id: string, body: FleetMaintenanceSchedulePatchBody) =>
      apiFetch<Envelope<FleetMaintenanceSchedule>>(`/v1/fleet/maintenance/schedules/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    listMaintenanceEvents: (vehicleId?: string, limit = 100) =>
      apiFetch<Envelope<FleetMaintenanceEvent[]>>(
        `/v1/fleet/maintenance/events${qs({ vehicle_id: vehicleId, limit })}`,
      ),

    createMaintenanceEvent: (body: FleetMaintenanceEventBody) =>
      apiFetch<Envelope<FleetMaintenanceEvent>>('/v1/fleet/maintenance/events', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    // Incidents
    listIncidents: (params?: FleetIncidentListParams) =>
      apiFetch<Envelope<FleetIncident[]>>(`/v1/fleet/incidents${qs(params)}`),

    // Document upload (multipart/form-data)
    uploadDocument: (formData: FormData) =>
      apiFetch<Envelope<{ url: string }>>('/v1/fleet/documents', {
        method: 'POST',
        body: formData,
      }).then((r) => r.data.url),
  },

  suppliers: {
    list: (params?: SupplierListParams) =>
      apiFetch<Paginated<Supplier>>(`/v1/suppliers${qs(params)}`),

    search: (q: string, limit = 20) =>
      apiFetch<{ data: SupplierSummary[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/suppliers/search${qs({ q, limit })}`,
      ),

    get: (id: string) => apiFetch<Envelope<Supplier>>(`/v1/suppliers/${id}`).then((r) => r.data),

    create: (body: CreateSupplierBody) =>
      apiFetch<Envelope<Supplier>>('/v1/suppliers', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    patch: (id: string, body: UpdateSupplierBody) =>
      apiFetch<Envelope<Supplier>>(`/v1/suppliers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    delete: (id: string) => apiFetch<void>(`/v1/suppliers/${id}`, { method: 'DELETE' }),

    qualify: (id: string, body?: QualificationBody) =>
      apiFetch<Envelope<Supplier>>(`/v1/suppliers/${id}/qualify`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }).then((r) => r.data),

    suspend: (id: string, body: QualificationBody) =>
      apiFetch<Envelope<Supplier>>(`/v1/suppliers/${id}/suspend`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    reactivate: (id: string, body?: QualificationBody) =>
      apiFetch<Envelope<Supplier>>(`/v1/suppliers/${id}/reactivate`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }).then((r) => r.data),

    blacklist: (id: string, body: QualificationBody) =>
      apiFetch<Envelope<Supplier>>(`/v1/suppliers/${id}/blacklist`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    // Phase 1 — enriched projections (Supplier + per-row AP aggregates).
    listEnriched: (params?: Omit<SupplierListParams, 'include'>) =>
      apiFetch<Paginated<SupplierEnriched>>(
        `/v1/suppliers${qs({ ...params, include: 'aggregates' })}`,
      ),

    getEnriched: (id: string) =>
      apiFetch<Envelope<SupplierEnriched>>(
        `/v1/suppliers/${id}${qs({ include: 'aggregates' })}`,
      ).then((r) => r.data),

    // Dashboard KPIs.
    dashboard: () =>
      apiFetch<Envelope<SupplierDashboardSnapshot>>('/v1/suppliers/dashboard').then((r) => r.data),

    // Saved views (per-user list configurations).
    savedViews: {
      list: () =>
        apiFetch<{ data: SupplierSavedView[]; meta: Schemas['PaginationMeta'] }>(
          '/v1/suppliers/saved-views',
        ),
      get: (id: string) =>
        apiFetch<Envelope<SupplierSavedView>>(`/v1/suppliers/saved-views/${id}`).then(
          (r) => r.data,
        ),
      create: (body: SupplierSavedViewBody) =>
        apiFetch<Envelope<SupplierSavedView>>('/v1/suppliers/saved-views', {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),
      update: (id: string, body: SupplierSavedViewBody) =>
        apiFetch<Envelope<SupplierSavedView>>(`/v1/suppliers/saved-views/${id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }).then((r) => r.data),
      delete: (id: string) =>
        apiFetch<void>(`/v1/suppliers/saved-views/${id}`, { method: 'DELETE' }),
    },
  },

  tariffs: {
    listGrids: (params?: TariffGridListParams) =>
      apiFetch<Paginated<TariffGrid>>(`/v1/tariffs/grids${qs(params)}`),

    getGrid: (id: string) =>
      apiFetch<Envelope<TariffGrid>>(`/v1/tariffs/grids/${id}`).then((r) => r.data),

    createGrid: (body: TariffGridCreateBody) =>
      apiFetch<Envelope<TariffGrid>>('/v1/tariffs/grids', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    patchGrid: (id: string, body: TariffGridPatchBody) =>
      apiFetch<Envelope<TariffGrid>>(`/v1/tariffs/grids/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    deleteGrid: (id: string) => apiFetch<void>(`/v1/tariffs/grids/${id}`, { method: 'DELETE' }),

    listSurcharges: (onlyActive?: boolean) =>
      apiFetch<{ data: Surcharge[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/tariffs/surcharges${qs({ only_active: onlyActive })}`,
      ),

    createSurcharge: (body: SurchargeCreateBody) =>
      apiFetch<Envelope<Surcharge>>('/v1/tariffs/surcharges', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    patchSurcharge: (id: string, body: SurchargePatchBody) =>
      apiFetch<Envelope<Surcharge>>(`/v1/tariffs/surcharges/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    deleteSurcharge: (id: string) =>
      apiFetch<void>(`/v1/tariffs/surcharges/${id}`, { method: 'DELETE' }),
  },

  public: {
    getQuote: (token: string) =>
      apiFetch<Envelope<Schemas['Quote']>>(`/v1/public/quotes/${token}`, { skipAuth: true }).then(
        (r) => r.data,
      ),

    accept: (token: string, body: Schemas['AcceptQuoteRequest']) =>
      apiFetch<Envelope<Schemas['Quote']>>(`/v1/public/quotes/${token}/accept`, {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      }).then((r) => r.data),

    reject: (token: string, body: Schemas['RejectQuoteRequest']) =>
      apiFetch<Envelope<Schemas['Quote']>>(`/v1/public/quotes/${token}/reject`, {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      }).then((r) => r.data),

    // Public PDF URL — the acceptance-link recipient can download the
    // quote in their preferred language + currency without signing in.
    getPDFUrl: (
      token: string,
      opts?: { locale?: 'fr' | 'en'; currency?: 'XOF' | 'USD'; rate?: number },
    ) => {
      const params = new URLSearchParams()
      if (opts?.locale) params.set('locale', opts.locale)
      if (opts?.currency === 'USD') {
        params.set('currency', 'USD')
        if (opts.rate && opts.rate > 0) params.set('rate', String(opts.rate))
      }
      const suffix = params.toString()
      return `${BASE_URL}/v1/public/quotes/${token}/pdf${suffix ? `?${suffix}` : ''}`
    },
  },

  bookings: {
    list: (params?: BookingListParams) =>
      apiFetch<Paginated<BookingSummary>>(`/v1/bookings${qs(params)}`),

    get: (id: string) => apiFetch<Envelope<Booking>>(`/v1/bookings/${id}`).then((r) => r.data),

    cancel: (id: string, body: BookingCancelBody) =>
      apiFetch<Envelope<Booking>>(`/v1/bookings/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    complete: (id: string) =>
      apiFetch<Envelope<Booking>>(`/v1/bookings/${id}/complete`, {
        method: 'POST',
      }).then((r) => r.data),

    emitInvoice: (id: string) =>
      apiFetch<Envelope<{ id: string; ref: string }>>(`/v1/bookings/${id}/invoice`, {
        method: 'POST',
      }).then((r) => r.data),

    listDocuments: (id: string) =>
      apiFetch<{ data: BookingDocument[] }>(`/v1/bookings/${id}/documents`).then((r) => r.data),

    uploadDocument: (id: string, body: UploadBookingDocumentBody) => {
      const fd = new FormData()
      fd.append('file', body.file)
      if (body.category) fd.append('category', body.category)
      if (body.notes) fd.append('notes', body.notes)
      return apiFetch<Envelope<BookingDocument>>(`/v1/bookings/${id}/documents`, {
        method: 'POST',
        body: fd,
      }).then((r) => r.data)
    },

    deleteDocument: (id: string, docId: string) =>
      apiFetch<void>(`/v1/bookings/${id}/documents/${docId}`, { method: 'DELETE' }),
  },

  shipments: {
    list: (params?: ShipmentListParams) =>
      apiFetch<Paginated<ShipmentSummary>>(`/v1/shipments${qs(params)}`),

    get: (id: string) => apiFetch<Envelope<Shipment>>(`/v1/shipments/${id}`).then((r) => r.data),

    listEvents: (id: string, params?: ListParams) =>
      apiFetch<Paginated<ShipmentEvent>>(`/v1/shipments/${id}/events${qs(params)}`),

    updateStatus: (id: string, body: StatusTransitionInput) =>
      apiFetch<Envelope<Shipment>>(`/v1/shipments/${id}/status`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateLocation: (id: string, body: LocationInput) =>
      apiFetch<void>(`/v1/shipments/${id}/location`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    addNote: (id: string, body: AddNoteInput) =>
      apiFetch<void>(`/v1/shipments/${id}/notes`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    cancel: (id: string, body: CancelShipmentInput) =>
      apiFetch<Envelope<Shipment>>(`/v1/shipments/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    capturePOD: (id: string, body: PODInput) =>
      apiFetch<Envelope<Shipment>>(`/v1/shipments/${id}/pod`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    getPOD: (id: string) =>
      apiFetch<Envelope<ProofOfDelivery>>(`/v1/shipments/${id}/pod`).then((r) => r.data),

    listDocuments: (id: string) =>
      apiFetch<{ data: ShipmentDocument[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/shipments/${id}/documents`,
      ),

    addDocument: (id: string, body: AddShipmentDocumentInput) =>
      apiFetch<Envelope<ShipmentDocument>>(`/v1/shipments/${id}/documents`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    deleteDocument: (id: string, docId: string) =>
      apiFetch<void>(`/v1/shipments/${id}/documents/${docId}`, { method: 'DELETE' }),

    listSubscribers: (id: string) =>
      apiFetch<{ data: ShipmentSubscriber[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/shipments/${id}/subscribers`,
      ),

    addSubscriber: (id: string, body: AddSubscriberInput) =>
      apiFetch<Envelope<ShipmentSubscriber>>(`/v1/shipments/${id}/subscribers`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    // Public tracking view — no auth. The server returns HTML by default
    // and JSON only when Accept: application/json. Hitting the aliased
    // JSON route is cleaner from React.
    getPublicTracking: (token: string) =>
      apiFetch<Envelope<PublicTrackingView>>(`/v1/public/track/${token}`, {
        skipAuth: true,
      }).then((r) => r.data),
  },

  finance: {
    invoices: {
      list: (params?: InvoiceListParams) =>
        apiFetch<Paginated<InvoiceSummary>>(`/v1/invoices${qs(params)}`),

      get: (id: string) => apiFetch<Envelope<Invoice>>(`/v1/invoices/${id}`).then((r) => r.data),

      create: (body: CreateInvoiceBody) =>
        apiFetch<Envelope<Invoice>>('/v1/invoices', {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      issue: (id: string) =>
        apiFetch<Envelope<Invoice>>(`/v1/invoices/${id}/issue`, {
          method: 'POST',
        }).then((r) => r.data),

      void: (id: string, body: VoidInvoiceBody) =>
        apiFetch<Envelope<Invoice>>(`/v1/invoices/${id}/void`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      getPDFUrl: (id: string) => `${BASE_URL}/v1/invoices/${id}/pdf`,

      uploadSourcePdf: (id: string, file: File) => {
        const fd = new FormData()
        fd.append('file', file)
        return apiFetch<Envelope<Invoice>>(`/v1/invoices/${id}/pdf`, {
          method: 'POST',
          body: fd,
        }).then((r) => r.data)
      },
    },

    payments: {
      list: (params?: PaymentListParams) =>
        apiFetch<Paginated<Payment>>(`/v1/payments${qs(params)}`),

      get: (id: string) => apiFetch<Envelope<Payment>>(`/v1/payments/${id}`).then((r) => r.data),

      record: (body: RecordPaymentBody) =>
        apiFetch<Envelope<Payment>>('/v1/payments', {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      allocate: (id: string, allocations: AllocationBody[]) =>
        apiFetch<Envelope<Payment>>(`/v1/payments/${id}/allocate`, {
          method: 'POST',
          body: JSON.stringify({ allocations }),
        }).then((r) => r.data),
    },

    ar: {
      getAging: (asOf?: string) =>
        apiFetch<Envelope<ARAgingSummary>>(`/v1/ar/aging${qs({ as_of: asOf })}`).then(
          (r) => r.data,
        ),

      getCustomerStatement: (customerId: string, params?: { from?: string; to?: string }) =>
        apiFetch<Envelope<CustomerStatement>>(`/v1/ar/statement/${customerId}${qs(params)}`).then(
          (r) => r.data,
        ),
    },

    vendorBills: {
      list: (
        params?: ListParams & {
          status?: VendorBillStatus
          supplier_id?: string
          q?: string
        },
      ) => apiFetch<Paginated<VendorBill>>(`/v1/vendor-bills${qs(params)}`),

      get: (id: string) =>
        apiFetch<Envelope<VendorBill>>(`/v1/vendor-bills/${id}`).then((r) => r.data),

      create: (body: CreateVendorBillBody) =>
        apiFetch<Envelope<VendorBill>>('/v1/vendor-bills', {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      approve: (id: string) =>
        apiFetch<Envelope<VendorBill>>(`/v1/vendor-bills/${id}/approve`, {
          method: 'POST',
        }).then((r) => r.data),

      pay: (id: string, body: PayVendorBillBody) =>
        apiFetch<Envelope<VendorBill>>(`/v1/vendor-bills/${id}/pay`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      reject: (id: string, body: VendorBillReasonBody) =>
        apiFetch<Envelope<VendorBill>>(`/v1/vendor-bills/${id}/reject`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      void: (id: string, body: VendorBillReasonBody) =>
        apiFetch<Envelope<VendorBill>>(`/v1/vendor-bills/${id}/void`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      postAccounting: (id: string) =>
        apiFetch<Envelope<VendorBill>>(`/v1/vendor-bills/${id}/post-accounting`, {
          method: 'POST',
        }).then((r) => r.data),

      uploadPdf: (id: string, file: File) => {
        const fd = new FormData()
        fd.append('file', file)
        return apiFetch<Envelope<VendorBill>>(`/v1/vendor-bills/${id}/pdf`, {
          method: 'POST',
          body: fd,
        }).then((r) => r.data)
      },
    },

    vendorPayments: {
      list: (params?: VendorPaymentListParams) =>
        apiFetch<Paginated<VendorPayment>>(`/v1/vendor-payments${qs(params)}`),

      get: (id: string) =>
        apiFetch<Envelope<VendorPayment>>(`/v1/vendor-payments/${id}`).then((r) => r.data),

      record: (body: RecordVendorPaymentBody) =>
        apiFetch<Envelope<VendorPayment>>('/v1/vendor-payments', {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),
    },

    postInvoiceAccounting: (id: string) =>
      apiFetch<Envelope<Invoice>>(`/v1/invoices/${id}/post-accounting`, {
        method: 'POST',
      }).then((r) => r.data),

    postPaymentAccounting: (id: string) =>
      apiFetch<Envelope<Payment>>(`/v1/payments/${id}/post-accounting`, {
        method: 'POST',
      }).then((r) => r.data),
  },

  customs: {
    list: (params?: CustomsListParams) =>
      apiFetch<Paginated<CustomsDeclarationSummary>>(`/v1/customs${qs(params)}`),

    queueCounts: () =>
      apiFetch<Envelope<CustomsQueueCounts>>('/v1/customs/queue-counts').then((r) => r.data),

    reports: () => apiFetch<Envelope<CustomsReport>>('/v1/customs/reports').then((r) => r.data),

    reportsExportUrl: (params: { format: 'pdf' | 'xlsx'; locale?: 'fr' | 'en' }) =>
      `${BASE_URL}/v1/customs/reports/export${qs(params)}`,

    get: (id: string) =>
      apiFetch<Envelope<CustomsDeclaration>>(`/v1/customs/${id}`).then((r) => r.data),

    create: (body: CreateCustomsInput) =>
      apiFetch<Envelope<CustomsDeclaration>>('/v1/customs', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    patch: (id: string, body: UpdateCustomsInput) =>
      apiFetch<Envelope<CustomsDeclaration>>(`/v1/customs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    delete: (id: string) => apiFetch<void>(`/v1/customs/${id}`, { method: 'DELETE' }),

    transition: (id: string, body: CustomsTransitionInput) =>
      apiFetch<Envelope<CustomsDeclaration>>(`/v1/customs/${id}/transition`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    replaceItems: (id: string, items: CustomsItemInput[]) =>
      apiFetch<Envelope<CustomsItem[]>>(`/v1/customs/${id}/items`, {
        method: 'POST',
        body: JSON.stringify({ items }),
      }).then((r) => r.data),

    calculate: (id: string) =>
      apiFetch<Envelope<DutyCalculation>>(`/v1/customs/${id}/calculate`, {
        method: 'POST',
      }).then((r) => r.data),

    addDocument: (id: string, body: AddDocumentInput) =>
      apiFetch<Envelope<CustomsDocument>>(`/v1/customs/${id}/documents`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    uploadDocument: (id: string, file: File, docType: string, isRequired?: boolean) => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('doc_type', docType)
      if (isRequired) fd.append('is_required', 'true')
      return apiFetch<Envelope<CustomsDocument>>(`/v1/customs/${id}/documents/upload`, {
        method: 'POST',
        body: fd,
      }).then((r) => r.data)
    },

    verifyDocument: (id: string, docId: string) =>
      apiFetch<Envelope<CustomsDocument>>(`/v1/customs/${id}/documents/${docId}/verify`, {
        method: 'POST',
      }).then((r) => r.data),

    deleteDocument: (id: string, docId: string) =>
      apiFetch<void>(`/v1/customs/${id}/documents/${docId}`, { method: 'DELETE' }),

    addFee: (id: string, body: AddFeeInput) =>
      apiFetch<Envelope<CustomsFee>>(`/v1/customs/${id}/fees`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateFee: (id: string, feeId: string, body: UpdateFeeInput) =>
      apiFetch<Envelope<CustomsFee>>(`/v1/customs/${id}/fees/${feeId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    searchHSCodes: (q: string) =>
      apiFetch<Envelope<HSCode[]>>(`/v1/customs/hs-codes/search${qs({ q })}`).then((r) => r.data),

    searchHSCodesExternal: (q: string) =>
      apiFetch<Envelope<HSCode[]>>(`/v1/customs/hs-codes/search-external${qs({ q })}`).then(
        (r) => r.data,
      ),

    liquidate: (id: string, body: FiscalLiquidateInput) =>
      apiFetch<Envelope<FinalDutiesResult>>(`/v1/customs/${id}/liquidate`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    getVariance: (id: string) =>
      apiFetch<Envelope<VarianceReport>>(`/v1/customs/${id}/variance`).then((r) => r.data),

    suspensive: {
      list: (params?: SuspenseAccountListParams) =>
        apiFetch<Paginated<SuspenseAccountListItem>>(`/v1/customs/suspensive${qs(params)}`),

      stats: () =>
        apiFetch<Envelope<SuspenseAccountStats>>('/v1/customs/suspensive/stats').then(
          (r) => r.data,
        ),

      get: (id: string) =>
        apiFetch<Envelope<SuspenseAccountDetail>>(`/v1/customs/suspensive/${id}`).then(
          (r) => r.data,
        ),

      create: (body: CreateSuspenseAccountInput) =>
        apiFetch<Envelope<SuspenseAccount>>('/v1/customs/suspensive', {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      previewClearance: (id: string, body: ClearanceInput) =>
        apiFetch<Envelope<ClearancePreview>>(`/v1/customs/suspensive/${id}/preview-clearance`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      confirmClearance: (id: string, body: ClearanceInput) =>
        apiFetch<Envelope<SuspenseMovement>>(`/v1/customs/suspensive/${id}/confirm-clearance`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      renew: (
        id: string,
        body: {
          new_deadline: string
          decision_number?: string
          decision_date?: string
          issuing_authority?: string
          notes?: string
        },
      ) =>
        apiFetch<Envelope<SuspenseAccount>>(`/v1/customs/suspensive/${id}/renew`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      closeReexport: (id: string, notes?: string) =>
        apiFetch<Envelope<SuspenseAccount>>(`/v1/customs/suspensive/${id}/close-reexport`, {
          method: 'POST',
          body: JSON.stringify({ notes }),
        }).then((r) => r.data),

      guarantees: (id: string) =>
        apiFetch<Envelope<Guarantee[]>>(`/v1/customs/suspensive/${id}/guarantees`).then(
          (r) => r.data,
        ),

      createGuarantee: (body: CreateGuaranteeInput) =>
        apiFetch<Envelope<Guarantee>>('/v1/customs/guarantees', {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      reserveGuarantee: (id: string, body: GuaranteeMovementInput) =>
        apiFetch<Envelope<Guarantee>>(`/v1/customs/guarantees/${id}/reserve`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      releaseGuarantee: (id: string, body: GuaranteeMovementInput) =>
        apiFetch<Envelope<Guarantee>>(`/v1/customs/guarantees/${id}/release`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      callGuarantee: (id: string, body: GuaranteeMovementInput) =>
        apiFetch<Envelope<Guarantee>>(`/v1/customs/guarantees/${id}/call`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      mainleveeGuarantee: (id: string, body: MainleveeInput) =>
        apiFetch<Envelope<Guarantee>>(`/v1/customs/guarantees/${id}/mainlevee`, {
          method: 'POST',
          body: JSON.stringify(body),
        }).then((r) => r.data),
    },

    // Panier multi-articles (L6) — /v1/customs/declarations/:id/items[/…]
    declarations: {
      addItem: (declarationId: string, body: CustomsAddItemInput) =>
        apiFetch<Envelope<CustomsDeclarationItem>>(
          `/v1/customs/declarations/${declarationId}/items`,
          { method: 'POST', body: JSON.stringify(body) },
        ).then((r) => r.data),

      getItem: (declarationId: string, itemId: string) =>
        apiFetch<Envelope<CustomsDeclarationItem>>(
          `/v1/customs/declarations/${declarationId}/items/${itemId}`,
        ).then((r) => r.data),

      updateItem: (declarationId: string, itemId: string, body: CustomsUpdateItemInput) =>
        apiFetch<Envelope<CustomsDeclarationItem>>(
          `/v1/customs/declarations/${declarationId}/items/${itemId}`,
          { method: 'PUT', body: JSON.stringify(body) },
        ).then((r) => r.data),

      deleteItem: (declarationId: string, itemId: string) =>
        apiFetch<void>(`/v1/customs/declarations/${declarationId}/items/${itemId}`, {
          method: 'DELETE',
        }),

      duplicateItem: (declarationId: string, itemId: string) =>
        apiFetch<Envelope<CustomsDeclarationItem>>(
          `/v1/customs/declarations/${declarationId}/items/${itemId}/duplicate`,
          { method: 'POST' },
        ).then((r) => r.data),

      calculateItem: (declarationId: string, itemId: string) =>
        apiFetch<Envelope<CustomsItemCalculationResult>>(
          `/v1/customs/declarations/${declarationId}/items/${itemId}/calculate`,
          { method: 'POST' },
        ).then((r) => r.data),

      calculateAll: (declarationId: string) =>
        apiFetch<Envelope<CustomsDeclarationCalculationResult>>(
          `/v1/customs/declarations/${declarationId}/calculate`,
          { method: 'POST' },
        ).then((r) => r.data),

      getTotals: (declarationId: string) =>
        apiFetch<Envelope<CustomsDeclarationTotals>>(
          `/v1/customs/declarations/${declarationId}/totals`,
        ).then((r) => r.data),

      getAssessments: (declarationId: string) =>
        apiFetch<Envelope<CustomsTaxAssessment[]>>(
          `/v1/customs/declarations/${declarationId}/assessments`,
        ).then((r) => r.data ?? []),

      validate: (declarationId: string) =>
        apiFetch<Envelope<CustomsValidationResult[]>>(
          `/v1/customs/declarations/${declarationId}/validate`,
          { method: 'POST' },
        ).then((r) => r.data),

      // Note de détail — PDF servi en application/pdf par le backend.
      // apiFetchBlob reprend l'enveloppe auth/refresh d'apiFetch mais
      // renvoie le Blob brut au lieu de forcer un JSON.parse.
      noteDetailBlob: (declarationId: string) =>
        apiFetchBlob(`/v1/customs/declarations/${declarationId}/note-detail`),

      // Chaîne 1 — Bloc 1+2 global values (PATCH /declarations/:id/global)
      updateGlobal: (declarationId: string, body: Record<string, unknown>) =>
        apiFetch<Envelope<CustomsDeclaration>>(`/v1/customs/declarations/${declarationId}/global`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }).then((r) => r.data),

      // Contrôles de réconciliation (GET /declarations/:id/controls)
      getControls: (declarationId: string) =>
        apiFetch<Envelope<CustomsDeclControlResult[]>>(
          `/v1/customs/declarations/${declarationId}/controls`,
        ).then((r) => r.data),
    },
  },

  warehouse: {
    // Entrepôts
    listWarehouses: () => apiFetch<{ data: Warehouse[] }>('/v1/warehouses').then((r) => r.data),

    createWarehouse: (body: CreateWarehouseBody) =>
      apiFetch<{ data: Warehouse }>('/v1/warehouses', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    getWarehouse: (id: string) =>
      apiFetch<{ data: Warehouse }>(`/v1/warehouses/${id}`).then((r) => r.data),

    updateWarehouse: (id: string, body: Partial<CreateWarehouseBody> & { is_active?: boolean; status?: string; is_default?: boolean }) =>
      apiFetch<{ data: Warehouse }>(`/v1/warehouses/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    getWarehouseStats: (id: string) =>
      apiFetch<{ data: WarehouseStats }>(`/v1/warehouses/${id}/stats`).then((r) => r.data),

    listLocations: (warehouseId: string, zone?: string) =>
      apiFetch<{ data: WarehouseLocation[] }>(
        `/v1/warehouses/${warehouseId}/locations${qs({ zone })}`,
      ).then((r) => r.data),

    createLocation: (warehouseId: string, body: CreateLocationBody) =>
      apiFetch<{ data: WarehouseLocation }>(`/v1/warehouses/${warehouseId}/locations`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateLocation: (locId: string, body: Partial<CreateLocationBody> & { is_active?: boolean }) =>
      apiFetch<{ data: WarehouseLocation }>(`/v1/locations/${locId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    // Stock
    listStock: (params?: StockListParams) =>
      apiFetch<{ data: StockItem[]; meta: { next_cursor: string } }>(`/v1/stock${qs(params)}`),

    getStockItem: (id: string) =>
      apiFetch<{ data: StockItem }>(`/v1/stock/${id}`).then((r) => r.data),

    moveIn: (body: MoveInBody) =>
      apiFetch<{ data: StockItem }>('/v1/stock/move-in', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    moveOut: (id: string, body: MoveOutBody) =>
      apiFetch<{ data: StockItem }>(`/v1/stock/${id}/move-out`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    transfer: (id: string, body: TransferBody) =>
      apiFetch<{ data: StockItem }>(`/v1/stock/${id}/transfer`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    adjust: (id: string, body: AdjustBody) =>
      apiFetch<{ data: StockItem }>(`/v1/stock/${id}/adjust`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    listMovements: (id: string) =>
      apiFetch<{ data: StockMovement[]; meta: { next_cursor: string } }>(
        `/v1/stock/${id}/movements`,
      ).then((r) => r.data),

    // Inventaire
    listInventorySessions: (cursor?: string) =>
      apiFetch<{ data: InventorySession[]; meta: { next_cursor: string } }>(
        `/v1/inventory${qs({ cursor })}`,
      ),

    startInventory: (warehouseId: string) =>
      apiFetch<{ data: InventorySession }>('/v1/inventory/start', {
        method: 'POST',
        body: JSON.stringify({ warehouse_id: warehouseId }),
      }).then((r) => r.data),

    getInventorySession: (sessionId: string) =>
      apiFetch<{ data: InventorySession }>(`/v1/inventory/${sessionId}`).then((r) => r.data),

    recordCount: (sessionId: string, body: RecordCountBody) =>
      apiFetch<{ data: InventoryCount }>(`/v1/inventory/${sessionId}/count`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    closeInventory: (sessionId: string, applyAdjustments: boolean) =>
      apiFetch<{ data: InventorySession }>(`/v1/inventory/${sessionId}/close`, {
        method: 'POST',
        body: JSON.stringify({ apply_adjustments: applyAdjustments }),
      }).then((r) => r.data),

    listCounts: (sessionId: string) =>
      apiFetch<{ data: InventoryCount[] }>(`/v1/inventory/${sessionId}/counts`).then((r) => r.data),

    listTasks: (params?: WarehouseTaskFilters) =>
      apiFetch<{ data: WarehouseTask[] }>(`/v1/warehouse/tasks${qs(params)}`).then((r) => r.data),

    listAssignments: (warehouseId: string) =>
      apiFetch<{ data: WarehouseAssignment[] }>(`/v1/warehouses/${warehouseId}/assignments`).then(
        (r) => r.data,
      ),

    assignUser: (warehouseId: string, userId: string) =>
      apiFetch<{ data: WarehouseAssignment }>(`/v1/warehouses/${warehouseId}/assignments`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      }).then((r) => r.data),

    unassignUser: (warehouseId: string, userId: string) =>
      apiFetch<void>(`/v1/warehouses/${warehouseId}/assignments/${userId}`, { method: 'DELETE' }),

    startSiteTransfer: (
      warehouseId: string,
      body: { stock_item_id: string; to_warehouse_id: string; notes?: string },
    ) =>
      apiFetch<{ data: WarehouseTask }>(`/v1/warehouses/${warehouseId}/transfers`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    board: (warehouseId: string) =>
      apiFetch<{ data: WarehouseBoard }>(`/v1/warehouses/${warehouseId}/board`).then((r) => r.data),

    suggestLocation: (stockItemId: string) =>
      apiFetch<{ data: WarehouseLocation | null }>(
        `/v1/stock/${stockItemId}/suggested-location`,
      ).then((r) => r.data),

    createTask: (body: CreateWarehouseTaskBody) =>
      apiFetch<{ data: WarehouseTask }>('/v1/warehouse/tasks', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    claimTask: (taskId: string) =>
      apiFetch<{ data: WarehouseTask }>(`/v1/warehouse/tasks/${taskId}/claim`, {
        method: 'POST',
      }).then((r) => r.data),

    completeTask: (taskId: string, body: CompleteWarehouseTaskBody) =>
      apiFetch<{ data: WarehouseTask }>(`/v1/warehouse/tasks/${taskId}/complete`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    blockTask: (taskId: string, reason: string) =>
      apiFetch<{ data: WarehouseTask }>(`/v1/warehouse/tasks/${taskId}/block`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }).then((r) => r.data),

    releaseTask: (taskId: string) =>
      apiFetch<{ data: WarehouseTask }>(`/v1/warehouse/tasks/${taskId}/release`, {
        method: 'POST',
      }).then((r) => r.data),

    cancelTask: (taskId: string) =>
      apiFetch<{ data: WarehouseTask }>(`/v1/warehouse/tasks/${taskId}/cancel`, {
        method: 'POST',
      }).then((r) => r.data),

    createIntake: (body: WarehouseIntakeBody) =>
      apiFetch<{ data: StockItem }>('/v1/warehouse/intakes', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    listCharges: (params?: { customer_id?: string; status?: 'uninvoiced' }) =>
      apiFetch<{ data: StorageCharge[] }>(`/v1/warehouse/charges${qs(params)}`).then((r) => r.data),

    listTariffs: (warehouseId: string) =>
      apiFetch<{ data: StorageTariff[] }>(`/v1/warehouses/${warehouseId}/tariffs`).then(
        (r) => r.data,
      ),

    saveTariff: (body: StorageTariffBody) =>
      apiFetch<{ data: StorageTariff }>('/v1/warehouse/tariffs', {
        method: 'PUT',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    listLeases: (warehouseId: string) =>
      apiFetch<{ data: WarehouseLease[] }>(`/v1/warehouses/${warehouseId}/leases`).then(
        (r) => r.data,
      ),

    createLease: (warehouseId: string, body: WarehouseLeaseBody) =>
      apiFetch<{ data: WarehouseLease }>(`/v1/warehouses/${warehouseId}/leases`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    terminateLease: (leaseId: string) =>
      apiFetch<void>(`/v1/leases/${leaseId}/terminate`, { method: 'POST' }),

    listSecurityContracts: (warehouseId: string) =>
      apiFetch<{ data: WarehouseSecurityContract[] }>(
        `/v1/warehouses/${warehouseId}/services`,
      ).then((r) => r.data),

    createSecurityContract: (warehouseId: string, body: WarehouseSecurityBody) =>
      apiFetch<{ data: WarehouseSecurityContract }>(`/v1/warehouses/${warehouseId}/services`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
  },

  accounting: {
    // Chart of accounts
    listAccounts: (params?: AccountFilters) =>
      apiFetch<{ data: Account[]; meta?: Schemas['PaginationMeta'] }>(
        `/v1/accounting/accounts${qs(params)}`,
      ).then((r) => r.data),

    getAccount: (code: string) =>
      apiFetch<Envelope<Account>>(`/v1/accounting/accounts/${code}`).then((r) => r.data),

    createAccount: (body: CreateAccountBody) =>
      apiFetch<Envelope<Account>>('/v1/accounting/accounts', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateAccount: (code: string, body: UpdateAccountBody) =>
      apiFetch<Envelope<Account>>(`/v1/accounting/accounts/${code}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    deleteAccount: (code: string) =>
      apiFetch<void>(`/v1/accounting/accounts/${code}`, { method: 'DELETE' }),

    getGrandLedger: (code: string, params?: { period?: string; cursor?: string; limit?: number }) =>
      apiFetch<{ data: JournalLine[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/accounting/accounts/${code}/ledger${qs(params)}`,
      ),

    // Journal entries
    listEntries: (params?: EntryFilters) =>
      apiFetch<{ data: JournalEntry[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/accounting/entries${qs(params)}`,
      ),

    getEntry: (id: string) =>
      apiFetch<Envelope<JournalEntry>>(`/v1/accounting/entries/${id}`).then((r) => r.data),

    // Reverse lookup : returns the journal entry generated from a given
    // source (invoice, payment, vendor_bill, payroll period, ...). Resolves
    // to null when no entry exists yet.
    getEntryBySource: async (
      sourceType:
        | 'invoice'
        | 'payment'
        | 'credit_note'
        | 'payroll'
        | 'payroll_payment'
        | 'vendor_bill'
        | 'vendor_bill_payment'
        | 'fleet_cost'
        | 'manual',
      sourceId: string,
    ): Promise<JournalEntry | null> => {
      try {
        const r = await apiFetch<Envelope<JournalEntry>>(
          `/v1/accounting/entries/by-source${qs({ type: sourceType, id: sourceId })}`,
        )
        return r.data
      } catch (e) {
        if (e instanceof HttpError && e.status === 404) return null
        throw e
      }
    },

    createEntry: (body: CreateEntryBody) =>
      apiFetch<Envelope<JournalEntry>>('/v1/accounting/entries', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateEntry: (id: string, body: UpdateEntryBody) =>
      apiFetch<Envelope<JournalEntry>>(`/v1/accounting/entries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    validateEntry: (id: string) =>
      apiFetch<Envelope<JournalEntry>>(`/v1/accounting/entries/${id}/validate`, {
        method: 'POST',
      }).then((r) => r.data),

    reverseEntry: (id: string, reason: string) =>
      apiFetch<Envelope<JournalEntry>>(`/v1/accounting/entries/${id}/reverse`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }).then((r) => r.data),

    // Periods
    listPeriods: () =>
      apiFetch<{ data: AccountingPeriod[] }>('/v1/accounting/periods').then((r) => r.data),

    closePeriod: (code: string) =>
      apiFetch<Envelope<AccountingPeriod>>(`/v1/accounting/periods/${code}/close`, {
        method: 'POST',
      }).then((r) => r.data),

    // Tenant accounting configuration (treasury accounts for cross-domain hooks)
    getTenantConfig: () =>
      apiFetch<Envelope<TenantAccountingConfig>>('/v1/accounting/config').then((r) => r.data),

    updateTenantConfig: (body: UpdateTenantAccountingConfigBody) =>
      apiFetch<Envelope<TenantAccountingConfig>>('/v1/accounting/config', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    // Financial reports
    getTrialBalance: (asOf?: string) =>
      apiFetch<Envelope<TrialBalance>>(`/v1/accounting/trial-balance${qs({ as_of: asOf })}`).then(
        (r) => r.data,
      ),

    getIncomeStatement: (params?: { from?: string; to?: string }) =>
      apiFetch<Envelope<IncomeStatement>>(`/v1/accounting/income-statement${qs(params)}`).then(
        (r) => r.data,
      ),

    getBalanceSheet: (asOf?: string) =>
      apiFetch<Envelope<BalanceSheet>>(`/v1/accounting/balance-sheet${qs({ as_of: asOf })}`).then(
        (r) => r.data,
      ),

    // Export URLs — callers attach the bearer token and fetch with
    // blob() to trigger a file download. Format is pdf (default) or xlsx.
    getTrialBalanceExportUrl: (params: {
      format: 'pdf' | 'xlsx'
      as_of?: string
      locale?: 'fr' | 'en'
    }) => `${BASE_URL}/v1/accounting/trial-balance/export${qs(params)}`,

    getIncomeStatementExportUrl: (params: {
      format: 'pdf' | 'xlsx'
      from?: string
      to?: string
      locale?: 'fr' | 'en'
    }) => `${BASE_URL}/v1/accounting/income-statement/export${qs(params)}`,

    getBalanceSheetExportUrl: (params: {
      format: 'pdf' | 'xlsx'
      as_of?: string
      locale?: 'fr' | 'en'
    }) => `${BASE_URL}/v1/accounting/balance-sheet/export${qs(params)}`,

    getJournalExportUrl: (params: {
      format: 'pdf' | 'xlsx'
      journal_code?: string
      period?: string
      status?: string
      from?: string
      to?: string
      q?: string
      locale?: 'fr' | 'en'
    }) => `${BASE_URL}/v1/accounting/entries/export${qs(params)}`,

    getLedgerExportUrl: (
      code: string,
      params: { format: 'pdf' | 'xlsx'; period?: string; locale?: 'fr' | 'en' },
    ) => `${BASE_URL}/v1/accounting/accounts/${code}/ledger/export${qs(params)}`,

    // ── VAT declarations (M13) ───────────────────────────────────────────
    listVATDeclarations: (params?: {
      status?: 'draft' | 'filed' | 'paid' | 'rejected' | 'cancelled'
      year?: number
      period_from?: string
      period_to?: string
    }) =>
      apiFetch<{ data: VATDeclaration[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/accounting/vat${qs(params)}`,
      ).then((r) => r.data),
    getVATDeclaration: (id: string) =>
      apiFetch<Envelope<VATDeclaration>>(`/v1/accounting/vat/${id}`).then((r) => r.data),
    prepareVATDeclaration: (body: {
      period_code: string
      regime?: 'reel' | 'simplifie' | 'forfait'
      vat_credit_carried?: number
      notes?: string
    }) =>
      apiFetch<Envelope<VATDeclaration>>('/v1/accounting/vat/prepare', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    recomputeVATDeclaration: (id: string) =>
      apiFetch<Envelope<VATDeclaration>>(`/v1/accounting/vat/${id}/recompute`, {
        method: 'POST',
      }).then((r) => r.data),
    fileVATDeclaration: (id: string, body: { declaration_number: string }) =>
      apiFetch<Envelope<VATDeclaration>>(`/v1/accounting/vat/${id}/file`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    payVATDeclaration: (
      id: string,
      body: { payment_reference: string; paid_at?: string; bank_account_code?: string },
    ) =>
      apiFetch<Envelope<VATDeclaration>>(`/v1/accounting/vat/${id}/pay`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    cancelVATDeclaration: (id: string) =>
      apiFetch<void>(`/v1/accounting/vat/${id}`, { method: 'DELETE' }),
    uploadVATProof: (
      id: string,
      file: File,
      params: {
        proof_type?:
          | 'declaration_form'
          | 'payment_receipt'
          | 'invoice_proof'
          | 'correspondence'
          | 'other'
        title?: string
      },
    ) => {
      const fd = new FormData()
      fd.append('file', file)
      if (params.proof_type) fd.append('proof_type', params.proof_type)
      if (params.title) fd.append('title', params.title)
      return apiFetch<Envelope<VATProof>>(`/v1/accounting/vat/${id}/proofs`, {
        method: 'POST',
        body: fd,
      }).then((r) => r.data)
    },
    deleteVATProof: (declarationId: string, proofId: string) =>
      apiFetch<void>(`/v1/accounting/vat/${declarationId}/proofs/${proofId}`, {
        method: 'DELETE',
      }),

    // ── Bank reconciliation (M13) ────────────────────────────────────────
    listBankAccounts: (includeInactive?: boolean) =>
      apiFetch<{ data: BankAccount[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/accounting/bank/accounts${qs({ include_inactive: includeInactive })}`,
      ).then((r) => r.data),
    getBankAccount: (id: string) =>
      apiFetch<Envelope<BankAccount>>(`/v1/accounting/bank/accounts/${id}`).then((r) => r.data),
    createBankAccount: (body: Schemas['BankAccountInput']) =>
      apiFetch<Envelope<BankAccount>>('/v1/accounting/bank/accounts', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updateBankAccount: (id: string, body: Schemas['BankAccountPatch']) =>
      apiFetch<Envelope<BankAccount>>(`/v1/accounting/bank/accounts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deleteBankAccount: (id: string) =>
      apiFetch<void>(`/v1/accounting/bank/accounts/${id}`, { method: 'DELETE' }),

    listBankStatements: (params?: {
      bank_account_id?: string
      status?: string
      from?: string
      to?: string
    }) =>
      apiFetch<{ data: BankStatement[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/accounting/bank/statements${qs(params)}`,
      ).then((r) => r.data),
    getBankStatement: (id: string) =>
      apiFetch<Envelope<BankStatement>>(`/v1/accounting/bank/statements/${id}`).then((r) => r.data),
    importBankStatement: (bankAccountId: string, file: File) => {
      const fd = new FormData()
      fd.append('bank_account_id', bankAccountId)
      fd.append('file', file)
      return apiFetch<Envelope<BankStatement>>('/v1/accounting/bank/statements/import', {
        method: 'POST',
        body: fd,
      }).then((r) => r.data)
    },
    getBankStatementTemplateUrl: () => `${BASE_URL}/v1/accounting/bank/statements/template`,
    closeBankStatement: (id: string) =>
      apiFetch<void>(`/v1/accounting/bank/statements/${id}/close`, { method: 'POST' }),
    autoMatchBankStatement: (id: string, body?: { date_tolerance?: number; min_score?: number }) =>
      apiFetch<Envelope<AutoMatchResult>>(`/v1/accounting/bank/statements/${id}/auto-match`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      }).then((r) => r.data),
    getBankLineSuggestions: (lineId: string, dateTolerance?: number) =>
      apiFetch<{ data: AutoMatchCandidate[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/accounting/bank/lines/${lineId}/suggestions${qs({
          date_tolerance: dateTolerance,
        })}`,
      ).then((r) => r.data),
    matchBankLine: (
      lineId: string,
      body: { journal_line_id: string; method?: string; score?: number },
    ) =>
      apiFetch<void>(`/v1/accounting/bank/lines/${lineId}/match`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    unmatchBankLine: (lineId: string) =>
      apiFetch<void>(`/v1/accounting/bank/lines/${lineId}/unmatch`, { method: 'POST' }),
  },

  // ── HR / GRH (M11) ──────────────────────────────────────────────────────
  hr: {
    // Departments
    listDepartments: () =>
      apiFetch<{ data: HRDepartment[]; meta: Schemas['PaginationMeta'] }>(
        '/v1/hr/departments',
      ).then((r) => r.data),
    createDepartment: (body: HRCreateDepartmentBody) =>
      apiFetch<Envelope<HRDepartment>>('/v1/hr/departments', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updateDepartment: (id: string, body: HRUpdateDepartmentBody) =>
      apiFetch<Envelope<HRDepartment>>(`/v1/hr/departments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deleteDepartment: (id: string) =>
      apiFetch<void>(`/v1/hr/departments/${id}`, { method: 'DELETE' }),

    // Positions
    listPositions: () =>
      apiFetch<{ data: HRPosition[]; meta: Schemas['PaginationMeta'] }>('/v1/hr/positions').then(
        (r) => r.data,
      ),
    createPosition: (body: HRCreatePositionBody) =>
      apiFetch<Envelope<HRPosition>>('/v1/hr/positions', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updatePosition: (id: string, body: HRUpdatePositionBody) =>
      apiFetch<Envelope<HRPosition>>(`/v1/hr/positions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deletePosition: (id: string) => apiFetch<void>(`/v1/hr/positions/${id}`, { method: 'DELETE' }),

    // Position grades (catalog)
    listPositionGrades: () =>
      apiFetch<{ data: HRPositionGrade[]; meta: Schemas['PaginationMeta'] }>(
        '/v1/hr/position-grades',
      ).then((r) => r.data),
    createPositionGrade: (body: HRCreatePositionGradeBody) =>
      apiFetch<Envelope<HRPositionGrade>>('/v1/hr/position-grades', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updatePositionGrade: (id: string, body: HRUpdatePositionGradeBody) =>
      apiFetch<Envelope<HRPositionGrade>>(`/v1/hr/position-grades/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deletePositionGrade: (id: string) =>
      apiFetch<void>(`/v1/hr/position-grades/${id}`, { method: 'DELETE' }),

    // Employees
    listEmployees: (params?: HREmployeeFilters) =>
      apiFetch<Paginated<HREmployee>>(`/v1/hr/employees${qs(params)}`),
    getEmployee: (id: string) =>
      apiFetch<Envelope<HREmployee>>(`/v1/hr/employees/${id}`).then((r) => r.data),
    createEmployee: (body: HRCreateEmployeeBody) =>
      apiFetch<Envelope<HREmployee>>('/v1/hr/employees', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updateEmployee: (id: string, body: HRUpdateEmployeeBody) =>
      apiFetch<Envelope<HREmployee>>(`/v1/hr/employees/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    terminateEmployee: (id: string, body: HRTerminateBody) =>
      apiFetch<Envelope<HREmployee>>(`/v1/hr/employees/${id}/terminate`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    getEmployeeBalance: (id: string, year?: number) =>
      apiFetch<{ data: HRLeaveBalance[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/hr/employees/${id}/balance${qs({ year })}`,
      ).then((r) => r.data),
    getEmployeeAttendance: (id: string, year: number, month: number) =>
      apiFetch<{ data: HRAttendanceRecord[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/hr/employees/${id}/attendance${qs({ year, month })}`,
      ).then((r) => r.data),

    // Contracts
    listContracts: (params?: HRContractFilters) =>
      apiFetch<{ data: HREmploymentContract[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/hr/contracts${qs(params)}`,
      ).then((r) => r.data),
    getContract: (id: string) =>
      apiFetch<Envelope<HREmploymentContract>>(`/v1/hr/contracts/${id}`).then((r) => r.data),
    createContract: (body: HRCreateContractBody) =>
      apiFetch<Envelope<HREmploymentContract>>('/v1/hr/contracts', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updateContract: (id: string, body: HRUpdateContractBody) =>
      apiFetch<Envelope<HREmploymentContract>>(`/v1/hr/contracts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    signContract: (id: string, body: { by_employee?: boolean; by_company?: boolean }) =>
      apiFetch<Envelope<HREmploymentContract>>(`/v1/hr/contracts/${id}/sign`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    activateContract: (id: string, force?: boolean) =>
      apiFetch<Envelope<HREmploymentContract>>(`/v1/hr/contracts/${id}/activate`, {
        method: 'POST',
        body: JSON.stringify({ force: Boolean(force) }),
      }).then((r) => r.data),
    uploadContractDocument: (id: string, file: File) => {
      const form = new FormData()
      form.append('file', file)
      return apiFetch<Envelope<HREmploymentContract>>(`/v1/hr/contracts/${id}/document`, {
        method: 'POST',
        body: form,
      }).then((r) => r.data)
    },
    terminateContract: (id: string, body: HRTerminateBody) =>
      apiFetch<Envelope<HREmploymentContract>>(`/v1/hr/contracts/${id}/terminate`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    amendContract: (id: string, body: HRAmendContractBody) =>
      apiFetch<Envelope<HREmploymentContract>>(`/v1/hr/contracts/${id}/amend`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    listContractAmendments: (id: string) =>
      apiFetch<{ data: HRContractAmendment[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/hr/contracts/${id}/amendments`,
      ).then((r) => r.data),

    // Leave types
    listLeaveTypes: () =>
      apiFetch<{ data: HRLeaveType[]; meta: Schemas['PaginationMeta'] }>('/v1/hr/leave-types').then(
        (r) => r.data,
      ),
    createLeaveType: (body: HRCreateLeaveTypeBody) =>
      apiFetch<Envelope<HRLeaveType>>('/v1/hr/leave-types', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updateLeaveType: (id: string, body: HRUpdateLeaveTypeBody) =>
      apiFetch<Envelope<HRLeaveType>>(`/v1/hr/leave-types/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deleteLeaveType: (id: string) =>
      apiFetch<void>(`/v1/hr/leave-types/${id}`, { method: 'DELETE' }),

    // Employee photo + documents
    uploadEmployeePhoto: (employeeId: string, file: File) => {
      const form = new FormData()
      form.append('file', file)
      return apiFetch<Envelope<HREmployee>>(`/v1/hr/employees/${employeeId}/photo`, {
        method: 'POST',
        body: form,
      }).then((r) => r.data)
    },
    listEmployeeDocuments: (employeeId: string) =>
      apiFetch<{ data: HREmployeeDocument[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/hr/employees/${employeeId}/documents`,
      ).then((r) => r.data),
    uploadEmployeeDocument: (
      employeeId: string,
      body: {
        file: File
        doc_type: HRDocumentType
        title: string
        description?: string
        issue_date?: string
        expiry_date?: string
      },
    ) => {
      const form = new FormData()
      form.append('file', body.file)
      form.append('doc_type', body.doc_type)
      form.append('title', body.title)
      if (body.description) form.append('description', body.description)
      if (body.issue_date) form.append('issue_date', body.issue_date)
      if (body.expiry_date) form.append('expiry_date', body.expiry_date)
      return apiFetch<Envelope<HREmployeeDocument>>(`/v1/hr/employees/${employeeId}/documents`, {
        method: 'POST',
        body: form,
      }).then((r) => r.data)
    },
    deleteEmployeeDocument: (id: string) =>
      apiFetch<void>(`/v1/hr/documents/${id}`, { method: 'DELETE' }),
    verifyEmployeeDocument: (id: string) =>
      apiFetch<void>(`/v1/hr/documents/${id}/verify`, { method: 'POST' }),
    replaceEmployeeDocument: (id: string, body: { file: File; expiry_date?: string }) => {
      const form = new FormData()
      form.append('file', body.file)
      if (body.expiry_date) form.append('expiry_date', body.expiry_date)
      return apiFetch<Envelope<HREmployeeDocument>>(`/v1/hr/documents/${id}/replace`, {
        method: 'POST',
        body: form,
      }).then((r) => r.data)
    },

    // Leave requests
    listLeaveRequests: (params?: HRLeaveFilters) =>
      apiFetch<Paginated<HRLeaveRequest>>(`/v1/hr/leave-requests${qs(params)}`),
    getLeaveKPIs: (year?: number) =>
      apiFetch<Envelope<HRLeaveKPIs>>(`/v1/hr/leave-requests/kpis${qs({ year })}`).then(
        (r) => r.data,
      ),
    getLeaveRequest: (id: string) =>
      apiFetch<Envelope<HRLeaveRequest>>(`/v1/hr/leave-requests/${id}`).then((r) => r.data),
    requestLeave: (body: HRLeaveRequestBody) =>
      apiFetch<Envelope<HRLeaveRequest>>('/v1/hr/leave-requests', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    approveLeave: (id: string, comment?: string | null) =>
      apiFetch<Envelope<HRLeaveRequest>>(`/v1/hr/leave-requests/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ comment: comment ?? null }),
      }).then((r) => r.data),
    rejectLeave: (id: string, reason: string) =>
      apiFetch<Envelope<HRLeaveRequest>>(`/v1/hr/leave-requests/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }).then((r) => r.data),
    cancelLeave: (id: string) =>
      apiFetch<Envelope<HRLeaveRequest>>(`/v1/hr/leave-requests/${id}/cancel`, {
        method: 'POST',
      }).then((r) => r.data),

    // Attendance — manager view (whole-org listing with filters)
    listAttendance: (params?: {
      from?: string
      to?: string
      employee_id?: string
      status?: HRAttendanceRecord['status']
    }) =>
      apiFetch<{ data: HRAttendanceRecord[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/hr/attendance${qs(params)}`,
      ).then((r) => r.data),

    // Attendance (admin)
    checkIn: (body: HRCheckInBody) =>
      apiFetch<Envelope<HRAttendanceRecord>>('/v1/hr/attendance/check-in', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    checkOut: (body: { employee_id: string }) =>
      apiFetch<Envelope<HRAttendanceRecord>>('/v1/hr/attendance/check-out', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    adjustAttendance: (body: HRAdjustAttendanceBody) =>
      apiFetch<Envelope<HRAttendanceRecord>>('/v1/hr/attendance/adjust', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    // Holidays
    listHolidays: (from: string, to: string) =>
      apiFetch<{ data: HRHoliday[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/hr/holidays${qs({ from, to })}`,
      ).then((r) => r.data),

    // Stats
    getStats: () => apiFetch<Envelope<HRStats>>('/v1/hr/stats').then((r) => r.data),
    getUpcomingEvents: () =>
      apiFetch<Envelope<HRUpcomingEvents>>('/v1/hr/upcoming-events').then((r) => r.data),
    getAnalytics: (params?: { from?: string; to?: string }) =>
      apiFetch<Envelope<HRAnalytics>>(`/v1/hr/analytics${qs(params ?? {})}`).then((r) => r.data),
    getEmployeesCSVUrl: () => `${BASE_URL}/v1/hr/analytics/employees.csv`,

    // Document requests — HR queue
    listDocumentRequests: (status?: HRDocumentRequest['status']) =>
      apiFetch<{ data: HRDocumentRequest[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/hr/document-requests${qs({ status })}`,
      ).then((r) => r.data),
    issueDocumentRequest: (id: string) =>
      apiFetch<Envelope<HRDocumentRequest>>(`/v1/hr/document-requests/${id}/issue`, {
        method: 'POST',
      }).then((r) => r.data),
    rejectDocumentRequest: (id: string, reason: string) =>
      apiFetch<Envelope<HRDocumentRequest>>(`/v1/hr/document-requests/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }).then((r) => r.data),

    // Document settings — templates + signature for issued HR documents
    getDocumentSettings: () =>
      apiFetch<Envelope<HRDocumentSettings>>('/v1/hr/document-settings').then((r) => r.data),
    updateDocumentSettings: (body: HRUpdateDocumentSettingsBody) =>
      apiFetch<Envelope<HRDocumentSettings>>('/v1/hr/document-settings', {
        method: 'PUT',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    uploadDocumentSignature: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return apiFetch<Envelope<HRDocumentSettings>>('/v1/hr/document-settings/signature', {
        method: 'POST',
        body: form,
      }).then((r) => r.data)
    },
  },

  // ── Payroll / Paie Sénégal (M12) ─────────────────────────────────────────
  payroll: {
    getStats: () => apiFetch<Envelope<PayrollStats>>('/v1/payroll/stats').then((r) => r.data),
    getCurrentParameters: (on?: string) =>
      apiFetch<Envelope<PayrollParameters>>(`/v1/payroll/parameters/current${qs({ on })}`).then(
        (r) => r.data,
      ),

    listPeriods: (params?: PayrollPeriodFilters) =>
      apiFetch<Paginated<PayrollPeriod>>(`/v1/payroll/periods${qs(params)}`),
    createPeriod: (body: { year: number; month: number }) =>
      apiFetch<Envelope<PayrollPeriod>>('/v1/payroll/periods', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    getPeriod: (id: string) =>
      apiFetch<Envelope<PayrollPeriod>>(`/v1/payroll/periods/${id}`).then((r) => r.data),
    computePeriod: (id: string, body?: { work_accident_rate?: number }) =>
      apiFetch<
        Envelope<{
          period: PayrollPeriod
          failed_employees?: { employee_id: string; error: string }[]
        }>
      >(`/v1/payroll/periods/${id}/compute`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
        // Traitement par lot sur tout l'effectif : peut dépasser la minute.
        timeoutMs: LONG_RUNNING_TIMEOUT_MS,
      }).then((r) => r.data),
    validatePeriod: (id: string) =>
      apiFetch<Envelope<PayrollPeriod>>(`/v1/payroll/periods/${id}/validate`, {
        method: 'POST',
      }).then((r) => r.data),
    payPeriod: (id: string, body: PayPeriodBody) =>
      apiFetch<Envelope<PayrollPeriod>>(`/v1/payroll/periods/${id}/pay`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    closePeriod: (id: string) =>
      apiFetch<Envelope<PayrollPeriod>>(`/v1/payroll/periods/${id}/close`, {
        method: 'POST',
      }).then((r) => r.data),
    cancelPeriod: (id: string, reason?: string) =>
      apiFetch<Envelope<PayrollPeriod>>(`/v1/payroll/periods/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }).then((r) => r.data),
    deletePeriod: (id: string) => apiFetch<void>(`/v1/payroll/periods/${id}`, { method: 'DELETE' }),

    listPayslipsByPeriod: (id: string) =>
      apiFetch<{ data: Payslip[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/payroll/periods/${id}/payslips`,
      ).then((r) => r.data),
    computePayslip: (periodId: string, body: ComputePayslipBody) =>
      apiFetch<Envelope<Payslip>>(`/v1/payroll/periods/${periodId}/payslips`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    getPayslip: (id: string) =>
      apiFetch<Envelope<Payslip>>(`/v1/payroll/payslips/${id}`).then((r) => r.data),
    updatePayslip: (id: string, body: UpdatePayslipBody) =>
      apiFetch<Envelope<Payslip>>(`/v1/payroll/payslips/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    getPayslipPDFUrl: (id: string) => `${BASE_URL}/v1/payroll/payslips/${id}/pdf`,
    listPayslipsByEmployee: (employeeId: string, year?: number) =>
      apiFetch<{ data: Payslip[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/payroll/employees/${employeeId}/payslips${qs({ year })}`,
      ).then((r) => r.data),

    // ── Analytics (M13) ──────────────────────────────────────────────────
    analyticsOverview: (params?: { from?: string; to?: string }) =>
      apiFetch<Envelope<PayrollAnalyticsOverview>>(
        `/v1/payroll/analytics/overview${qs(params)}`,
      ).then((r) => r.data),
    analyticsByDepartment: (params?: { from?: string; to?: string }) =>
      apiFetch<{ data: DepartmentPayroll[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/payroll/analytics/by-department${qs(params)}`,
      ).then((r) => r.data),
    analyticsMonthlyEvolution: (year?: number) =>
      apiFetch<{ data: MonthlyEvolution[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/payroll/analytics/monthly-evolution${qs({ year })}`,
      ).then((r) => r.data),
    analyticsCostBreakdown: (params?: { from?: string; to?: string }) =>
      apiFetch<Envelope<CostBreakdown>>(`/v1/payroll/analytics/cost-breakdown${qs(params)}`).then(
        (r) => r.data,
      ),
    analyticsTopEarners: (params?: { from?: string; to?: string; limit?: number }) =>
      apiFetch<{ data: TopEarner[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/payroll/analytics/top-earners${qs(params)}`,
      ).then((r) => r.data),

    // ── Social declarations export (M13) ────────────────────────────────
    declarationDownloadUrl: (
      type: 'ipres' | 'css' | 'irpp' | 'cfce',
      params: { period: string; format?: 'csv' | 'xlsx' },
    ) => `${BASE_URL}/v1/payroll/declarations/${type}${qs(params)}`,

    patchCurrentParameters: (body: PatchPayrollParametersBody) =>
      apiFetch<Envelope<PayrollParameters>>('/v1/payroll/parameters/current', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    getPeriodReport: (periodId: string) =>
      apiFetch<Envelope<PeriodReport>>(`/v1/payroll/periods/${periodId}/report`).then(
        (r) => r.data,
      ),

    createAdvance: (body: { employee_id: string; amount: number; date: string; notes?: string }) =>
      apiFetch<Envelope<PayrollAdvance>>('/v1/payroll/advances', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    listPendingAdvances: (employeeId: string) =>
      apiFetch<Envelope<PayrollAdvance[]>>(`/v1/payroll/advances?employee_id=${employeeId}`).then(
        (r) => r.data,
      ),

    cancelAdvance: (advanceId: string) =>
      apiFetch<void>(`/v1/payroll/advances/${advanceId}`, { method: 'DELETE' }),
  },

  // ── Expenses / Notes de frais (M13) ──────────────────────────────────────
  expenses: {
    listCategories: (includeInactive?: boolean) =>
      apiFetch<{ data: ExpenseCategory[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/expenses/categories${qs({ include_inactive: includeInactive })}`,
      ).then((r) => r.data),
    createCategory: (body: Schemas['ExpenseCategoryInput']) =>
      apiFetch<Envelope<ExpenseCategory>>('/v1/expenses/categories', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updateCategory: (code: string, body: Schemas['ExpenseCategoryPatch']) =>
      apiFetch<Envelope<ExpenseCategory>>(`/v1/expenses/categories/${code}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    listRules: () =>
      apiFetch<{ data: ExpenseApprovalRule[]; meta: Schemas['PaginationMeta'] }>(
        '/v1/expenses/rules',
      ).then((r) => r.data),
    createRule: (body: Schemas['ExpenseApprovalRuleInput']) =>
      apiFetch<Envelope<ExpenseApprovalRule>>('/v1/expenses/rules', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deleteRule: (id: string) => apiFetch<void>(`/v1/expenses/rules/${id}`, { method: 'DELETE' }),

    listClaims: (params?: {
      status?: string
      expense_type?: string
      employee_id?: string
      shipment_id?: string
      vehicle_ref?: string
      q?: string
      from?: string
      to?: string
    }) =>
      apiFetch<{ data: ExpenseClaim[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/expenses/claims${qs(params)}`,
      ).then((r) => r.data),
    getClaim: (id: string) =>
      apiFetch<Envelope<ExpenseClaim>>(`/v1/expenses/claims/${id}`).then((r) => r.data),
    createClaim: (body: Schemas['CreateExpenseClaimInput']) =>
      apiFetch<Envelope<ExpenseClaim>>('/v1/expenses/claims', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    addClaimLine: (id: string, body: Schemas['CreateExpenseClaimLineInput']) =>
      apiFetch<Envelope<ExpenseClaim>>(`/v1/expenses/claims/${id}/lines`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deleteClaimLine: (id: string, lineId: string) =>
      apiFetch<Envelope<ExpenseClaim>>(`/v1/expenses/claims/${id}/lines/${lineId}`, {
        method: 'DELETE',
      }).then((r) => r.data),
    uploadReceipt: (id: string, lineId: string, file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return apiFetch<Envelope<ExpenseClaimLine>>(
        `/v1/expenses/claims/${id}/lines/${lineId}/receipt`,
        { method: 'POST', body: fd },
      ).then((r) => r.data)
    },
    submitClaim: (id: string) =>
      apiFetch<Envelope<ExpenseClaim>>(`/v1/expenses/claims/${id}/submit`, {
        method: 'POST',
      }).then((r) => r.data),
    approveClaim: (
      id: string,
      body: { step: 'manager' | 'hr' | 'finance' | 'director'; comment?: string },
    ) =>
      apiFetch<Envelope<ExpenseClaim>>(`/v1/expenses/claims/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    rejectClaim: (
      id: string,
      body: { step: 'manager' | 'hr' | 'finance' | 'director'; reason: string },
    ) =>
      apiFetch<Envelope<ExpenseClaim>>(`/v1/expenses/claims/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    reimburseClaim: (
      id: string,
      body: {
        method: 'bank_transfer' | 'cash' | 'check' | 'mobile_money' | 'payroll_deduction'
        reference: string
        paid_at?: string
      },
    ) =>
      apiFetch<Envelope<ExpenseClaim>>(`/v1/expenses/claims/${id}/reimburse`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    cancelClaim: (id: string) =>
      apiFetch<void>(`/v1/expenses/claims/${id}/cancel`, { method: 'POST' }),
  },

  // ── CRM (M08) ───────────────────────────────────────────────────────────
  crm: {
    getDashboard: () => apiFetch<Envelope<CRMDashboard>>('/v1/crm/dashboard').then((r) => r.data),
    getSegments: () =>
      apiFetch<Envelope<CRMSegmentSummary[]>>('/v1/crm/segments').then((r) => r.data),
    getAdvancedSegments: () =>
      apiFetch<Envelope<AdvancedSegments>>('/v1/crm/segments/advanced').then((r) => r.data),
    getChurnRiskList: () =>
      apiFetch<Envelope<Customer360[]>>('/v1/crm/churn-risk').then((r) => r.data),
    listUpcomingActions: (days?: number) =>
      apiFetch<Envelope<CustomerInteraction[]>>(`/v1/crm/upcoming-actions${qs({ days })}`).then(
        (r) => r.data,
      ),

    getSettings: () => apiFetch<Envelope<CRMSettings>>('/v1/crm/settings').then((r) => r.data),
    updateSettings: (body: Schemas['UpdateCRMSettingsRequest']) =>
      apiFetch<Envelope<CRMSettings>>('/v1/crm/settings', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    listCustomers: (params?: {
      tier?: CRMTier
      churn_risk?: CRMChurnRisk
      status?: 'active' | 'suspended' | 'archived'
      q?: string
      risk_flag?: boolean
      limit?: number
    }) => apiFetch<Envelope<Customer360[]>>(`/v1/crm/customers${qs(params)}`).then((r) => r.data),
    getCustomer: (id: string) =>
      apiFetch<Envelope<Customer360>>(`/v1/crm/customers/${id}`).then((r) => r.data),
    // Resolves id against leads, then opportunities, then customers —
    // powers the unified CRM entity page (one round trip instead of
    // three sequential lookups).
    getEntity: (id: string) =>
      apiFetch<Envelope<CrmEntity>>(`/v1/crm/entities/${id}`).then((r) => r.data),
    updateCustomer: (id: string, body: Schemas['UpdateCRMCustomerRequest']) =>
      apiFetch<Envelope<Customer360>>(`/v1/crm/customers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    listContacts: (customerId: string) =>
      apiFetch<Envelope<CustomerContact[]>>(`/v1/crm/customers/${customerId}/contacts`).then(
        (r) => r.data,
      ),
    addContact: (customerId: string, body: Schemas['ContactRequest']) =>
      apiFetch<Envelope<CustomerContact>>(`/v1/crm/customers/${customerId}/contacts`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updateContact: (customerId: string, contactId: string, body: Schemas['ContactRequest']) =>
      apiFetch<Envelope<CustomerContact>>(`/v1/crm/customers/${customerId}/contacts/${contactId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    removeContact: (customerId: string, contactId: string) =>
      apiFetch<void>(`/v1/crm/customers/${customerId}/contacts/${contactId}`, {
        method: 'DELETE',
      }),

    listInteractions: (
      customerId: string,
      params?: { type?: CRMInteractionType; outcome?: CRMInteractionOutcome; limit?: number },
    ) =>
      apiFetch<Envelope<CustomerInteraction[]>>(
        `/v1/crm/customers/${customerId}/interactions${qs(params)}`,
      ).then((r) => r.data),
    logInteraction: (customerId: string, body: Schemas['InteractionRequest']) =>
      apiFetch<Envelope<CustomerInteraction>>(`/v1/crm/customers/${customerId}/interactions`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    getTimeline: (customerId: string) =>
      apiFetch<Envelope<CRMTimelineItem[]>>(`/v1/crm/customers/${customerId}/timeline`).then(
        (r) => r.data,
      ),

    getScoringHistory: (customerId: string, months?: number) =>
      apiFetch<Envelope<CustomerScoring[]>>(
        `/v1/crm/customers/${customerId}/scoring${qs({ months })}`,
      ).then((r) => r.data),
    triggerRescore: (customerId: string) =>
      apiFetch<Envelope<CustomerScoring>>(`/v1/crm/customers/${customerId}/rescore`, {
        method: 'POST',
      }).then((r) => r.data),

    listOpportunities: (params?: {
      stage?: OpportunityStage
      assigned_to?: string
      priority?: OpportunityPriority
      customer_id?: string
      limit?: number
      cursor?: string
    }) =>
      apiFetch<Envelope<Opportunity[]> & { meta?: { next_cursor?: string; total?: number } }>(
        `/v1/crm/opportunities${qs(params)}`,
      ),
    createOpportunity: (body: Schemas['OpportunityRequest']) =>
      apiFetch<Envelope<Opportunity>>('/v1/crm/opportunities', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    getOpportunity: (id: string) =>
      apiFetch<Envelope<Opportunity>>(`/v1/crm/opportunities/${id}`).then((r) => r.data),
    updateOpportunity: (id: string, body: Schemas['OpportunityRequest']) =>
      apiFetch<Envelope<Opportunity>>(`/v1/crm/opportunities/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    moveOpportunityStage: (id: string, body: Schemas['MoveOpportunityStageRequest']) =>
      apiFetch<Envelope<Opportunity>>(`/v1/crm/opportunities/${id}/stage`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deleteOpportunity: (id: string) =>
      apiFetch<void>(`/v1/crm/opportunities/${id}`, { method: 'DELETE' }),
    getOpportunityHistory: (id: string) =>
      apiFetch<Envelope<OpportunityStageChange[]>>(`/v1/crm/opportunities/${id}/history`).then(
        (r) => r.data,
      ),

    listLeads: (params?: {
      status?: LeadStatus
      source?: LeadSource
      assigned_to?: string
      limit?: number
      cursor?: string
    }) =>
      apiFetch<Envelope<Lead[]> & { meta?: { next_cursor?: string; total?: number } }>(
        `/v1/crm/leads${qs(params)}`,
      ),
    createLead: (body: Schemas['LeadRequest']) =>
      apiFetch<Envelope<Lead>>('/v1/crm/leads', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    importLeads: (rows: Schemas['LeadRequest'][]) =>
      apiFetch<Envelope<LeadImportResult[]>>('/v1/crm/leads/import', {
        method: 'POST',
        body: JSON.stringify(rows),
      }).then((r) => r.data),
    getLead: (id: string) => apiFetch<Envelope<Lead>>(`/v1/crm/leads/${id}`).then((r) => r.data),
    updateLead: (id: string, body: Schemas['LeadRequest']) =>
      apiFetch<Envelope<Lead>>(`/v1/crm/leads/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deleteLead: (id: string) => apiFetch<void>(`/v1/crm/leads/${id}`, { method: 'DELETE' }),
    qualifyLead: (id: string) =>
      apiFetch<Envelope<Lead>>(`/v1/crm/leads/${id}/qualify`, { method: 'POST' }).then(
        (r) => r.data,
      ),
    downgradeLead: (id: string) =>
      apiFetch<Envelope<Lead>>(`/v1/crm/leads/${id}/downgrade`, { method: 'POST' }).then(
        (r) => r.data,
      ),
    archiveLead: (id: string) =>
      apiFetch<Envelope<Lead>>(`/v1/crm/leads/${id}/archive`, { method: 'POST' }).then(
        (r) => r.data,
      ),
    reactivateLead: (id: string) =>
      apiFetch<Envelope<Lead>>(`/v1/crm/leads/${id}/reactivate`, { method: 'POST' }).then(
        (r) => r.data,
      ),
    convertLead: (id: string, body: Schemas['ConvertLeadRequest']) =>
      apiFetch<Envelope<ConvertLeadResponse>>(`/v1/crm/leads/${id}/convert`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    listActivities: (params?: {
      status?: ActivityStatus
      activity_type?: ActivityType
      assigned_to?: string
      customer_id?: string
      lead_id?: string
      opportunity_id?: string
      due_after?: string
      due_before?: string
      limit?: number
      cursor?: string
    }) =>
      apiFetch<Envelope<Activity[]> & { meta?: { next_cursor?: string; total?: number } }>(
        `/v1/crm/activities${qs(params)}`,
      ),
    createActivity: (body: Schemas['ActivityRequest']) =>
      apiFetch<Envelope<Activity>>('/v1/crm/activities', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    getActivity: (id: string) =>
      apiFetch<Envelope<Activity>>(`/v1/crm/activities/${id}`).then((r) => r.data),
    updateActivity: (id: string, body: Schemas['ActivityRequest']) =>
      apiFetch<Envelope<Activity>>(`/v1/crm/activities/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    completeActivity: (id: string) =>
      apiFetch<Envelope<Activity>>(`/v1/crm/activities/${id}/complete`, {
        method: 'POST',
      }).then((r) => r.data),
    cancelActivity: (id: string) =>
      apiFetch<Envelope<Activity>>(`/v1/crm/activities/${id}/cancel`, {
        method: 'POST',
      }).then((r) => r.data),
    deleteActivity: (id: string) =>
      apiFetch<void>(`/v1/crm/activities/${id}`, { method: 'DELETE' }),

    listSalesTargets: (period?: string) =>
      apiFetch<Envelope<SalesTarget[]>>(`/v1/crm/sales-targets${qs({ period })}`).then(
        (r) => r.data,
      ),
    setSalesTarget: (body: Schemas['SalesTargetRequest']) =>
      apiFetch<Envelope<SalesTarget>>('/v1/crm/sales-targets', {
        method: 'PUT',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    getForecast: (period?: string) =>
      apiFetch<Envelope<Forecast>>(`/v1/crm/forecast${qs({ period })}`).then((r) => r.data),
  },

  // ── Avances & Prêts — surface d'approbation (Manager / RH / Finance) ────
  advances: {
    list: (params?: ListParams & { status?: string; kind?: 'advance' | 'loan' }) =>
      apiFetch<Paginated<SalaryAdvance>>(`/v1/advances${qs(params)}`),
    get: (id: string) =>
      apiFetch<Envelope<AdvanceDetail>>(`/v1/advances/${id}`).then((r) => r.data),
    approveManager: (id: string, body?: Schemas['ApproveAdvanceRequest']) =>
      apiFetch<Envelope<SalaryAdvance>>(`/v1/advances/${id}/approve/manager`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }).then((r) => r.data),
    approveHR: (id: string, body?: Schemas['ApproveAdvanceRequest']) =>
      apiFetch<Envelope<SalaryAdvance>>(`/v1/advances/${id}/approve/hr`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }).then((r) => r.data),
    approveFinance: (id: string, body?: Schemas['ApproveAdvanceRequest']) =>
      apiFetch<Envelope<SalaryAdvance>>(`/v1/advances/${id}/approve/finance`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }).then((r) => r.data),
    reject: (id: string, body: Schemas['RejectAdvanceRequest']) =>
      apiFetch<Envelope<SalaryAdvance>>(`/v1/advances/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    settle: (id: string, body?: Schemas['SettleAdvanceRequest']) =>
      apiFetch<Envelope<SalaryAdvance>>(`/v1/advances/${id}/settle`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }).then((r) => r.data),
  },

  // ── Self-service (M11) ──────────────────────────────────────────────────
  my: {
    // PERF F4 — single HTTP RT + single DB tx replacing 4 parallel calls
    // (profile + balance + recent leaves + month attendance).
    getDashboard: (params?: { year?: number; month?: number; recent_limit?: number }) =>
      apiFetch<Envelope<HRMyDashboard>>(`/v1/my/dashboard${qs(params)}`).then((r) => r.data),
    getProfile: () => apiFetch<Envelope<HREmployee>>('/v1/my/profile').then((r) => r.data),
    updateProfile: (body: UpdateMyEmployeeProfileBody) =>
      apiFetch<Envelope<HREmployee>>('/v1/my/profile', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    // GED — fichiers RH du collaborateur (CNI, contrat, diplômes…).
    listMyEmployeeDocuments: (
      params?: ListParams & { type?: string; expired?: boolean; search?: string },
    ) => apiFetch<Paginated<HREmployeeDocument>>(`/v1/my/employee-documents${qs(params)}`),
    listDocumentRequests: (params?: ListParams) =>
      apiFetch<Paginated<HRDocumentRequest>>(`/v1/my/document-requests${qs(params)}`),
    requestDocument: (body: { doc_type: HRDocumentRequest['doc_type']; reason?: string | null }) =>
      apiFetch<Envelope<HRDocumentRequest>>('/v1/my/document-requests', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    getDocumentRequest: (id: string) =>
      apiFetch<Envelope<HRDocumentRequest>>(`/v1/my/document-requests/${id}`).then((r) => r.data),
    getBalance: (year?: number) =>
      apiFetch<{ data: HRLeaveBalance[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/my/balance${qs({ year })}`,
      ).then((r) => r.data),
    listLeaves: (params?: ListParams) =>
      apiFetch<Paginated<HRLeaveRequest>>(`/v1/my/leaves${qs(params)}`),
    getLeave: (id: string) =>
      apiFetch<Envelope<HRLeaveRequest>>(`/v1/my/leaves/${id}`).then((r) => r.data),
    listHolidays: (params?: { from?: string; to?: string }) =>
      apiFetch<{ data: HRHoliday[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/my/holidays${qs(params)}`,
      ).then((r) => r.data),
    requestLeave: (body: HRLeaveRequestBody) =>
      apiFetch<Envelope<HRLeaveRequest>>('/v1/my/leaves', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    cancelLeave: (id: string) =>
      apiFetch<Envelope<HRLeaveRequest>>(`/v1/my/leaves/${id}/cancel`, {
        method: 'POST',
      }).then((r) => r.data),
    getAttendance: (year: number, month: number) =>
      apiFetch<{ data: HRAttendanceRecord[]; meta: Schemas['PaginationMeta'] }>(
        `/v1/my/attendance${qs({ year, month })}`,
      ).then((r) => r.data),
    checkIn: () =>
      apiFetch<Envelope<HRAttendanceRecord>>('/v1/my/attendance/check-in', {
        method: 'POST',
      }).then((r) => r.data),
    checkOut: () =>
      apiFetch<Envelope<HRAttendanceRecord>>('/v1/my/attendance/check-out', {
        method: 'POST',
      }).then((r) => r.data),
    getTeamStats: () => apiFetch<Envelope<HRStats>>('/v1/my/team/stats').then((r) => r.data),

    // ── Avances & prêts (M-Avances) ──────────────────────────────────────
    listAdvances: (params?: ListParams) =>
      apiFetch<Paginated<SalaryAdvance>>(`/v1/my/advances${qs(params)}`),
    getAdvance: (id: string) =>
      apiFetch<Envelope<AdvanceDetail>>(`/v1/my/advances/${id}`).then((r) => r.data),
    getAdvancesSummary: () =>
      apiFetch<Envelope<AdvancesSummary>>('/v1/my/advances/summary').then((r) => r.data),
    createAdvance: (body: CreateAdvanceRequestBody) =>
      apiFetch<Envelope<SalaryAdvance>>('/v1/my/advances', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    cancelAdvance: (id: string) =>
      apiFetch<Envelope<SalaryAdvance>>(`/v1/my/advances/${id}/cancel`, {
        method: 'POST',
      }).then((r) => r.data),

    // M12 — self-service payslips
    listPayslips: (params?: ListParams & { year?: number }) =>
      apiFetch<Paginated<Payslip>>(`/v1/my/payslips${qs(params)}`),
    getPayslip: (id: string) =>
      apiFetch<Envelope<Payslip>>(`/v1/my/payslips/${id}`).then((r) => r.data),
    getPayslipPDFUrl: (id: string) => `${BASE_URL}/v1/my/payslips/${id}/pdf`,

    // ── 17C — sécurité & préférences self-service ─────────────────────────
    changePassword: (body: { current_password: string; new_password: string }) =>
      apiFetch<void>('/v1/my/password', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    getPreferences: () =>
      apiFetch<Envelope<Schemas['UserPreferences']>>('/v1/my/preferences').then((r) => r.data),
    updatePreferences: (body: Schemas['UpdatePreferencesRequest']) =>
      apiFetch<Envelope<Schemas['UserPreferences']>>('/v1/my/preferences', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    listSessions: () =>
      apiFetch<{ data: Schemas['Session'][] }>('/v1/my/sessions').then((r) => r.data),
    revokeMySession: (id: string) =>
      apiFetch<void>(`/v1/my/sessions/${id}/revoke`, { method: 'POST' }),
    revokeOtherSessions: () => apiFetch<void>('/v1/my/sessions/revoke-others', { method: 'POST' }),
    loginHistory: (params?: { limit?: number }) =>
      apiFetch<{ data: Schemas['LoginAttempt'][] }>(`/v1/my/login-history${qs(params)}`).then(
        (r) => r.data,
      ),
    listMyDelegations: () =>
      apiFetch<{
        data: {
          as_delegator: Schemas['RoleDelegation'][]
          as_delegatee: Schemas['RoleDelegation'][]
        }
      }>('/v1/my/delegations').then((r) => r.data),
    createMyDelegation: (body: Schemas['CreateDelegationRequest']) =>
      apiFetch<Envelope<Schemas['RoleDelegation']>>('/v1/my/delegations', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
  },

  // ── 17C — Workspaces ────────────────────────────────────────────────────
  workspaces: {
    listMine: () =>
      apiFetch<{ data: Schemas['Workspace'][] }>('/v1/workspaces').then((r) => r.data),
  },

  // ── 17C — Admin RBAC, sessions, audit, password policy ──────────────────
  admin: {
    // Roles
    listRoles: () => apiFetch<{ data: Schemas['Role'][] }>('/v1/admin/roles').then((r) => r.data),
    getRole: (id: string) =>
      apiFetch<Envelope<Schemas['Role']>>(`/v1/admin/roles/${id}`).then((r) => r.data),
    createRole: (body: Schemas['CreateRoleRequest']) =>
      apiFetch<Envelope<Schemas['Role']>>('/v1/admin/roles', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    updateRole: (id: string, body: Schemas['UpdateRoleRequest']) =>
      apiFetch<Envelope<Schemas['Role']>>(`/v1/admin/roles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    deleteRole: (id: string) => apiFetch<void>(`/v1/admin/roles/${id}`, { method: 'DELETE' }),
    listPermissions: () =>
      apiFetch<{ data: Schemas['PermissionGroup'][] }>('/v1/admin/permissions').then((r) => r.data),

    // User roles (multi-rôles)
    listUserRoles: (userId: string) =>
      apiFetch<{ data: Schemas['UserRole'][] }>(`/v1/admin/users/${userId}/roles`).then(
        (r) => r.data,
      ),
    assignRole: (userId: string, roleId: string) =>
      apiFetch<Envelope<Schemas['UserRole']>>(`/v1/admin/users/${userId}/roles`, {
        method: 'POST',
        body: JSON.stringify({ role_id: roleId }),
      }).then((r) => r.data),
    revokeUserRole: (userRoleId: string, reason: string) =>
      apiFetch<void>(`/v1/admin/user-roles/${userRoleId}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason }),
      }),

    // Sessions admin
    listUserSessions: (userId: string) =>
      apiFetch<{ data: Schemas['Session'][] }>(`/v1/admin/users/${userId}/sessions`).then(
        (r) => r.data,
      ),
    revokeAllUserSessions: (userId: string) =>
      apiFetch<void>(`/v1/admin/users/${userId}/sessions/revoke-all`, {
        method: 'POST',
      }),

    // Sécurité user
    unlockUser: (userId: string) =>
      apiFetch<void>(`/v1/admin/users/${userId}/unlock`, { method: 'POST' }),
    // send_email=false (default): returns temp_password in clear once.
    // send_email=true: no temp_password — a reset link is emailed, the
    // response only confirms dispatch via email_sent.
    resetUserPassword: (userId: string, body?: Schemas['AdminResetPasswordRequest']) =>
      apiFetch<Envelope<Schemas['AdminResetPasswordResponse']>>(
        `/v1/admin/users/${userId}/reset-password`,
        { method: 'POST', body: body ? JSON.stringify(body) : undefined },
      ).then((r) => r.data),

    // Délégations
    listDelegations: (params?: {
      delegator_id?: string
      delegatee_id?: string
      status?: 'pending' | 'active' | 'expired' | 'revoked'
      cursor?: string
      limit?: number
    }) => apiFetch<Paginated<Schemas['RoleDelegation']>>(`/v1/admin/delegations${qs(params)}`),
    createDelegation: (body: Schemas['CreateDelegationRequest']) =>
      apiFetch<Envelope<Schemas['RoleDelegation']>>('/v1/admin/delegations', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),
    revokeDelegation: (id: string, reason: string) =>
      apiFetch<Envelope<Schemas['RoleDelegation']>>(`/v1/admin/delegations/${id}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }).then((r) => r.data),

    // Audit log (utilise /v1/audit existant — le backend filtre par tenant)

    listAuditEvents: (params?: {
      cursor?: string
      limit?: number
      resource_type?: string
      resource_id?: string
      user_id?: string
      action?: string
      severity?: 'info' | 'warning' | 'critical'
      category?: string
      session_id?: string
      q?: string
      from?: string
      to?: string
    }) => apiFetch<Paginated<Schemas['AuditLogEntry']>>(`/v1/audit${qs(params)}`),
    getAuditEvent: (id: string) =>
      apiFetch<Envelope<Schemas['AuditLogEntry']>>(`/v1/audit/${id}`).then((r) => r.data),
    getAuditStats: (window?: '24h' | '7d' | '30d') =>
      apiFetch<Envelope<Schemas['AuditStats']>>(`/v1/audit/stats${qs({ window })}`).then(
        (r) => r.data,
      ),
    getAuditSession: (sessionId: string) =>
      apiFetch<Paginated<Schemas['AuditLogEntry']>>(`/v1/audit/sessions/${sessionId}`),
    // Consumed by <ExportButtons getUrl={...}> — the component itself
    // fetches with the bearer token and triggers the blob download.
    getAuditExportUrl: (params?: {
      resource_type?: string
      resource_id?: string
      user_id?: string
      action?: string
      severity?: 'info' | 'warning' | 'critical'
      category?: string
      session_id?: string
      q?: string
      from?: string
      to?: string
    }) => `${BASE_URL}/v1/audit/export.csv${qs(params)}`,

    // Politique mot de passe
    getPasswordPolicy: () =>
      apiFetch<Envelope<Schemas['PasswordPolicy']>>('/v1/admin/password-policy').then(
        (r) => r.data,
      ),
    updatePasswordPolicy: (body: Partial<Schemas['PasswordPolicy']>) =>
      apiFetch<Envelope<Schemas['PasswordPolicy']>>('/v1/admin/password-policy', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),
  },

  // ── Learn Hub (M-Learn) ─────────────────────────────────────────────────
  learn: {
    listCollections: () =>
      apiFetch<{ data: LearnCollection[] }>('/v1/learn/collections').then((r) => r.data),

    getCollection: (slug: string) =>
      apiFetch<Envelope<LearnCollection>>(`/v1/learn/collections/${slug}`).then((r) => r.data),

    listArticles: (params?: { collection?: string; tag?: string; q?: string; limit?: number }) =>
      apiFetch<{ data: LearnArticleSummary[] }>(`/v1/learn/articles${qs(params)}`).then(
        (r) => r.data,
      ),

    getArticle: (slug: string) =>
      apiFetch<Envelope<LearnArticle>>(`/v1/learn/articles/${slug}`).then((r) => r.data),

    getArticleByRoute: (route: string) =>
      apiFetch<Envelope<LearnArticle | null>>(
        `/v1/learn/articles/by-route?route=${encodeURIComponent(route)}`,
      ).then((r) => r.data),

    search: (q: string) =>
      apiFetch<{ data: LearnSearchResult[] }>(`/v1/learn/search?q=${encodeURIComponent(q)}`).then(
        (r) => r.data,
      ),

    getGlossary: () =>
      apiFetch<{ data: LearnGlossaryTerm[] }>('/v1/learn/glossary').then((r) => r.data),

    getWalkthrough: (id: string) =>
      apiFetch<Envelope<LearnWalkthrough>>(`/v1/learn/walkthroughs/${id}`).then((r) => r.data),

    startWalkthrough: (id: string) =>
      apiFetch<void>(`/v1/learn/walkthroughs/${id}/started`, { method: 'POST' }),

    completeWalkthrough: (id: string) =>
      apiFetch<void>(`/v1/learn/walkthroughs/${id}/completed`, { method: 'POST' }),

    trackProgress: (articleId: string, body: { completed?: boolean; feedback?: boolean }) =>
      apiFetch<void>(`/v1/learn/articles/${articleId}/progress`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    getMyProgress: () =>
      apiFetch<{ data: LearnUserProgress[] }>('/v1/learn/my-progress').then((r) => r.data),

    trackView: (articleId: string) =>
      apiFetch<void>(`/v1/learn/articles/${articleId}/view`, { method: 'POST' }),
  },

  // ── Learn Hub admin (learn:write) ───────────────────────────────────────
  learnAdmin: {
    listArticles: () =>
      apiFetch<{ data: LearnArticleSummary[] }>('/v1/learn-admin/articles').then((r) => r.data),

    createArticle: (body: LearnArticleInput) =>
      apiFetch<Envelope<LearnArticle>>('/v1/learn-admin/articles', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateArticle: (id: string, body: LearnArticleInput) =>
      apiFetch<Envelope<LearnArticle>>(`/v1/learn-admin/articles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    publishArticle: (id: string) =>
      apiFetch<void>(`/v1/learn-admin/articles/${id}/publish`, { method: 'POST' }),

    unpublishArticle: (id: string) =>
      apiFetch<void>(`/v1/learn-admin/articles/${id}/unpublish`, { method: 'POST' }),

    listGlossary: () =>
      apiFetch<{ data: LearnGlossaryTerm[] }>('/v1/learn-admin/glossary').then((r) => r.data),

    createGlossaryTerm: (body: LearnGlossaryInput) =>
      apiFetch<Envelope<LearnGlossaryTerm>>('/v1/learn-admin/glossary', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateGlossaryTerm: (id: string, body: LearnGlossaryInput) =>
      apiFetch<Envelope<LearnGlossaryTerm>>(`/v1/learn-admin/glossary/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    listWalkthroughs: () =>
      apiFetch<{ data: LearnWalkthrough[] }>('/v1/learn-admin/walkthroughs').then((r) => r.data),

    createWalkthrough: (body: LearnWalkthroughInput) =>
      apiFetch<Envelope<LearnWalkthrough>>('/v1/learn-admin/walkthroughs', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateWalkthrough: (id: string, body: LearnWalkthroughInput) =>
      apiFetch<Envelope<LearnWalkthrough>>(`/v1/learn-admin/walkthroughs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    publishWalkthrough: (id: string) =>
      apiFetch<void>(`/v1/learn-admin/walkthroughs/${id}/publish`, { method: 'POST' }),

    getAnalytics: () =>
      apiFetch<Envelope<LearnAnalytics>>('/v1/learn-admin/analytics').then((r) => r.data),
  },

  freight: {
    list: (params?: FreightListParams) =>
      apiFetch<Paginated<FreightFileSummary>>(`/v1/freight/files${qs(params)}`),

    queueCounts: (params?: { owner?: 'me' }) =>
      apiFetch<Envelope<FreightQueueCounts>>(`/v1/freight/files/queue-counts${qs(params)}`).then(
        (r) => r.data,
      ),

    get: (id: string) =>
      apiFetch<Envelope<FreightFile>>(`/v1/freight/files/${id}`).then((r) => r.data),

    create: (body: FreightFileWrite) =>
      apiFetch<Envelope<FreightFile>>('/v1/freight/files', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    update: (id: string, body: FreightFileWrite) =>
      apiFetch<Envelope<FreightFile>>(`/v1/freight/files/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    transition: (id: string, status: FreightStatus) =>
      apiFetch<Envelope<FreightFile>>(`/v1/freight/files/${id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      }).then((r) => r.data),

    addContainer: (id: string, body: FreightContainerWrite) =>
      apiFetch<Envelope<FreightFile>>(`/v1/freight/files/${id}/containers`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateContainer: (id: string, containerId: string, body: FreightContainerPatch) =>
      apiFetch<Envelope<FreightFile>>(`/v1/freight/files/${id}/containers/${containerId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    putDocuments: (id: string, documents: FreightDocumentWrite[]) =>
      apiFetch<Envelope<FreightFile>>(`/v1/freight/files/${id}/documents`, {
        method: 'PUT',
        body: JSON.stringify({ documents }),
      }).then((r) => r.data),

    listConsolidations: (params?: { cursor?: string; limit?: number }) =>
      apiFetch<Paginated<FreightConsolidationSummary>>(`/v1/freight/consolidations${qs(params)}`),

    getConsolidation: (id: string) =>
      apiFetch<Envelope<FreightConsolidation>>(`/v1/freight/consolidations/${id}`).then((r) => r.data),

    createConsolidation: (body: FreightConsolidationWrite) =>
      apiFetch<Envelope<FreightConsolidation>>('/v1/freight/consolidations', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateConsolidation: (id: string, body: FreightConsolidationWrite) =>
      apiFetch<Envelope<FreightConsolidation>>(`/v1/freight/consolidations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    transitionConsolidation: (id: string, status: FreightConsolidationStatus) =>
      apiFetch<Envelope<FreightConsolidation>>(`/v1/freight/consolidations/${id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      }).then((r) => r.data),

    attachHouse: (id: string, body: FreightHouseWrite) =>
      apiFetch<Envelope<FreightConsolidation>>(`/v1/freight/consolidations/${id}/houses`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    detachHouse: (id: string, fileId: string) =>
      apiFetch<void>(`/v1/freight/consolidations/${id}/houses/${fileId}`, { method: 'DELETE' }),

    addConsolidationCost: (id: string, body: FreightConsolidationCostWrite) =>
      apiFetch<Envelope<FreightConsolidation>>(`/v1/freight/consolidations/${id}/costs`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    allocateConsolidation: (id: string) =>
      apiFetch<Envelope<FreightConsolidation>>(`/v1/freight/consolidations/${id}/allocate`, {
        method: 'POST',
      }).then((r) => r.data),

    locations: (params?: { q?: string; kind?: FreightLocationKind }) =>
      apiFetch<Envelope<FreightLocation[]>>(`/v1/freight/locations${qs(params)}`).then(
        (r) => r.data,
      ),

    companies: (params?: { q?: string; mode?: FreightMode }) =>
      apiFetch<Envelope<FreightCompany[]>>(`/v1/freight/companies${qs(params)}`).then(
        (r) => r.data,
      ),

    getCompany: (id: string) =>
      apiFetch<Envelope<FreightCompanyDetail>>(`/v1/freight/companies/${id}`).then((r) => r.data),

    createCompany: (body: FreightCompanyWrite) =>
      apiFetch<Envelope<FreightCompany>>('/v1/freight/companies', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateCompany: (id: string, body: FreightCompanyWrite) =>
      apiFetch<Envelope<FreightCompany>>(`/v1/freight/companies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    createCompanyContact: (id: string, body: FreightContactWrite) =>
      apiFetch<Envelope<FreightContact>>(`/v1/freight/companies/${id}/contacts`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateCompanyContact: (id: string, contactId: string, body: FreightContactWrite) =>
      apiFetch<Envelope<FreightContact>>(`/v1/freight/companies/${id}/contacts/${contactId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    deleteCompanyContact: (id: string, contactId: string) =>
      apiFetch<void>(`/v1/freight/companies/${id}/contacts/${contactId}`, { method: 'DELETE' }),

    createCompanyRate: (id: string, body: FreightRateWrite) =>
      apiFetch<Envelope<FreightRate>>(`/v1/freight/companies/${id}/rates`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateCompanyRate: (id: string, rateId: string, body: FreightRateWrite) =>
      apiFetch<Envelope<FreightRate>>(`/v1/freight/companies/${id}/rates/${rateId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    deleteCompanyRate: (id: string, rateId: string) =>
      apiFetch<void>(`/v1/freight/companies/${id}/rates/${rateId}`, { method: 'DELETE' }),

    createCompanyRule: (id: string, body: FreightRuleWrite) =>
      apiFetch<Envelope<FreightRule>>(`/v1/freight/companies/${id}/rules`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    updateCompanyRule: (id: string, ruleId: string, body: FreightRuleWrite) =>
      apiFetch<Envelope<FreightRule>>(`/v1/freight/companies/${id}/rules/${ruleId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.data),

    deleteCompanyRule: (id: string, ruleId: string) =>
      apiFetch<void>(`/v1/freight/companies/${id}/rules/${ruleId}`, { method: 'DELETE' }),

    previewCompanyRates: (
      id: string,
      params: {
        mode: FreightMode
        origin?: string
        destination?: string
        equipment?: string
        on: string
      },
    ) =>
      apiFetch<Envelope<FreightRatePreview>>(
        `/v1/freight/companies/${id}/rate-preview${qs(params)}`,
      ).then((r) => r.data),
  },
}

// ── Freight FCL (M02) ────────────────────────────────────────────────────────

export type FreightMode = 'sea' | 'air' | 'road'
export type FreightLocationKind = 'sea' | 'air' | 'inland'

export type FreightPaymentTerms = 'prepaid' | 'collect' | 'credit'
export type FreightContactRole = 'booking' | 'documentation' | 'local_agent' | 'commercial'
export type FreightChargeCode = 'OFR' | 'AFC' | 'RFR' | 'THC' | 'BAF' | 'DOC' | 'SEAL' | 'ISPS'
export type FreightBasis = 'per_container' | 'per_kg' | 'per_bl' | 'per_truck' | 'percent'
export type FreightRuleCode =
  | 'DEMURRAGE_FREE_DAYS'
  | 'DETENTION_FREE_DAYS'
  | 'AIR_VOLUMETRIC_CM3_PER_KG'
  | 'AIR_MIN_KG'
  | 'ROAD_WAITING_FREE_HOURS'

export interface FreightCompany {
  id: string
  name: string
  legal_name: string
  trade_name?: string | null
  code: string
  scac?: string | null
  iata_code?: string | null
  modes: FreightMode[]
  country_code?: string | null
  website?: string | null
  currency: string
  payment_terms?: FreightPaymentTerms | null
  credit_days?: number | null
  notes?: string | null
  active: boolean
}

export interface FreightCompanyWrite {
  name: string
  legal_name?: string
  trade_name?: string | null
  code: string
  scac?: string | null
  iata_code?: string | null
  modes: FreightMode[]
  country_code?: string | null
  website?: string | null
  currency?: string
  payment_terms?: FreightPaymentTerms | null
  credit_days?: number | null
  notes?: string | null
  active?: boolean
}

export interface FreightContact {
  id: string
  role: FreightContactRole
  name: string
  email?: string | null
  phone?: string | null
  city?: string | null
}

export interface FreightContactWrite {
  role: FreightContactRole
  name: string
  email?: string | null
  phone?: string | null
  city?: string | null
}

export interface FreightRate {
  id: string
  mode: FreightMode
  charge_code: FreightChargeCode
  basis: FreightBasis
  origin_code?: string
  destination_code?: string
  equipment?: string
  amount_cents: number
  currency: string
  min_amount_cents?: number | null
  valid_from: string
  valid_until?: string | null
  notes?: string | null
}

export interface FreightRateWrite {
  mode: FreightMode
  charge_code: string
  basis: string
  origin_code?: string
  destination_code?: string
  equipment?: string
  amount_cents: number
  currency?: string
  min_amount_cents?: number | null
  valid_from: string
  valid_until?: string | null
  notes?: string | null
}

export interface FreightRule {
  id: string
  mode: FreightMode
  rule_code: FreightRuleCode
  origin_code?: string
  destination_code?: string
  equipment?: string
  value_int: number
  valid_from: string
  valid_until?: string | null
}

export interface FreightRuleWrite {
  mode: FreightMode
  rule_code: string
  origin_code?: string
  destination_code?: string
  equipment?: string
  value_int: number
  valid_from: string
  valid_until?: string | null
}

export interface FreightCompanyDetail extends FreightCompany {
  contacts: FreightContact[]
  rates: FreightRate[]
  rules: FreightRule[]
}

export interface FreightRatePreview {
  freight?: FreightRate | null
  charges: FreightRate[]
  rules: FreightRule[]
}

export interface FreightLocation {
  code: string
  name: string
  country_code: string
  kind: FreightLocationKind
}

export type FreightStatus =
  | 'opened'
  | 'booked'
  | 'on_board'
  | 'arrived'
  | 'in_customs'
  | 'delivered'
  | 'empty_returned'
  | 'closed'
  | 'cancelled'

export type FreightQueue = 'free_time' | 'arriving' | 'customs' | 'empty_return'

export type FreightEquipment = '20GP' | '40GP' | '40HC' | '40RF'

export type FreightDocType =
  | 'commercial_invoice'
  | 'packing_list'
  | 'bill_of_lading'
  | 'certificate_of_origin'
  | 'delivery_order'
  | 'bsc'

export interface FreightQueueCounts {
  free_time: number
  arriving: number
  customs: number
  empty_return: number
}

export interface FreightFileSummary {
  id: string
  ref: string
  status: FreightStatus
  customer_name: string
  carrier_name?: string
  vessel?: string | null
  voyage?: string | null
  mbl_no?: string | null
  bl_release?: 'original' | 'telex' | 'seaway' | null
  eta?: string | null
  container_count: number
  demurrage_days_left?: number | null
  created_at: string
}

export interface FreightContainer {
  id: string
  number: string
  equipment: FreightEquipment
  seal_no?: string | null
  tare_kg?: number | null
  cargo_weight_kg?: number | null
  packages?: number | null
  is_reefer: boolean
  temperature_c?: number | null
  discharged_at?: string | null
  gate_out_at?: string | null
  empty_returned_at?: string | null
  demurrage_free_days: number
  detention_free_days: number
  check_digit_ok: boolean
  demurrage_days_left?: number | null
  detention_days_left?: number | null
}

export interface FreightDocument {
  doc_type: FreightDocType
  received_at?: string | null
}

export interface FreightFile {
  id: string
  ref: string
  direction: 'import' | 'export'
  service: string
  status: FreightStatus
  customer_id: string
  customer_name: string
  carrier_supplier_id?: string | null
  carrier_company_id?: string | null
  carrier_name?: string
  incoterm?: string | null
  freight_payment?: 'prepaid' | 'collect' | null
  pol?: string | null
  pod: string
  place_of_delivery?: string | null
  booking_no?: string | null
  mbl_no?: string | null
  hbl_no?: string | null
  bl_release?: 'original' | 'telex' | 'seaway' | null
  vessel?: string | null
  voyage?: string | null
  etd?: string | null
  eta?: string | null
  ata?: string | null
  goods_description?: string | null
  package_count?: number | null
  gross_weight_kg?: number | null
  volume_cbm?: number | null
  measured_weight_kg?: number | null
  measured_volume_cbm?: number | null
  freight_amount_cents: number
  allocated_cost_cents?: number
  freight_currency: string
  booking_id?: string | null
  customs_declaration_id?: string | null
  consolidation_id?: string | null
  is_dangerous?: boolean
  notes?: string | null
  created_at: string
  updated_at: string
  containers: FreightContainer[]
  documents: FreightDocument[]
}

export type FreightConsolidationStatus =
  | 'planning'
  | 'open'
  | 'sealed'
  | 'departed'
  | 'arrived'
  | 'stripping'
  | 'closed'
  | 'cancelled'

export interface FreightHouse {
  id: string
  ref: string
  customer_id: string
  customer_name: string
  status: FreightStatus
  hbl_no?: string | null
  goods_description?: string | null
  package_count?: number | null
  weight_kg: number
  volume_cbm: number
  chargeable_wm: number
  freight_amount_cents: number
  allocated_cost_cents: number
  is_dangerous: boolean
  measured: boolean
}

export interface FreightConsolidationCost {
  id: string
  cost_type: string
  supplier_id?: string | null
  amount_cents: number
  allocatable: boolean
  notes?: string | null
}

export interface FreightConsolidation {
  id: string
  org_id: string
  ref: string
  direction: 'import' | 'export'
  mode: string
  status: FreightConsolidationStatus
  carrier_supplier_id?: string | null
  carrier_name?: string
  mbl_no?: string | null
  container_number?: string | null
  equipment?: string | null
  seal_no?: string | null
  pol?: string | null
  pod: string
  vessel?: string | null
  voyage?: string | null
  cutoff_at?: string | null
  etd?: string | null
  eta?: string | null
  atd?: string | null
  ata?: string | null
  capacity_cbm?: number | null
  capacity_weight_kg?: number | null
  used_weight_kg: number
  used_volume_cbm: number
  used_wm: number
  fill_rate_pct?: number | null
  allocatable_cost_cents: number
  sell_cents: number
  notes?: string | null
  created_at: string
  updated_at: string
  houses: FreightHouse[]
  costs: FreightConsolidationCost[]
}

export interface FreightConsolidationSummary {
  id: string
  ref: string
  status: FreightConsolidationStatus
  pod: string
  pol?: string | null
  container_number?: string | null
  equipment?: string | null
  mbl_no?: string | null
  eta?: string | null
  house_count: number
  created_at: string
}

export interface FreightConsolidationWrite {
  carrier_supplier_id?: string | null
  clear_carrier?: boolean
  mbl_no?: string | null
  container_number?: string | null
  equipment?: string | null
  seal_no?: string | null
  pol?: string | null
  pod?: string | null
  vessel?: string | null
  voyage?: string | null
  cutoff_at?: string | null
  etd?: string | null
  eta?: string | null
  clear_cutoff?: boolean
  clear_etd?: boolean
  clear_eta?: boolean
  capacity_cbm?: number | null
  capacity_weight_kg?: number | null
  notes?: string | null
}

export interface FreightHouseWrite {
  file_id?: string
  customer_id?: string
  hbl_no?: string
  goods_description?: string
  package_count?: number
  measured_weight_kg?: number
  measured_volume_cbm?: number
  gross_weight_kg?: number
  volume_cbm?: number
  freight_amount_cents?: number
  is_dangerous?: boolean
}

export interface FreightConsolidationCostWrite {
  cost_type: string
  supplier_id?: string
  amount_cents: number
  allocatable?: boolean
  notes?: string
}

export interface FreightFileWrite {
  customer_id?: string
  direction?: 'import' | 'export'
  carrier_supplier_id?: string | null
  carrier_company_id?: string | null
  clear_carrier?: boolean
  clear_company?: boolean
  incoterm?: string | null
  freight_payment?: 'prepaid' | 'collect' | null
  clear_freight_payment?: boolean
  pol?: string | null
  pod?: string | null
  place_of_delivery?: string | null
  booking_no?: string | null
  mbl_no?: string | null
  hbl_no?: string | null
  bl_release?: 'original' | 'telex' | 'seaway' | null
  clear_bl_release?: boolean
  vessel?: string | null
  voyage?: string | null
  etd?: string | null
  eta?: string | null
  ata?: string | null
  clear_etd?: boolean
  clear_eta?: boolean
  clear_ata?: boolean
  goods_description?: string | null
  package_count?: number | null
  gross_weight_kg?: number | null
  volume_cbm?: number | null
  freight_amount_cents?: number
  freight_currency?: string
  booking_id?: string | null
  clear_booking?: boolean
  customs_declaration_id?: string | null
  clear_customs?: boolean
  notes?: string | null
}

export interface FreightContainerWrite {
  number: string
  equipment: FreightEquipment
  seal_no?: string
  demurrage_free_days?: number
  detention_free_days?: number
}

export interface FreightContainerPatch {
  equipment?: FreightEquipment
  seal_no?: string
  discharged_at?: string
  clear_discharged?: boolean
  gate_out_at?: string
  clear_gate_out?: boolean
  empty_returned_at?: string
  clear_empty_returned?: boolean
  demurrage_free_days?: number
  detention_free_days?: number
}

export interface FreightDocumentWrite {
  doc_type: FreightDocType
  received: boolean
}

export type FreightListParams = {
  queue?: FreightQueue
  owner?: 'me'
  q?: string
  cursor?: string
  limit?: number
}

// ── Customs / Dédouanement (M05) types ───────────────────────────────────────

export type CustomsStatus =
  | 'draft'
  | 'documents_pending'
  | 'submitted'
  | 'under_examination'
  | 'inspection_requested'
  | 'inspection_done'
  | 'simulation'
  | 'duties_assessed'
  | 'compared'
  | 'duties_paid'
  | 'released'
  | 'rejected'

export type CustomsDirection = 'import' | 'export'
export type CustomsRegime =
  | 'definitive_import'
  | 'temporary_import'
  | 'transit'
  | 'warehouse'
  | 'export'
  | 're_export'

export type CustomsDocType =
  | 'bill_of_lading'
  | 'commercial_invoice'
  | 'packing_list'
  | 'certificate_of_origin'
  | 'import_license'
  | 'phytosanitary_cert'
  | 'veterinary_cert'
  | 'insurance_cert'
  | 'asycuda_form'
  | 'customs_receipt'
  | 'other'

export type CustomsFeeType =
  | 'declarant_fees'
  | 'port_fees'
  | 'scanner_fees'
  | 'storage_fees'
  | 'bank_fees'
  | 'other'

export interface CustomsItem {
  id: string
  declaration_id: string
  hs_code: string
  description?: string | null
  country_of_origin?: string | null
  quantity?: number | null
  unit?: string | null
  total_value: number
  customs_value: number
  duty_amount: number
  tva_amount: number
  rs_amount: number
  pc_amount: number
  warnings: string[]
}

export interface CustomsDocument {
  id: string
  declaration_id: string
  doc_type: CustomsDocType
  file_url?: string | null
  file_name?: string | null
  mime_type?: string | null
  size_bytes?: number | null
  is_required: boolean
  is_verified: boolean
  verified_by?: string | null
  verified_at?: string | null
  created_at: string
}

export interface CustomsFee {
  id: string
  declaration_id: string
  fee_type: CustomsFeeType
  description: string
  amount: number
  currency: string
  paid: boolean
  paid_at?: string | null
  invoiced_by?: string | null
}

export interface CustomsDeclaration {
  id: string
  org_id: string
  ref: string
  status: CustomsStatus
  direction: CustomsDirection
  regime: CustomsRegime
  shipment_id?: string | null
  shipment_ref?: string | null
  booking_id?: string | null
  port_of_entry?: string | null
  country_of_origin?: string | null
  country_of_export?: string | null
  incoterm?: string | null
  total_packages?: number | null
  invoice_value: number
  freight_value: number
  insurance_value: number
  customs_value: number
  currency?: string
  total_duties: number
  total_taxes: number
  total_fees: number
  grand_total_payable: number
  official_duties?: number | null
  official_total?: number | null
  notes?: string | null
  rejection_reason?: string | null
  lodged_at?: string | null
  assessed_at?: string | null
  paid_at?: string | null
  released_at?: string | null
  rejected_at?: string | null
  declarant_name?: string | null
  declarant_ref?: string | null
  // Section A — panier multi-articles (migration 180, AUD-01). Le backend
  // ne les renvoyait pas avant cette correction : GetByID/List oubliaient
  // ces colonnes dans leur SELECT, si bien que liquidation_state — dont
  // dépend le bouton note de détail — revenait toujours vide.
  dossier_number?: string | null
  importer_ninea?: string | null
  importer_name?: string | null
  supplier_name?: string | null
  supplier_country?: string | null
  invoice_number?: string | null
  invoice_date?: string | null
  exchange_rate?: number | null
  general_regime_code?: string | null
  total_adjustments?: number
  liquidation_state?: CustomsLiquidationState
  // Bloc 2 — valeurs globales Chaîne 1 (migration 185)
  allocation_mode?: CustomsAllocationMode
  invoice_currency?: string | null
  bill_of_lading_number?: string | null
  general_observation?: string | null
  fob_total_original?: number | null
  fob_total_declaration?: number
  freight_total_original?: number | null
  freight_total?: number
  insurance_total_original?: number | null
  insurance_total?: number
  deductions_total?: number
  expected_gross_weight?: number | null
  expected_net_weight?: number | null
  expected_volume?: number | null
  expected_packages?: number | null
  planned_positions_count?: number | null
  expected_caf?: number
  items: CustomsItem[]
  documents: CustomsDocument[]
  fees: CustomsFee[]
  created_at: string
  updated_at: string
}

export type CustomsAllocationMode = 'MANUAL' | 'FOB' | 'GROSS_WEIGHT' | 'VOLUME'

export interface CustomsDeclarationSummary {
  id: string
  ref: string
  status: CustomsStatus
  direction: CustomsDirection
  regime?: CustomsRegime
  shipment_id?: string | null
  shipment_ref?: string | null
  customer_id?: string
  invoice_value?: number
  customs_value: number
  grand_total_payable: number
  currency?: string
  item_count?: number
  document_count?: number
  // Section A — panier multi-articles (migration 180)
  dossier_number?: string | null
  port_of_entry?: string | null
  supplier_name?: string | null
  supplier_country?: string | null
  // ImporterName is the customer's legal_name, joined at query time by the
  // backend so the list can show who imports without a second call.
  importer_name?: string | null
  liquidation_state?: CustomsLiquidationState
  // Lot 4 (AUD-04) : les colonnes générées `customs_value` et
  // `grand_total_payable` ne sont plus remplies par le panier ; elles
  // gardent leur valeur legacy pour compatibilité, mais les vrais
  // totaux à afficher dans la liste sont `panier_caf_total` (agrégé
  // depuis customs_items.caf) et `panier_total_duties_taxes` (agrégé
  // depuis les liquidations courantes).
  panier_caf_total?: number
  panier_total_duties_taxes?: number
  released_at?: string | null
  eta_release?: string | null
  created_at?: string
  updated_at: string
}

// Liquidation-state (dossier-level) — customs_declarations.liquidation_state
export type CustomsLiquidationState =
  | 'DRAFT'
  | 'CALCULATING'
  | 'CALCULATED'
  | 'TO_REVIEW'
  | 'VALIDATED'
  | 'FINALIZED'

// Per-article liquidation status — customs_items.liquidation_status
export type CustomsItemLiquidationStatus =
  | 'DRAFT'
  | 'CALCULATED'
  | 'VALIDATED'
  | 'ERROR'
  | 'RECALCULATION_REQUIRED'

// One line of the panier multi-articles (backend ItemDTO).
export interface CustomsDeclarationItem {
  id: string
  line_number: number
  article_number: number
  hs_code: string
  nts_code?: string | null
  description: string
  regime_code?: string | null
  country_of_origin: string
  provenance_country?: string | null
  preference_code?: string | null
  quantity: number
  unit: string
  packages?: number | null
  gross_weight_kg?: number | null
  net_weight_kg?: number | null
  fob_foreign_currency?: number | null
  freight_foreign_currency?: number | null
  insurance_foreign_currency?: number | null
  fob_fcfa?: number | null
  freight?: number | null
  insurance?: number | null
  adjustments?: number | null
  customs_value?: number | null
  caf?: number | null
  total_value: number
  currency: string
  duty_amount: number
  tva_amount: number
  rs_amount: number
  pc_amount: number
  liquidation_status: CustomsItemLiquidationStatus
  // Attributs transport/véhicule (migration 182, AUD-06)
  transport_mode?: string | null
  is_new_vehicle?: boolean
  vehicle_horsepower_cv?: number | null
  vehicle_engine_cc?: number | null
  bic_exempt?: boolean
  created_at: string
  updated_at: string
}

export interface CustomsAddItemInput {
  hs_code: string
  nts_code?: string
  designation: string
  regime_code?: string
  origin_country?: string
  provenance_country?: string
  preference_code?: string
  quantity?: number
  statistical_unit?: string
  packages?: number
  gross_weight_kg?: number
  net_weight_kg?: number
  fob_foreign_currency?: number
  freight_foreign_currency?: number
  insurance_foreign_currency?: number
  fob_fcfa?: number
  freight?: number
  insurance?: number
  adjustments?: number
  // Attributs transport/véhicule (migration 182, AUD-06) — mêmes champs
  // que SimulationRequest, persistés pour qu'un article reste recalculable
  // à l'identique une fois le formulaire quitté.
  transport_mode?: string
  is_new_vehicle?: boolean
  vehicle_horsepower_cv?: number
  vehicle_engine_cc?: number
  bic_exempt?: boolean
}

export type CustomsUpdateItemInput = Partial<CustomsAddItemInput>

// Backend renvoie decimal.Decimal en string dans le JSON.
export type Decimal = string

export interface CustomsTaxResult {
  tax_code: string
  base: Decimal
  rate?: Decimal | null
  unit_rate?: Decimal | null
  theoretical: Decimal
  treatment: string
  payable: Decimal
  suspended: Decimal
  legal_reference: string
  explanation?: {
    base_components?: string[]
    formula?: string
    parameter_status?: unknown
    matrix_version?: string
  }
}

export interface CustomsTaxAssessment {
  id: string
  org_id: string
  declaration_id: string
  line_id: string
  hs_code: string
  origin_country: string
  customs_regime: string
  customs_value_xof: Decimal
  total_theoretical: Decimal
  total_payable: Decimal
  total_suspended: Decimal
  taxes: CustomsTaxResult[]
  ruleset_version: string
  formula_version: string
  matrix_version: string
  is_simulation: boolean
  computed_at: string
}

export interface CustomsValidationResult {
  Code: string
  Severity: 'INFO' | 'WARNING' | 'ERROR'
  Message: string
  ArticleNumber?: number | null
  Field?: string | null
}

export interface CustomsItemCalculationResult {
  item: CustomsDeclarationItem
  assessment?: CustomsTaxAssessment | null
  controls: CustomsValidationResult[] | null
  has_error: boolean
}

export interface CustomsDeclarationTotals {
  fob_total: number
  freight_total: number
  insurance_total: number
  adjustments_total: number
  caf_total: number
  dd_total: number
  vat_total: number
  other_duties_taxes_total: number
  total_duties_taxes: number
  caf_plus_duties_taxes: number
  gross_weight_total: number
  net_weight_total: number
  packages_total: number
}

export interface CustomsDeclarationCalculationResult {
  items: CustomsItemCalculationResult[]
  totals?: CustomsDeclarationTotals | null
  controls: CustomsValidationResult[] | null
  has_error: boolean
}

// Chaîne 1 — contrôles de réconciliation dossier (L4, §3.2 brief)
export type CustomsDeclControlStatus = 'OK' | 'TO_VERIFY' | 'NEUTRAL'
export interface CustomsDeclControlResult {
  code: string
  label: string
  status: CustomsDeclControlStatus
  is_blocking: boolean
  expected: number
  calculated: number
  difference: number
}

export interface HSCode {
  code: string
  description_fr: string
  description_en: string
  cedeao_category: number
  import_duty_rate: number
  redevance_statistique: number
  prelevement_communautaire: number
  tva_rate: number
  requires_phytosanitary: boolean
  requires_veterinary: boolean
  requires_import_license: boolean
  is_prohibited: boolean
  /** "local" = from internal hs_codes table (CEDEAO rates available).
   *  "external" = UK Trade Tariff proxy (description only, duty rates unknown). */
  source?: 'local' | 'external'
}

export interface DutyCalculation {
  customs_value: number
  total_duties: number
  total_tva: number
  total_rs: number
  total_pc: number
  grand_total: number
  warnings: string[]
  items: Array<{
    id: string
    hs_code: string
    customs_value: number
    duty_amount: number
    tva_amount: number
    rs_amount: number
    pc_amount: number
    warnings: string[]
  }>
}

export type CustomsQueue =
  | 'new'
  | 'preparing'
  | 'to_review'
  | 'liquidated'
  | 'submitted'
  | 'examination'
  | 'to_pay'
  | 'to_release'
  | 'closed'
  | 'rejected'

export interface CustomsQueueCounts {
  new: number
  preparing: number
  to_review: number
  liquidated: number
  submitted: number
  examination: number
  to_pay: number
  to_release: number
  closed: number
  rejected: number
}

export interface CustomsStatusCount {
  status: string
  count: number
}

export interface CustomsCustomerActivity {
  customer_id: string
  customer_name: string
  dossiers: number
  liquidated_xof: number
}

export interface CustomsReport {
  by_status: CustomsStatusCount[]
  liquidated_xof: number
  receipted_xof: number
  by_customer: CustomsCustomerActivity[]
  avg_release_days?: number | null
  released_count: number
}

export type CustomsListParams = ListParams & {
  status?: CustomsStatus
  direction?: CustomsDirection
  shipment_id?: string
  queue?: CustomsQueue
  q?: string
}

export interface CustomsTransitionInput {
  to_status: CustomsStatus
  official_duties?: number
  notes?: string
}

export interface CreateCustomsInput {
  customer_id: string
  direction: CustomsDirection
  regime?: CustomsRegime | 'definitive_import'
  shipment_id?: string
  booking_id?: string
  port_of_entry?: string
  country_of_origin?: string
  country_of_export?: string
  incoterm?: string
  invoice_value?: number
  freight_value?: number
  insurance_value?: number
  currency?: string
  declarant_name?: string
  declarant_ref?: string
  // Section A — panier multi-articles (migration 180, AUD-02). Tous
  // optionnels à la création ; importer_ninea n'y figure pas : il est
  // copié côté serveur depuis le client sélectionné.
  dossier_number?: string
  supplier_name?: string
  supplier_country?: string
  invoice_number?: string
  invoice_date?: string // "2026-01-31"
  exchange_rate?: number
  general_regime_code?: string
}

// UpdateCustomsInput mirrors backend updateDeclarationRequest exactly
// (PATCH /v1/customs/:id → Handler.Update). Previously only carried
// invoice/freight/insurance/notes — `notes` isn't even a real column, and
// nothing let the "Détail du dossier" page write fournisseur, facture ou
// taux de change (AUD-02/03).
export interface UpdateCustomsInput {
  port_of_entry?: string
  country_of_origin?: string
  country_of_export?: string
  incoterm?: string
  total_packages?: number
  total_weight_kg?: number
  total_volume_cbm?: number
  invoice_value?: number
  freight_value?: number
  insurance_value?: number
  declarant_name?: string
  declarant_ref?: string
  // Section A — panier multi-articles (migration 180, AUD-02/03).
  dossier_number?: string
  importer_ninea?: string
  supplier_name?: string
  supplier_country?: string
  invoice_number?: string
  invoice_date?: string // "2026-01-31"
  exchange_rate?: number
  general_regime_code?: string
}

export interface CustomsItemInput {
  hs_code: string
  description?: string
  country_of_origin: string
  quantity?: number
  unit?: string
  total_value: number
}

export interface AddDocumentInput {
  doc_type: CustomsDocType
  file_url: string
  file_name: string
  is_required?: boolean
}

export interface AddFeeInput {
  fee_type: CustomsFeeType
  description: string
  amount: number
  currency?: string
  paid?: boolean
}

export interface UpdateFeeInput {
  amount?: number
  paid?: boolean
  notes?: string
}

// ── Fiscal liquidation (Phase B3) ─────────────────────────────────────────────

export interface FiscalLiquidateInput {
  hs_code: string
  customs_regime: string
  transport_mode: string
  includes_bic?: boolean
  is_new_vehicle?: boolean
  fob_value_xof: number
  freight_value_xof: number
  insurance_value_xof: number
  vehicle_age_months?: number
  estimated_total?: number
}

export interface DutiesBreakdown {
  hs_code: string
  caf_value: number
  taxable_value: number
  dd_amount: number
  rs_amount: number
  pcs_uemoa_amount: number
  pcc_cedeao_amount: number
  tva_amount: number
  cosec_amount: number
  promad_amount: number
  bic_amount: number
  accise_amount: number
  te_amount: number
  interest_amount: number
  total_due: number
  effective_rate: number
  /** Taux effectifs : dd_pct, rs_pct, tva_pct, cosec_pct, pcs_uemoa_pct, … */
  applied_rates: Record<string, number>
  notes: string[]
}

export interface FinalDutiesResult {
  liquidation: DutiesBreakdown
  estimated_total: number
  variance_pct: number
  variance_alert: boolean
  variance_reason: string
}

export interface VarianceReport {
  declaration_id: string
  dd_amount: number
  rs_amount: number
  tva_amount: number
  cosec_amount: number
  total_due: number
  official_duties: number
  estimated_total: number
  variance_pct: number
  variance_alert: boolean
  variance_reason: string
}

export type SuspenseAccountStatus =
  | 'OPEN'
  | 'PARTIALLY_CLEARED'
  | 'CLEARED'
  | 'OVERDUE'
  | 'FORFEITED'

export type SuspenseUrgency = 'OK' | 'WARNING' | 'CRITICAL' | 'OVERDUE'

export interface SuspenseAccount {
  id: string
  authorization_id?: string | null
  declaration_id?: string | null
  account_ref: string
  regime_code: string
  hs_code: string
  goods_description: string
  initial_quantity: number
  current_quantity: number
  quantity_unit: string
  initial_value_xof: number
  current_value_xof: number
  theoretical_duties_xof: number
  suspended_duties_xof: number
  paid_duties_xof: number
  entry_date: string
  initial_deadline: string
  current_deadline: string
  status: SuspenseAccountStatus
  created_at: string
  updated_at: string
}

export interface SuspenseAccountListItem extends SuspenseAccount {
  days_remaining: number
  urgency_level: SuspenseUrgency
  estimated_penalty_xof: number
  movements_count: number
  total_cleared_value_xof: number
  total_penalties_paid_xof: number
}

export interface SuspenseMovement {
  id: string
  account_id: string
  movement_type: string
  movement_date: string
  quantity: number
  quantity_unit: string
  value_xof: number
  balance_after_qty: number
  reference_doc: string
  notes?: string
  created_at: string
}

export interface SuspenseMotiveOption {
  motive: string
  duties_exigible: boolean
  requires_decision: boolean
}

export interface SuspenseAccountDetail {
  account: SuspenseAccount
  movements: SuspenseMovement[]
  valid_motives: SuspenseMotiveOption[]
}

export interface SuspenseAccountStats {
  open_count: number
  overdue_count: number
  partially_cleared_count: number
  total_value_xof: number
  total_suspended_duties_xof: number
  total_exposure_xof: number
}

export type SuspenseAccountListParams = {
  status?: SuspenseAccountStatus
  regime?: string
  urgency?: SuspenseUrgency
  cursor?: string
  limit?: number
}

export interface CreateSuspenseAccountInput {
  regime_code: string
  hs_code: string
  goods_description: string
  quantity: number
  quantity_unit?: string
  value_xof: number
  entry_date?: string
  initial_deadline: string
  declaration_id?: string
}

export interface ClearanceInput {
  movement_type: 'EXIT' | 'TRANSFORMATION_OUT' | 'WASTE'
  motive?: string
  movement_date?: string
  quantity: number
  value_xof: number
  reference_doc?: string
  notes?: string
  bic_exempt?: boolean
}

export interface ClearancePreview {
  quantity: number
  value_xof: number
  is_overdue: boolean
  days_overdue: number
  fiscal_regime: string
  interest_amount: number
  total_due: number
}

export interface Guarantee {
  id: string
  account_id?: string | null
  guarantee_ref: string
  guarantee_type: string
  provider_name: string
  provider_ref: string
  total_amount_xof: number
  reserved_amount_xof: number
  released_amount_xof: number
  called_amount_xof: number
  available_amount_xof: number
  status: string
  effective_from: string
  effective_to?: string | null
}

export interface CreateGuaranteeInput {
  account_id: string
  guarantee_type: 'CAUTION_BANCAIRE' | 'CONSIGNATION' | 'CAUTION_SOLIDAIRE' | 'DISPENSE'
  provider_name: string
  provider_ref?: string
  total_amount_xof: number
  effective_from?: string
}

export interface GuaranteeMovementInput {
  amount_xof: number
  reference_doc?: string
  notes?: string
}

export interface MainleveeInput extends GuaranteeMovementInput {
  approver: string
}

// ── Suspensive regimes (Phase E2) ─────────────────────────────────────────────

export type SuspensiveRegime = 'ATE' | 'ATS' | 'ENTREPOT'
export type SuspensiveStatus =
  | 'ACTIVE'
  | 'EXPIRING_SOON'
  | 'OVERDUE'
  | 'APURED'
  | 'CANCELLED'
  | 'CONFISCATED'
export type SuspensiveUrgency = 'OK' | 'WARNING' | 'CRITICAL' | 'OVERDUE'

export interface SuspensiveFile {
  id: string
  org_id: string
  ref: string
  booking_id?: string | null
  regime: SuspensiveRegime
  transport_mode: string
  hs_code?: string | null
  goods_description: string
  quantity?: number | null
  quantity_unit?: string | null
  entry_date: string
  current_deadline: string
  fob_value_xof: number
  freight_value_xof: number
  insurance_value_xof: number
  caf_value_xof: number
  cosec_amount_xof: number
  status: SuspensiveStatus
  alert_j30_sent_at?: string | null
  alert_j7_sent_at?: string | null
  alert_j0_sent_at?: string | null
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface SuspensiveDashboardItem extends SuspensiveFile {
  days_remaining: number
  urgency_level: SuspensiveUrgency
  estimated_penalty_xof: number
  operations_count: number
  total_apured_value_xof: number
  total_penalties_paid_xof: number
}

export interface SuspensiveOperation {
  id: string
  suspensive_file_id: string
  org_id: string
  operation_type: string
  operation_date: string
  quantity_concerned?: number | null
  value_concerned_xof?: number | null
  dd_amount: number
  rs_amount: number
  pcs_uemoa_amount: number
  pcc_cedeao_amount: number
  cosec_amount: number
  tva_amount: number
  interest_amount: number
  total_due: number
  is_overdue: boolean
  days_overdue: number
  interest_rate_pct: number
  payment_status: string
  document_ref?: string | null
  notes?: string | null
  created_at: string
}

export interface SuspensiveStats {
  active_count: number
  expiring_soon_count: number
  overdue_count: number
  total_value_xof: number
  total_pending_duty_xof: number
  total_accrued_penalty: number
}

export interface CreateSuspensiveInput {
  booking_id?: string | null
  regime: SuspensiveRegime
  transport_mode: string
  hs_code?: string | null
  goods_description: string
  quantity?: number | null
  quantity_unit?: string | null
  fob_value_xof: number
  freight_value_xof: number
  insurance_value_xof: number
  entry_date?: string | null
  notes?: string | null
}

export interface ApurementInput {
  suspensive_file_id: string
  operation_type: 'FULL_APUREMENT' | 'PARTIAL_APUREMENT' | 'REEXPORT'
  operation_date: string
  quantity_concerned?: number | null
  value_concerned_xof?: number | null
  document_ref?: string | null
  notes?: string | null
  includes_bic?: boolean
}

export interface ApurementPreview {
  is_overdue: boolean
  days_overdue: number
  fiscal_regime: string
  interest_base: number
  interest_rate: number
  interest_amount: number
  cosec_already_paid: number
  total_due: number
  savings_vs_immediate: number
  liquidation: DutiesBreakdown
}

export type SuspensiveListParams = {
  status?: SuspensiveStatus
  regime?: SuspensiveRegime
  urgency?: SuspensiveUrgency
  cursor?: string
  limit?: number
}

// ── Warehouse & Stock (M06) types ─────────────────────────────────────────────

export type StockStatus =
  | 'expected'
  | 'in_stock'
  | 'reserved'
  | 'quarantine'
  | 'out'
  | 'damaged'
  | 'lost'
export type MovementType = 'in' | 'out' | 'transfer' | 'adjustment' | 'damage' | 'loss'
export type WarehouseTaskType = 'receive' | 'putaway' | 'pick' | 'handover' | 'control' | 'dispatch'
export type WarehouseTaskStatus = 'pending' | 'in_progress' | 'done' | 'blocked' | 'cancelled'

export interface Warehouse {
  id: string
  org_id: string
  name: string
  code: string
  address?: string | null
  city?: string | null
  country: string
  total_capacity_cbm?: number | null
  total_capacity_kg?: number | null
  status?: 'active' | 'full' | 'closed' | 'maintenance'
  is_active: boolean
  is_default?: boolean
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface WarehouseStats {
  warehouse_id: string
  total_items: number
  total_weight: number
  total_volume: number
  occupancy_percent: number
  used_cbm: number
  used_kg: number
  capacity_cbm: number
  capacity_kg: number
  occupancy_cbm_percent: number
  occupancy_kg_percent: number
  unknown_volume: number
  locations: LocationOccupancy[]
  alerts: OccupancyAlert[]
  items_in_stock: number
  items_reserved: number
  items_quarantine: number
  oldest_item_days: number
  stock_value: number
}

export interface LocationOccupancy {
  location_id: string
  code: string
  location_type: string
  usage: string
  used_cbm: number
  used_kg: number
  used_packages: number
  percent: number
  unknown_volume: number
  open: boolean
}

export interface OccupancyAlert {
  code: 'LOCATION_HIGH' | 'LOCATION_OVER' | 'DOCK_DWELL'
  location_id?: string
  location_code?: string
  stock_item_id?: string
  stock_ref?: string
}

export interface WarehouseLocation {
  id: string
  org_id: string
  warehouse_id: string
  code: string
  name?: string | null
  location_type: 'zone' | 'dock' | 'floor' | 'shelf' | 'rack'
  zone?: string | null
  aisle?: string | null
  level?: number | null
  capacity_cbm?: number | null
  capacity_kg?: number | null
  capacity_packages?: number | null
  usage?: 'dry' | 'cold' | 'dangerous' | 'quarantine'
  is_active: boolean
  is_occupied: boolean
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface StockItem {
  id: string
  org_id: string
  ref: string
  warehouse_id: string
  location_id?: string | null
  customer_id?: string | null
  shipment_id?: string | null
  declaration_id?: string | null
  booking_id?: string | null
  description: string
  hs_code?: string | null
  origin_country?: string | null
  quantity_initial: number
  quantity_current: number
  unit: string
  weight_kg?: number | null
  volume_cbm?: number | null
  packages_count?: number | null
  unit_value: number
  currency: string
  status: StockStatus
  received_at: string | null
  expected_release_date?: string | null
  released_at?: string | null
  last_moved_at?: string | null
  requires_cold: boolean
  requires_ventilation: boolean
  is_dangerous: boolean
  danger_class?: string | null
  notes?: string | null
  created_at: string
  updated_at: string
  movements?: StockMovement[]
}

export interface StockMovement {
  id: string
  org_id: string
  stock_item_id: string
  movement_type: MovementType
  from_location_id?: string | null
  to_location_id?: string | null
  quantity_before: number
  quantity_change: number
  quantity_after: number
  unit: string
  reason?: string | null
  reference?: string | null
  inventory_session_id?: string | null
  actor_user_id?: string | null
  actor_type: 'agent' | 'system' | 'driver'
  created_at: string
}

export interface InventorySession {
  id: string
  org_id: string
  warehouse_id: string
  ref: string
  status: 'open' | 'counting' | 'validating' | 'closed'
  started_at: string
  closed_at?: string | null
  started_by?: string | null
  closed_by?: string | null
  notes?: string | null
  items_counted: number
  discrepancies_found: number
  adjustments_applied: number
}

export interface InventoryCount {
  id: string
  session_id: string
  org_id: string
  stock_item_id: string
  expected_quantity: number
  counted_quantity?: number | null
  discrepancy?: number | null
  status: 'pending' | 'counted' | 'validated' | 'adjusted'
  counted_by?: string | null
  counted_at?: string | null
  notes?: string | null
}

export interface CreateWarehouseBody {
  name: string
  code: string
  address?: string
  city?: string
  country?: string
  total_capacity_cbm?: number
  total_capacity_kg?: number
  status?: 'active' | 'full' | 'closed' | 'maintenance'
  notes?: string
}

export interface CreateLocationBody {
  code?: string
  name?: string
  location_type?: WarehouseLocation['location_type']
  zone?: string
  aisle?: string
  level?: number
  capacity_cbm?: number
  capacity_kg?: number
  capacity_packages?: number
  usage?: WarehouseLocation['usage']
  notes?: string
}

export interface WarehouseTask {
  id: string
  org_id: string
  warehouse_id: string
  stock_item_id: string
  task_type: WarehouseTaskType
  status: WarehouseTaskStatus
  assigned_user_id?: string | null
  blocked_reason?: string | null
  quantity?: number | null
  location_id?: string | null
  dest_warehouse_id?: string | null
  notes?: string | null
  started_at?: string | null
  completed_at?: string | null
  completed_by?: string | null
  created_at: string
  updated_at: string
  stock_ref?: string
  stock_description?: string
  stock_status?: StockStatus
  quantity_current?: number
  unit?: string
}

export interface WarehouseAssignment {
  id: string
  org_id: string
  warehouse_id: string
  user_id: string
  first_name?: string
  last_name?: string
  created_at: string
}

export interface WarehouseBoard {
  warehouse_id: string
  expected: number
  to_putaway: number
  to_pick: number
  to_handover: number
  blocked: number
  dwell_over_30: number
  unbillable: number
}

export interface WarehouseIntakeBody {
  warehouse_id: string
  customer_id: string
  description: string
  quantity: number
  unit?: string
  weight_kg?: number
  volume_cbm?: number
  packages_count?: number
  custody_enabled?: boolean
}

export interface StorageCharge {
  id: string
  customer_id: string
  kind: 'storage' | 'custody'
  period_start: string
  period_end: string
  days: number
  amount_ht: number
  description: string
  invoice_id?: string | null
  stock_ref?: string
}

export interface StorageTariff {
  id?: string
  warehouse_id: string
  customer_id?: string | null
  kind: 'storage' | 'custody'
  basis: 'package' | 'ton' | 'cbm' | 'lot'
  cadence: 'daily' | 'monthly' | 'included'
  free_days: number
  rate: number
  cold_multiplier: number
  danger_multiplier: number
}

export type StorageTariffBody = Omit<StorageTariff, 'id'>

export interface WarehouseLease {
  id: string
  warehouse_id: string
  lessor_supplier_id: string
  contract_ref: string
  area_sqm?: number | null
  monthly_rent: number
  deposit: number
  currency: string
  start_date: string
  end_date: string
  renewal_notice_days: number
  status: string
}

export interface WarehouseLeaseBody {
  lessor_supplier_id: string
  contract_ref: string
  area_sqm?: number
  monthly_rent: number
  deposit?: number
  start_date: string
  end_date: string
  renewal_notice_days?: number
}

export interface WarehouseSecurityContract {
  id: string
  provider_supplier_id: string
  contract_ref: string
  cost_model: 'monthly' | 'hourly'
  rate: number
  currency: string
  start_date: string
  end_date: string
  status: string
}

export interface WarehouseSecurityBody {
  provider_supplier_id: string
  contract_ref: string
  cost_model: 'monthly' | 'hourly'
  rate: number
  start_date: string
  end_date: string
}

export type WarehouseTaskFilters = {
  type?: WarehouseTaskType
  status?: WarehouseTaskStatus
  warehouse_id?: string
}

export interface CreateWarehouseTaskBody {
  stock_item_id: string
  task_type: 'pick'
  quantity?: number
  notes?: string
}

export interface CompleteWarehouseTaskBody {
  quantity?: number
  location_id?: string
  notes?: string
  reason?: string
  decision?: 'in_stock' | 'damaged' | 'lost' | 'return_receive'
  new_quantity?: number
}

export type StockListParams = {
  warehouse_id?: string
  customer_id?: string
  shipment_id?: string
  status?: StockStatus
  q?: string
  cursor?: string
  limit?: number
}

export interface MoveInBody {
  warehouse_id: string
  location_id?: string
  customer_id?: string
  shipment_id?: string
  declaration_id?: string
  booking_id?: string
  description: string
  hs_code?: string
  quantity: number
  unit: string
  weight_kg?: number
  volume_cbm?: number
  packages_count?: number
  unit_value?: number
  currency?: string
  notes?: string
}

export interface MoveOutBody {
  quantity: number
  reason?: string
  reference?: string
}

export interface TransferBody {
  to_location_id: string
  reason?: string
}

export interface AdjustBody {
  new_quantity: number
  reason: string
}

export interface RecordCountBody {
  stock_item_id: string
  quantity: number
  notes?: string
}

// ── Accounting / SYSCOHADA (M09) ──────────────────────────────────────────

export type AccountClass = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
export type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense' | 'other'
export type NormalBalance = 'debit' | 'credit'
export type JournalCode = 'VT' | 'AC' | 'BQ' | 'CA' | 'OD' | 'AN' | 'PA'
export type EntryStatus = 'draft' | 'validated' | 'cancelled'
export type SourceType =
  | 'invoice'
  | 'payment'
  | 'credit_note'
  | 'payroll'
  | 'fleet_cost'
  | 'manual'
  | 'closing'
export type PeriodStatus = 'open' | 'closing' | 'closed'
export type EntityType = 'customer' | 'supplier' | 'employee'

export interface Account {
  id: string
  account_code: string
  account_name: string
  account_class: number
  account_type: AccountType
  parent_code?: string | null
  is_detail: boolean
  normal_balance: NormalBalance
  balance: number
  status: 'active' | 'inactive'
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface CreateAccountBody {
  account_code: string
  account_name: string
  account_class: number
  account_type: AccountType
  parent_code?: string | null
  normal_balance: NormalBalance
  notes?: string | null
}

export interface UpdateAccountBody {
  account_name?: string
  status?: 'active' | 'inactive'
  notes?: string | null
}

export interface TenantAccountingConfig {
  org_id: string
  payroll_bank_account_code: string
  payroll_cash_account_code: string
  payroll_check_account_code: string
  payroll_mobile_money_account_code: string
  payroll_gross_account_code: string
  payroll_employer_charges_account_code: string
  payroll_net_due_account_code: string
  payroll_ipres_account_code: string
  payroll_css_account_code: string
  payroll_am_account_code: string
  payroll_cfce_account_code: string
  payroll_irpp_account_code: string
  payroll_advances_account_code: string
  created_at: string
  updated_at: string
}

export interface UpdateTenantAccountingConfigBody {
  payroll_bank_account_code?: string
  payroll_cash_account_code?: string
  payroll_check_account_code?: string
  payroll_mobile_money_account_code?: string
  payroll_gross_account_code?: string
  payroll_employer_charges_account_code?: string
  payroll_net_due_account_code?: string
  payroll_ipres_account_code?: string
  payroll_css_account_code?: string
  payroll_am_account_code?: string
  payroll_cfce_account_code?: string
  payroll_irpp_account_code?: string
  payroll_advances_account_code?: string
}

export interface JournalLine {
  id: string
  entry_id: string
  account_code: string
  account_name: string
  debit: number
  credit: number
  entity_type?: EntityType | null
  entity_id?: string | null
  entity_name?: string | null
  description?: string | null
  position: number
  created_at: string
}

export interface JournalEntry {
  id: string
  ref: string
  journal_code: JournalCode
  period_code: string
  entry_date: string
  description: string
  source_type?: SourceType | null
  source_id?: string | null
  status: EntryStatus
  total_debit: number
  total_credit: number
  validated_at?: string | null
  cancelled_at?: string | null
  cancel_reason?: string | null
  reversed_by?: string | null
  reverses?: string | null
  lines?: JournalLine[]
  created_at: string
  updated_at: string
}

export interface CreateEntryLineBody {
  account_code: string
  debit?: number
  credit?: number
  entity_type?: EntityType | null
  entity_id?: string | null
  entity_name?: string | null
  description?: string | null
}

export interface CreateEntryBody {
  journal_code: JournalCode
  entry_date: string
  description: string
  lines: CreateEntryLineBody[]
}

export interface UpdateEntryBody {
  journal_code?: JournalCode | null
  entry_date?: string | null
  description?: string | null
  lines: CreateEntryLineBody[]
}

export interface AccountingPeriod {
  id: string
  fiscal_year_id: string
  period_code: string
  start_date: string
  end_date: string
  status: PeriodStatus
  closed_at?: string | null
}

export interface TrialBalanceLine {
  account_code: string
  account_name: string
  account_class: number
  account_type: AccountType
  normal_balance: NormalBalance
  total_debit: number
  total_credit: number
  balance: number
}

export interface TrialBalance {
  accounts: TrialBalanceLine[]
  as_of: string
  total_debit: number
  total_credit: number
}

export interface AccountBalance {
  account_code: string
  account_name: string
  balance: number
}

export interface IncomeStatement {
  from: string
  to: string
  revenue: AccountBalance[]
  total_revenue: number
  expenses: AccountBalance[]
  total_expenses: number
  net_income: number
}

export interface BalanceSheet {
  as_of: string
  assets: AccountBalance[]
  total_assets: number
  liabilities: AccountBalance[]
  total_liabilities: number
  equity: AccountBalance[]
  total_equity: number
  net_income: number
}

export type AccountFilters = {
  class?: number
  type?: AccountType
  status?: 'active' | 'inactive'
  q?: string
}

export type EntryFilters = ListParams & {
  journal_code?: JournalCode
  period?: string
  status?: EntryStatus
  account_code?: string
  from?: string
  to?: string
  q?: string
}

export type { paths, Schemas }
export const apiBaseURL = BASE_URL

// ── HR / GRH (M11) — re-exports of generated schemas ──────────────────────
export type HRDepartment = Schemas['HRDepartment']
export type HRPosition = Schemas['HRPosition']
export type HREmployee = Schemas['HREmployee']
export type HREmploymentContract = Schemas['HREmploymentContract']
export type HRLeaveType = Schemas['HRLeaveType']
export type HRLeaveBalance = Schemas['HRLeaveBalance']
export type HRLeaveRequest = Schemas['HRLeaveRequest']
export type HRAttendanceRecord = Schemas['HRAttendanceRecord']
export type HRHoliday = Schemas['HRHoliday']
export type HRStats = Schemas['HRStats']
export type HRUpcomingEvent = Schemas['UpcomingEvent']
export type HRUpcomingEvents = Schemas['UpcomingEvents']
export type HRMyDashboard = Schemas['HRMyDashboard']

// ── Avances & prêts ────────────────────────────────────────────────────
export type SalaryAdvance = Schemas['SalaryAdvance']
export type AdvanceInstallment = Schemas['AdvanceInstallment']
export type AdvanceDetail = Schemas['AdvanceDetail']
export type AdvancesSummary = Schemas['AdvancesSummary']
export type AdvanceKind = SalaryAdvance['kind']
export type AdvanceStatus = SalaryAdvance['status']
export type CreateAdvanceRequestBody = Schemas['CreateAdvanceRequest']
export type HREmployeeDocument = Schemas['HREmployeeDocument']

// Document settings (templates + signature) are not part of the generated
// OpenAPI schema yet — hand-written to match the hr.DocumentSettingsDTO Go type.
export type HRDocumentTemplateOverride = {
  intro?: string
  body?: string
  closing?: string
}
export type HRDocumentSettings = {
  signature_image_url?: string | null
  signatory_name?: string | null
  signatory_title: string
  closing_text?: string | null
  templates: Record<string, HRDocumentTemplateOverride>
}
export type HRUpdateDocumentSettingsBody = {
  signatory_name?: string | null
  signatory_title?: string | null
  closing_text?: string | null
  templates?: Record<string, HRDocumentTemplateOverride>
}
export type HRDocumentType = NonNullable<HREmployeeDocument['doc_type']>
export type HRPositionGrade = Schemas['HRPositionGrade']
export type PatchPayrollParametersBody = Schemas['PatchPayrollParametersRequest']
export type HRContractAmendment = Schemas['HRContractAmendment']
export type HRAnalytics = Schemas['HRAnalytics']
export type HRDocumentRequest = Schemas['HRDocumentRequest']
export type UpdateMyEmployeeProfileBody = {
  email?: string | null
  phone?: string | null
  phone_secondary?: string | null
  address?: string | null
  city?: string | null
  marital_status?: 'single' | 'married' | 'divorced' | 'widowed' | null
  dependents_count?: number | null
  emergency_contact_name?: string | null
  emergency_contact_relation?: string | null
  emergency_contact_phone?: string | null
}
export type HRCreatePositionGradeBody = Schemas['CreatePositionGradeRequest']
export type HRUpdatePositionGradeBody = Schemas['UpdatePositionGradeRequest']

export type HRCreateDepartmentBody = Schemas['CreateDepartmentRequest']
export type HRUpdateDepartmentBody = Schemas['UpdateDepartmentRequest']
export type HRCreatePositionBody = Schemas['CreatePositionRequest']
export type HRCreateEmployeeBody = Schemas['CreateEmployeeRequest']
export type HRUpdateEmployeeBody = Schemas['UpdateEmployeeRequest']
export type HRCreateContractBody = Schemas['CreateContractRequest']
export type HRUpdateContractBody = Schemas['UpdateContractRequest']
export type HRUpdatePositionBody = Schemas['UpdatePositionRequest']
export type HRAmendContractBody = Schemas['AmendContractRequest']
export type HRCreateLeaveTypeBody = Schemas['CreateLeaveTypeRequest']
export type HRUpdateLeaveTypeBody = Schemas['UpdateLeaveTypeRequest']
export type HRLeaveRequestBody = Schemas['LeaveRequestBody']
export type HRAdjustAttendanceBody = Schemas['AdjustAttendanceRequest']

export type HREmployeeStatus = NonNullable<HREmployee['status']>
export type HRContractType = NonNullable<HREmploymentContract['contract_type']>
export type HRContractStatus = NonNullable<HREmploymentContract['status']>
export type HRLeaveStatus = NonNullable<HRLeaveRequest['status']>
export type HRAttendanceStatus = NonNullable<HRAttendanceRecord['status']>

export type HRTerminateBody = {
  reason: string
  effective_date?: string | null
}

export type HRCheckInBody = {
  employee_id: string
  method?: HRAttendanceRecord['check_in_method']
  location?: string | null
}

export type HREmployeeFilters = ListParams & {
  status?: HREmployeeStatus
  department_id?: string
  position_id?: string
  manager_id?: string
  hired_from?: string
  hired_to?: string
  q?: string
}

export type HRContractFilters = {
  employee_id?: string
  status?: HRContractStatus
  type?: HRContractType
  ending_before?: string
}

export type HRLeaveFilters = ListParams & {
  employee_id?: string
  leave_type_id?: string
  department_id?: string
  status?: HRLeaveStatus
  from?: string
  to?: string
  q?: string
}

export type HRLeaveKPIs = Schemas['LeaveKPIs']

// ── Payroll / Paie (M12) types ──────────────────────────────────────────────

export type PayrollParameters = Schemas['PayrollParameters']
export type PayrollPeriod = Schemas['PayrollPeriod']
export type Payslip = Schemas['Payslip']
export type PayslipItem = Schemas['PayslipItem']
export type PayrollStats = Schemas['PayrollStats']

export type PayrollPeriodStatus = NonNullable<PayrollPeriod['status']>
export type PayslipStatus = NonNullable<Payslip['status']>
export type PayslipItemCategory = NonNullable<PayslipItem['category']>
export type PayrollPaymentMethod = NonNullable<Payslip['payment_method']>

export type PayPeriodBody = {
  pay_date: string
  payment_method: PayrollPaymentMethod
  payment_reference?: string | null
}

export type PayrollPeriodFilters = ListParams & {
  year?: number
  status?: PayrollPeriodStatus
}

export type PayslipEarningInput = {
  code: string
  label: string
  amount: number
  is_taxable?: boolean
}

export type PayslipDeductionInput = {
  code: string
  label: string
  amount: number
}

export type ComputePayslipBody = {
  employee_id: string
  work_accident_rate?: number | null
  worked_days?: number | null
  overtime_hours?: number | null
  additional_earnings?: PayslipEarningInput[]
  additional_deductions?: PayslipDeductionInput[]
}

// UpdatePayslipBody corrects an already-computed payslip — re-runs the
// calculator with these overrides and overwrites the existing bulletin
// (no new payslip number). Only valid while the payslip is still 'computed'.
export type UpdatePayslipBody = {
  worked_days?: number | null
  overtime_hours?: number | null
  additional_earnings?: PayslipEarningInput[]
  additional_deductions?: PayslipDeductionInput[]
}

export type PeriodReport = {
  period_id: string
  employee_count: number
  total_gross: number
  total_net: number
  total_ipres_employee: number
  total_ipres_employer: number
  total_css_family: number
  total_css_accident: number
  total_am_employee: number
  total_am_employer: number
  total_cfce: number
  total_irpp: number
  total_trimf: number
  total_advance_deduction: number
  total_employer_cost: number
}

export type PayrollAdvance = {
  id: string
  org_id: string
  employee_id: string
  period_id?: string
  amount: number
  date: string
  notes?: string
  status: 'pending' | 'deducted' | 'cancelled'
  created_at: string
}

// ── Learn Hub types ───────────────────────────────────────────────────────────

export type LearnDifficulty = 'beginner' | 'intermediate' | 'advanced'

export type LearnCollection = {
  id: string
  org_id: string | null
  slug: string
  title: string
  description: string | null
  icon: string | null
  sort_order: number
  is_published: boolean
  article_count?: number
  created_at: string
  updated_at: string
}

export type LearnArticleSummary = {
  id: string
  collection_slug: string
  collection_title: string
  slug: string
  title: string
  excerpt?: string | null
  difficulty: LearnDifficulty
  reading_time_min?: number | null
  walkthrough_id?: string | null
  walkthrough_slug?: string | null
  is_published: boolean
  views_count: number
  helpful_count: number
  sort_order: number
  published_at?: string | null
}

export type LearnAnnotation = {
  id: string
  type: string
  x: number
  y: number
  width?: number | null
  height?: number | null
  label?: string | null
  number?: number | null
  color?: string | null
}

export type LearnCapture = {
  id: string
  article_id: string
  org_id?: string | null
  file_url: string
  alt_text: string
  caption?: string | null
}

export type LearnVideoChapter = {
  title: string
  start_seconds: number
  end_seconds: number
}

export type LearnVideo = {
  id: string
  article_id?: string | null
  title: string
  description?: string | null
  embed_type: string
  embed_id: string
  embed_url: string
  thumbnail_url?: string | null
  duration_seconds?: number | null
  transcript?: string | null
  chapters?: LearnVideoChapter[]
  is_published: boolean
  views_count: number
}

export type LearnWalkthroughStep = {
  selector: string
  title: string
  content: string
  side?: string | null
  align?: string | null
}

export type LearnWalkthrough = {
  id: string
  title: string
  steps: LearnWalkthroughStep[]
  created_at: string
  updated_at: string
}

export type LearnArticle = LearnArticleSummary & {
  content: string
  content_format: string
  related_route?: string | null
  related_module?: string | null
  related_permission?: string | null
  target_roles?: string[]
  unhelpful_count: number
  version: number
  glossary_terms?: LearnGlossaryTerm[]
  primary_video?: LearnVideo | null
  captures?: LearnCapture[]
  created_at: string
  updated_at: string
}

export type LearnSearchResult = {
  article_id: string
  slug: string
  title: string
  excerpt: string | null
  collection_slug: string | null
  collection_title: string | null
  rank: number
}

export type LearnUserProgress = {
  id: string
  user_id: string
  article_id: string
  completed: boolean
  feedback: boolean | null
  last_viewed_at: string
}

export type LearnGlossaryTerm = {
  id: string
  org_id: string | null
  term: string
  definition: string
  module_tag: string | null
  see_also: string[]
  created_at: string
}

export type LearnArticleInput = {
  collection_id: string
  slug: string
  title: string
  excerpt?: string | null
  content: string
  content_format?: string
  difficulty?: LearnDifficulty
  reading_time_min?: number | null
  related_route?: string | null
  related_module?: string | null
  related_permission?: string | null
  target_roles?: string[]
  sort_order?: number
}

export type LearnGlossaryInput = {
  term: string
  definition: string
  module_tag?: string | null
  see_also?: string[]
}

export type LearnWalkthroughInput = {
  title: string
  steps: LearnWalkthroughStep[]
  routes?: string[]
}

export type LearnAnalytics = {
  total_articles: number
  total_views: number
  total_walkthroughs_started: number
  total_walkthroughs_completed: number
  top_articles: { id: string; title: string; views: number }[]
  top_searches: { query: string; count: number }[]
  walkthrough_completion: { id: string; title: string; started: number; completed: number }[]
}
